import React, { useEffect, useState, useRef, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import { handleTokenAndPostRequest } from "../utils/utils";
import Spinner from "react-bootstrap/Spinner";
import Skeleton from "react-loading-skeleton";
import "react-loading-skeleton/dist/skeleton.css";
import {
  FaPlus, FaTrash, FaEye, FaSearch, FaCheckSquare, FaRegSquare,
  FaTimes, FaHome, FaPen, FaUpload, FaImage,
  FaChevronLeft, FaChevronRight, FaChevronDown, FaGlobe, FaExternalLinkAlt,
  FaPaperPlane, FaLink, FaEnvelope, FaClock, FaMagic,
  FaBed, FaBath, FaCar, FaCalendarAlt, FaMapMarkerAlt, FaCheck,
  FaRulerCombined, FaTools, FaSms, FaMobileAlt
} from "react-icons/fa";
import AppraisalMap from "../components/AppraisalMap";
import AgentBadge from "../components/AgentBadge";
import EmptyFeatureState from "../components/EmptyFeatureState";
import { useViewScope } from "../contexts/ViewScopeContext";

// ─── Demo data used when the API is unavailable ───
const DEMO_APPRAISALS = [
  {
    id: "appr_001",
    address: "42 Ocean View Drive, Coogee NSW 2034",
    created_at: "2026-03-10T09:15:00Z",
    updated_at: "2026-03-12T14:30:00Z",
    price_low: 2200000,
    price_high: 2500000,
    price_mid: 2350000,
    rent_low: null,
    rent_high: null,
    rent_mid: null,
    include_rental: false,
    include_sale: true,
    comparables_count: 3,
    status: "completed",
    image: "https://images.pexels.com/photos/323780/pexels-photo-323780.jpeg?auto=compress&cs=tinysrgb&w=400",
  },
  {
    id: "appr_002",
    address: "15/88 Marine Parade, Maroubra NSW 2035",
    created_at: "2026-03-08T11:00:00Z",
    updated_at: "2026-03-08T11:00:00Z",
    price_low: 950000,
    price_high: 1050000,
    price_mid: 1000000,
    rent_low: 750,
    rent_high: 850,
    rent_mid: 800,
    include_rental: true,
    include_sale: true,
    comparables_count: 5,
    status: "completed",
    image: "https://images.pexels.com/photos/144632/pexels-photo-144632.jpeg?auto=compress&cs=tinysrgb&w=400",
  },
  {
    id: "appr_003",
    address: "7 Banksia Street, Corrimal NSW 2518",
    created_at: "2026-02-28T16:45:00Z",
    updated_at: "2026-03-01T10:20:00Z",
    price_low: 820000,
    price_high: 900000,
    price_mid: 860000,
    rent_low: 580,
    rent_high: 640,
    rent_mid: 610,
    include_rental: true,
    include_sale: true,
    comparables_count: 4,
    status: "draft",
    image: "https://images.pexels.com/photos/8292794/pexels-photo-8292794.jpeg?auto=compress&cs=tinysrgb&w=400",
  },
];

const DEMO_PROPERTY_IMAGES = [
  "https://images.pexels.com/photos/323780/pexels-photo-323780.jpeg?auto=compress&cs=tinysrgb&w=800",
  "https://images.pexels.com/photos/1396122/pexels-photo-1396122.jpeg?auto=compress&cs=tinysrgb&w=800",
  "https://images.pexels.com/photos/1571460/pexels-photo-1571460.jpeg?auto=compress&cs=tinysrgb&w=800",
  "https://images.pexels.com/photos/2724749/pexels-photo-2724749.jpeg?auto=compress&cs=tinysrgb&w=800",
];

const DEMO_COMPARABLES = [
  {
    id: "comp_1",
    display_address: "38 Ocean View Drive, Coogee NSW 2034",
    price: 2280000,
    filter_date: "2026-01-15",
    bedrooms: 4,
    bathrooms: 2,
    carspaces: 2,
    photo_url_main: "https://images.pexels.com/photos/323780/pexels-photo-323780.jpeg?auto=compress&cs=tinysrgb&w=400",
    photo_urls: [
      "https://images.pexels.com/photos/323780/pexels-photo-323780.jpeg?auto=compress&cs=tinysrgb&w=400",
      "https://images.pexels.com/photos/1396122/pexels-photo-1396122.jpeg?auto=compress&cs=tinysrgb&w=400",
      "https://images.pexels.com/photos/1571460/pexels-photo-1571460.jpeg?auto=compress&cs=tinysrgb&w=400",
    ],
    property_type_display: "House",
  },
  {
    id: "comp_2",
    display_address: "55 Dolphin Street, Coogee NSW 2034",
    price: 2450000,
    filter_date: "2025-12-20",
    bedrooms: 4,
    bathrooms: 3,
    carspaces: 2,
    photo_url_main: "https://images.pexels.com/photos/144632/pexels-photo-144632.jpeg?auto=compress&cs=tinysrgb&w=400",
    photo_urls: [
      "https://images.pexels.com/photos/144632/pexels-photo-144632.jpeg?auto=compress&cs=tinysrgb&w=400",
      "https://images.pexels.com/photos/2724749/pexels-photo-2724749.jpeg?auto=compress&cs=tinysrgb&w=400",
    ],
    property_type_display: "House",
  },
  {
    id: "comp_3",
    display_address: "12 Beach Road, Coogee NSW 2034",
    price: 2150000,
    filter_date: "2026-02-05",
    bedrooms: 3,
    bathrooms: 2,
    carspaces: 1,
    photo_url_main: "https://images.pexels.com/photos/8292794/pexels-photo-8292794.jpeg?auto=compress&cs=tinysrgb&w=400",
    photo_urls: [
      "https://images.pexels.com/photos/8292794/pexels-photo-8292794.jpeg?auto=compress&cs=tinysrgb&w=400",
      "https://images.pexels.com/photos/1571460/pexels-photo-1571460.jpeg?auto=compress&cs=tinysrgb&w=400",
      "https://images.pexels.com/photos/6284229/pexels-photo-6284229.jpeg?auto=compress&cs=tinysrgb&w=400",
    ],
    property_type_display: "House",
  },
  {
    id: "comp_4",
    display_address: "21 Carr Street, Coogee NSW 2034",
    price: 2600000,
    filter_date: "2025-11-08",
    bedrooms: 5,
    bathrooms: 3,
    carspaces: 2,
    photo_url_main: "https://images.pexels.com/photos/6284229/pexels-photo-6284229.jpeg?auto=compress&cs=tinysrgb&w=400",
    photo_urls: [
      "https://images.pexels.com/photos/6284229/pexels-photo-6284229.jpeg?auto=compress&cs=tinysrgb&w=400",
    ],
    property_type_display: "House",
  },
  {
    id: "comp_5",
    display_address: "9 Arden Street, Coogee NSW 2034",
    price: 1950000,
    filter_date: "2026-01-28",
    bedrooms: 3,
    bathrooms: 2,
    carspaces: 1,
    photo_url_main: "https://images.pexels.com/photos/323780/pexels-photo-323780.jpeg?auto=compress&cs=tinysrgb&w=400",
    photo_urls: [
      "https://images.pexels.com/photos/323780/pexels-photo-323780.jpeg?auto=compress&cs=tinysrgb&w=400",
      "https://images.pexels.com/photos/2724749/pexels-photo-2724749.jpeg?auto=compress&cs=tinysrgb&w=400",
      "https://images.pexels.com/photos/1396122/pexels-photo-1396122.jpeg?auto=compress&cs=tinysrgb&w=400",
    ],
    property_type_display: "House",
  },
];

const DEMO_RENTAL_COMPARABLES = [
  {
    id: "rcomp_1",
    display_address: "38 Ocean View Drive, Coogee NSW 2034",
    price: 1350,
    filter_date: "2026-02-01",
    bedrooms: 4,
    bathrooms: 2,
    carspaces: 2,
    photo_url_main: "https://images.pexels.com/photos/323780/pexels-photo-323780.jpeg?auto=compress&cs=tinysrgb&w=400",
    photo_urls: [
      "https://images.pexels.com/photos/323780/pexels-photo-323780.jpeg?auto=compress&cs=tinysrgb&w=400",
      "https://images.pexels.com/photos/1396122/pexels-photo-1396122.jpeg?auto=compress&cs=tinysrgb&w=400",
    ],
    property_type_display: "House",
  },
  {
    id: "rcomp_2",
    display_address: "55 Dolphin Street, Coogee NSW 2034",
    price: 1500,
    filter_date: "2025-12-15",
    bedrooms: 4,
    bathrooms: 3,
    carspaces: 2,
    photo_url_main: "https://images.pexels.com/photos/144632/pexels-photo-144632.jpeg?auto=compress&cs=tinysrgb&w=400",
    photo_urls: [
      "https://images.pexels.com/photos/144632/pexels-photo-144632.jpeg?auto=compress&cs=tinysrgb&w=400",
      "https://images.pexels.com/photos/2724749/pexels-photo-2724749.jpeg?auto=compress&cs=tinysrgb&w=400",
    ],
    property_type_display: "House",
  },
  {
    id: "rcomp_3",
    display_address: "12 Beach Road, Coogee NSW 2034",
    price: 1100,
    filter_date: "2026-01-20",
    bedrooms: 3,
    bathrooms: 2,
    carspaces: 1,
    photo_url_main: "https://images.pexels.com/photos/8292794/pexels-photo-8292794.jpeg?auto=compress&cs=tinysrgb&w=400",
    photo_urls: [
      "https://images.pexels.com/photos/8292794/pexels-photo-8292794.jpeg?auto=compress&cs=tinysrgb&w=400",
    ],
    property_type_display: "House",
  },
  {
    id: "rcomp_4",
    display_address: "21 Carr Street, Coogee NSW 2034",
    price: 1600,
    filter_date: "2025-11-01",
    bedrooms: 5,
    bathrooms: 3,
    carspaces: 2,
    photo_url_main: "https://images.pexels.com/photos/6284229/pexels-photo-6284229.jpeg?auto=compress&cs=tinysrgb&w=400",
    photo_urls: [
      "https://images.pexels.com/photos/6284229/pexels-photo-6284229.jpeg?auto=compress&cs=tinysrgb&w=400",
      "https://images.pexels.com/photos/1571460/pexels-photo-1571460.jpeg?auto=compress&cs=tinysrgb&w=400",
    ],
    property_type_display: "House",
  },
];

const PROPERTY_FEATURE_DEFAULTS = {
  bedrooms: "",
  bathrooms: "",
  parking: "",
  land_size: "",
  property_type: "House",
  year_built: "",
  condition: "Average",
  notes: "",
  latitude: "",
  longitude: "",
};

const PROPERTY_TYPES = ["House", "Unit", "Apartment", "Townhouse", "Villa", "Duplex", "Acreage", "Land", "Other"];
const CONDITIONS = ["New", "Excellent", "Good", "Average", "Fair", "Poor"];
const PROPERTY_FEATURES_LIST = [
  "Air Conditioning", "Alarm System", "Balcony", "BBQ Area", "Built-in Wardrobes",
  "Courtyard", "Deck", "Dishwasher", "Ducted Heating", "Ensuite",
  "Fireplace", "Floorboards", "Garage", "Garden", "Gas Heating",
  "Gym", "Intercom", "Internal Laundry", "Outdoor Entertaining", "Pergola",
  "Secure Parking", "Shed", "Solar Panels", "Study", "Swimming Pool",
  "Tennis Court", "Water Tank",
];

function formatCurrency(val) {
  if (val == null || val === "") return "—";
  return "$" + Number(val).toLocaleString();
}

// Derive suburb_id from address text: "... <Suburb> <STATE> <Postcode>" → "suburb-state-postcode"
function deriveSuburbId(address) {
  if (!address) return "";
  // Try to match "Suburb STATE Postcode" at the end
  const m = address.match(/([A-Za-z\s]+?)\s+(NSW|VIC|QLD|SA|WA|TAS|NT|ACT)\s+(\d{4})\s*$/i);
  if (m) {
    const suburb = m[1].trim().toLowerCase().replace(/\s+/g, "-");
    const state = m[2].toLowerCase();
    const postcode = m[3];
    return `${suburb}-${state}-${postcode}`;
  }
  return "";
}

function formatSuburbLabel(suburbId) {
  if (!suburbId) return "";
  // "coogee-nsw-2034" -> "Coogee, NSW 2034"
  const parts = suburbId.match(/^(.+)-(nsw|vic|qld|sa|wa|tas|nt|act)-(\d{4})$/i);
  if (parts) {
    const suburb = parts[1].replace(/-/g, " ").replace(/\b\w/g, c => c.toUpperCase());
    return `${suburb}, ${parts[2].toUpperCase()} ${parts[3]}`;
  }
  return suburbId;
}

// Parse the `media` field (JSON string or array) into an array of image URLs
function parseMediaUrls(media) {
  if (!media) return [];
  try {
    const arr = typeof media === "string" ? JSON.parse(media) : media;
    if (!Array.isArray(arr)) return [];
    return arr
      .filter(m => m.mediaCategory === "IMAGE" && m.url)
      .map(m => m.url);
  } catch {
    return [];
  }
}

// Helper for authenticated GET requests (for /property-search)
async function authGet(url) {
  const jwt_token = localStorage.getItem('jwt_token');
  const storedParamToken = localStorage.getItem('param_token');
  const sep = url.includes('?') ? '&' : '?';
  const fullUrl = `${url}${sep}param_token=${encodeURIComponent(storedParamToken || '')}&source=${encodeURIComponent(window.location.href)}`;
  const res = await fetch(fullUrl, {
    headers: { 'Authorization': `Bearer ${jwt_token}` },
  });
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
  return res.json();
}

// Format epoch timestamps (seconds) to Date
function epochToDate(val) {
  if (val == null) return null;
  let d;
  const num = typeof val === 'number' ? val : (typeof val === 'string' && /^\d+$/.test(val.trim()) ? Number(val) : null);
  if (num != null) {
    d = new Date(num * 1000);
  } else {
    d = new Date(val);
  }
  return isNaN(d.getTime()) ? null : d;
}

export default function PropertyAppraisals() {
  const [searchParams, setSearchParams] = useSearchParams();
  // ─── List state ───
  const [appraisals, setAppraisals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // ─── Mobile ───
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  useEffect(() => {
    const h = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener("resize", h);
    return () => window.removeEventListener("resize", h);
  }, []);

  // ─── Agency scope ───
  const { viewScope, agentFilter } = useViewScope();

  // Close features dropdown on click outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (featuresDropdownRef.current && !featuresDropdownRef.current.contains(e.target)) {
        setFeaturesDropdownOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // ─── Create / Edit modal ───
  const [modalOpen, setModalOpen] = useState(false);
  const [modalLoading, setModalLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [editingId, setEditingId] = useState(null); // null = create mode, string = editing
  const [modalTab, setModalTab] = useState("details");

  // Address search
  const [addressQuery, setAddressQuery] = useState("");
  const [addressResults, setAddressResults] = useState([]);
  const [addressSearching, setAddressSearching] = useState(false);
  const [addressDropdownOpen, setAddressDropdownOpen] = useState(false);
  const addressTimer = useRef(null);
  const addressInputRef = useRef(null);

  // Selected address & comparables
  const [selectedAddress, setSelectedAddress] = useState(null);
  const [comparables, setComparables] = useState([]);
  const [comparablesLoading, setComparablesLoading] = useState(false);
  const [selectedComparables, setSelectedComparables] = useState({}); // id -> true
  const [comparableComments, setComparableComments] = useState({}); // id -> string

  // Rental comparables
  const [rentalComparables, setRentalComparables] = useState([]);
  const [rentalComparablesLoading, setRentalComparablesLoading] = useState(false);
  const [selectedRentalComparables, setSelectedRentalComparables] = useState({});
  const [rentalComparableComments, setRentalComparableComments] = useState({});

  // Comparable image carousel indexes { [compId]: number }
  const [compCarouselIndexes, setCompCarouselIndexes] = useState({});
  // Lazy-loaded image count per comp { [compId]: number } — starts at 2
  const [compLoadedImages, setCompLoadedImages] = useState({});

  // Property images (carousel)
  const [propertyImages, setPropertyImages] = useState([]);
  const [carouselIndex, setCarouselIndex] = useState(0);
  const [failedImages, setFailedImages] = useState(new Set());
  const [deselectedImages, setDeselectedImages] = useState(new Set());
  const [uploadingImages, setUploadingImages] = useState(false);
  const imageInputRef = useRef(null);

  // Property features
  const [features, setFeatures] = useState({ ...PROPERTY_FEATURE_DEFAULTS });

  // Data type toggles
  const [includeSale, setIncludeSale] = useState(true);
  const [includeRental, setIncludeRental] = useState(false);

  // Section visibility toggles (which tabs/sections to include in the appraisal)
  const [sectionToggles, setSectionToggles] = useState({
    details: true,
    improvements: true,
    comparables: true,
    location: true,
    valuation: true,
    team: true,
  });
  const toggleSection = (key) => setSectionToggles(prev => ({ ...prev, [key]: !prev[key] }));

  // Price range
  const [priceLow, setPriceLow] = useState("");
  const [priceHigh, setPriceHigh] = useState("");
  const [priceMid, setPriceMid] = useState("");
  const [rentLow, setRentLow] = useState("");
  const [rentHigh, setRentHigh] = useState("");
  const [rentMid, setRentMid] = useState("");

  // Toast
  const [toast, setToast] = useState({ show: false, message: "" });

  // Delete
  const [deleteModal, setDeleteModal] = useState({ open: false, id: null });
  const [deleting, setDeleting] = useState(false);

  // Publish
  const [publishing, setPublishing] = useState(false);
  const [publishedUrl, setPublishedUrl] = useState(null);

  // Send Appraisal modal
  const [sendModal, setSendModal] = useState({ open: false, appraisalId: null, address: "" });
  const [sendRecipients, setSendRecipients] = useState([{ name: "", email: "", mobile: "", message: "" }]);
  const [sending, setSending] = useState(false);

  // Send history
  const [sendHistory, setSendHistory] = useState([]);

  // Slug (from /property-search)
  const [selectedSlug, setSelectedSlug] = useState("");

  // Comparable analysis
  const [analysisResults, setAnalysisResults] = useState({}); // id -> { score, summary, diff }
  const [analysisLoading, setAnalysisLoading] = useState({}); // id -> true while loading
  const [analysisPrompt, setAnalysisPrompt] = useState("");

  // Property features multiselect
  const [selectedPropertyFeatures, setSelectedPropertyFeatures] = useState([]);
  const [featuresDropdownOpen, setFeaturesDropdownOpen] = useState(false);
  const [customFeatureInput, setCustomFeatureInput] = useState("");
  const [featureSearchQuery, setFeatureSearchQuery] = useState("");
  const featuresDropdownRef = useRef(null);

  // AI property feature analysis
  const [featureAnalysisPrompt, setFeatureAnalysisPrompt] = useState("");
  const [featureAnalysisLoading, setFeatureAnalysisLoading] = useState(false);
  const [featureAnalysisProgress, setFeatureAnalysisProgress] = useState(0);
  const featureAnalysisTimer = useRef(null);

  // Image improvements
  const [imageImprovements, setImageImprovements] = useState({}); // url -> { quick_wins, moderate_upgrades, major_improvements, overall_assessment, ... }
  const [imageImprovementsLoading, setImageImprovementsLoading] = useState({}); // url -> true
  const [selectedImprovementImages, setSelectedImprovementImages] = useState({}); // url -> true
  const imageImprovementsAutoRan = useRef(false);

  // Location analysis
  const [locationAnalysisPrompt, setLocationAnalysisPrompt] = useState("");
  const [locationAnalysis, setLocationAnalysis] = useState(null);
  const [locationAnalysisLoading, setLocationAnalysisLoading] = useState(false);
  const [locationAnalysisProgress, setLocationAnalysisProgress] = useState(0);
  const locationAnalysisTimer = useRef(null);

  // Agency/team stats
  const [agencyStats, setAgencyStats] = useState(null);
  const [agencyStatsLoading, setAgencyStatsLoading] = useState(false);
  const [selectedTeamMembers, setSelectedTeamMembers] = useState({}); // email -> true
  const [selectedRecentListings, setSelectedRecentListings] = useState({}); // id -> true

  // Map camera state (updated when user pans/zooms)
  const [mapCenter, setMapCenter] = useState(null);
  const [mapZoom, setMapZoom] = useState(15);



  // ─── Listing search filters ───
  const [filterSuburbs, setFilterSuburbs] = useState([]); // [{ value, label }]
  const [suburbQuery, setSuburbQuery] = useState("");
  const [suburbResults, setSuburbResults] = useState([]);
  const [suburbSearching, setSuburbSearching] = useState(false);
  const [suburbDropdownOpen, setSuburbDropdownOpen] = useState(false);
  const suburbSearchTimer = useRef(null);
  const suburbInputRef = useRef(null);
  const [filterDays, setFilterDays] = useState("365");
  const [filterPropertyTypes, setFilterPropertyTypes] = useState([]);
  const [filterBedrooms, setFilterBedrooms] = useState([]);
  const [filterOnMarket, setFilterOnMarket] = useState(false);
  const [filterOffMarket, setFilterOffMarket] = useState(true);

  // ─── Suburb search for filters ───
  const searchFilterSuburbs = useCallback((query) => {
    if (suburbSearchTimer.current) clearTimeout(suburbSearchTimer.current);
    if (!query.trim()) {
      setSuburbResults([]);
      setSuburbDropdownOpen(false);
      return;
    }
    suburbSearchTimer.current = setTimeout(async () => {
      setSuburbSearching(true);
      try {
        const res = await fetch(
          `${window.api_host}/location-search?q=${encodeURIComponent(query)}`
        );
        const data = await res.json();
        const filtered = (Array.isArray(data) ? data : []).filter(
          item => item.type === "suburb" && !filterSuburbs.some(s => s.value === item.value)
        );
        setSuburbResults(filtered);
        setSuburbDropdownOpen(filtered.length > 0);
      } catch {
        setSuburbResults([]);
      }
      setSuburbSearching(false);
    }, 300);
  }, [filterSuburbs]);

  const handleSelectFilterSuburb = (item) => {
    if (filterSuburbs.some(s => s.value === item.value)) return;
    setFilterSuburbs(prev => [...prev, { value: item.value, label: item.label || item.value }]);
    setSuburbQuery("");
    const remaining = suburbResults.filter(r => r.value !== item.value);
    setSuburbResults(remaining);
    setSuburbDropdownOpen(remaining.length > 0);
    if (suburbInputRef.current) suburbInputRef.current.focus();
  };

  const handleRemoveFilterSuburb = (idx) => {
    setFilterSuburbs(prev => prev.filter((_, i) => i !== idx));
  };

  // ─── Fetch a single listing by id ───
  const fetchListing = useCallback((id) =>
    handleTokenAndPostRequest(`${window.api_host}/get-listing`, { id })
      .then(r => {
        const l = r.data?.listing;
        if (!l) return null;
        return {
          ...l,
          filter_date: l.filter_date || l.sold_timestamp || l.date_listed || null,
          photo_urls: l.photo_urls || parseMediaUrls(l.media),
          land_area: (() => {
            if (l.land_area == null) return null;
            if (typeof l.land_area === 'number') return l.land_area;
            try { return JSON.parse(l.land_area)?.size || null; } catch { return null; }
          })(),
        };
      })
      .catch(() => null),
  []);

  // ─── Fetch comparables with current filters ───
  // Also fetches any selected comparables missing from results via get-listing
  const fetchFilteredComparables = useCallback(async (suburbOverride, selectedSaleIds, selectedRentalIds) => {
    const suburbs = suburbOverride
      ? [suburbOverride]
      : filterSuburbs.map(s => s.value);
    if (suburbs.length === 0) return;
    setComparablesLoading(true);
    setRentalComparablesLoading(true);

    let finalSale, finalRent;
    try {
      // Search each suburb and merge results
      const salePromises = suburbs.map(suburb =>
        handleTokenAndPostRequest(
          `${window.api_host}/listing-search`,
          {
            suburb,
            listing_type: "Sold",
            days_in_past: filterDays,
            include_on_market: filterOnMarket,
            include_off_market: filterOffMarket,
            property_types: filterPropertyTypes,
            bedrooms: filterBedrooms,
          }
        ).catch(() => null)
      );
      const rentPromises = suburbs.map(suburb =>
        handleTokenAndPostRequest(
          `${window.api_host}/listing-search`,
          {
            suburb,
            listing_type: "Rent",
            days_in_past: filterDays,
            include_on_market: filterOnMarket,
            include_off_market: filterOffMarket,
            property_types: filterPropertyTypes,
            bedrooms: filterBedrooms,
          }
        ).catch(() => null)
      );
      const [saleResults, rentResults] = await Promise.all([
        Promise.all(salePromises),
        Promise.all(rentPromises),
      ]);
      const allSale = saleResults.flatMap(r => r?.data?.results || []);
      const allRent = rentResults.flatMap(r => r?.data?.results || []);
      finalSale = allSale;
      finalRent = allRent;
    } catch {
      finalSale = [];
      finalRent = [];
    }

    // Fetch any selected comparables not present in search results
    const saleIdSet = new Set(finalSale.map(c => c.id));
    const rentIdSet = new Set(finalRent.map(c => c.id));
    const missingSaleIds = (selectedSaleIds || []).filter(id => !saleIdSet.has(id));
    const missingRentIds = (selectedRentalIds || []).filter(id => !rentIdSet.has(id));
    if (missingSaleIds.length > 0 || missingRentIds.length > 0) {
      const [missingSale, missingRent] = await Promise.all([
        Promise.all(missingSaleIds.map(fetchListing)),
        Promise.all(missingRentIds.map(fetchListing)),
      ]);
      finalSale = [...finalSale, ...missingSale.filter(Boolean).map(c => ({ ...c, _outsideSearch: true }))];
      finalRent = [...finalRent, ...missingRent.filter(Boolean).map(c => ({ ...c, _outsideSearch: true }))];
    }

    setComparables(finalSale);
    setRentalComparables(finalRent);
    setComparablesLoading(false);
    setRentalComparablesLoading(false);
    return { sale: finalSale, rent: finalRent };
  }, [filterSuburbs, filterDays, filterPropertyTypes, filterBedrooms, filterOnMarket, filterOffMarket, fetchListing]);

  // ─── Fetch appraisals ───
  const fetchAppraisals = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await handleTokenAndPostRequest(
        `${window.api_host}/get-appraisals`,
        { view_scope: viewScope, agent_filter: agentFilter }
      );
      setAppraisals(res.data?.appraisals || []);
    } catch {
      // Fallback to demo data
      setAppraisals(DEMO_APPRAISALS);
    }
    setLoading(false);
  }, [viewScope, agentFilter]);

  useEffect(() => { fetchAppraisals(); }, [fetchAppraisals]);

  // ─── Auto-open create modal from URL params (e.g. ?create=true&address=...) ───
  const autoCreateHandled = useRef(false);
  useEffect(() => {
    if (autoCreateHandled.current) return;
    if (searchParams.get("create") !== "true") return;
    autoCreateHandled.current = true;
    // Wait for component to mount fully, then open modal with prepopulated data
    const address = searchParams.get("address") || "";
    const bedrooms = searchParams.get("bedrooms") || "";
    const bathrooms = searchParams.get("bathrooms") || "";
    const parking = searchParams.get("parking") || "";
    const land_size = searchParams.get("land_size") || "";
    const property_type = searchParams.get("property_type") || "House";
    const contact_email = searchParams.get("contact_email") || "";
    const property_id = searchParams.get("property_id") || "";
    // Clear the query params from URL without navigation
    setSearchParams({}, { replace: true });
    // Open the create modal with prepopulated fields
    setTimeout(() => {
      setEditingId(null);
      setPublishedUrl(null);
      setSelectedSlug("");
      setAddressQuery(address);
      setAddressResults([]);
      setSelectedAddress(address ? { address, property_id, slug: "" } : null);
      setComparables([]);
      setSelectedComparables({});
      setComparableComments({});
      setAnalysisResults({});
      setAnalysisPrompt("");
      setSelectedPropertyFeatures([]);
      setImageImprovements({});
      setImageImprovementsLoading({});
      setSelectedImprovementImages({});
      imageImprovementsAutoRan.current = false;
      setRentalComparables([]);
      setSelectedRentalComparables({});
      setRentalComparableComments({});
      setPropertyImages([]);
      setCarouselIndex(0);
      setDeselectedImages(new Set());
      setFeatures({
        ...PROPERTY_FEATURE_DEFAULTS,
        bedrooms,
        bathrooms,
        parking,
        land_size,
        property_type,
      });
      setIncludeSale(true);
      setIncludeRental(false);
      setPriceLow("");
      setPriceHigh("");
      setPriceMid("");
      setRentLow("");
      setRentHigh("");
      setRentMid("");
      setSendHistory([]);
      setFilterSuburbs([]);
      setSuburbQuery("");
      setFilterDays("365");
      setFilterPropertyTypes([]);
      setFilterBedrooms([]);
      setFilterOnMarket(false);
      setFilterOffMarket(true);
      setLocationAnalysisPrompt("");
      setLocationAnalysis(null);
      setAgencyStats(null);
      setSelectedTeamMembers({});
      setSelectedRecentListings({});
      setModalTab("details");
      setModalOpen(true);
      // If address was provided, try to fetch property features
      if (address && property_id) {
        handleSelectAddress({ address, property_id, slug: "" });
      }
    }, 100);
  }, [searchParams, setSearchParams]);

  // ─── Address search (GET /property-search) ───
  const handleAddressSearch = useCallback((query) => {
    setAddressQuery(query);
    if (addressTimer.current) clearTimeout(addressTimer.current);
    if (query.length < 3) {
      setAddressResults([]);
      setAddressDropdownOpen(false);
      return;
    }
    setAddressSearching(true);
    addressTimer.current = setTimeout(async () => {
      try {
        const data = await authGet(
          `${window.api_host}/property-search?q=${encodeURIComponent(query)}`
        );
        // Response: [{ id, address_text, slug }]
        const results = (Array.isArray(data) ? data : []).map(r => ({
          address: r.address_text,
          property_id: r.id,
          slug: r.slug,
        }));
        setAddressResults(results);
        setAddressDropdownOpen(results.length > 0);
      } catch {
        // Demo fallback
        const demoResults = [
          { address: `${query} Street, Sydney NSW 2000`, property_id: "demo_1", slug: "demo-1" },
          { address: `${query} Avenue, Bondi NSW 2026`, property_id: "demo_2", slug: "demo-2" },
          { address: `${query} Road, Coogee NSW 2034`, property_id: "demo_3", slug: "demo-3" },
        ];
        setAddressResults(demoResults);
        setAddressDropdownOpen(true);
      }
      setAddressSearching(false);
    }, 400);
  }, []);

  // ─── Select address & fetch comparables ───
  const handleSelectAddress = useCallback(async (item) => {
    setSelectedAddress(item);
    setAddressQuery(item.address);
    setAddressDropdownOpen(false);
    setSelectedSlug(item.slug || "");
    setSelectedComparables({});
    setComparableComments({});
    setSelectedRentalComparables({});
    setRentalComparableComments({});
    setPropertyImages([]);
    setCarouselIndex(0);
    setDeselectedImages(new Set());

    const suburbId = deriveSuburbId(item.address);
    // Auto-populate the suburb filter (only if a valid suburb was derived)
    if (suburbId) {
      setFilterSuburbs([{ value: suburbId, label: formatSuburbLabel(suburbId) }]);
    } else {
      setFilterSuburbs([]);
    }
    setSuburbQuery("");

    // Try to fetch property features (pass slug)
    try {
      const res = await handleTokenAndPostRequest(
        `${window.api_host}/get-property-features`,
        { property_id: item.property_id, address: item.address, slug: item.slug || "" }
      );
      if (res.data) {
        setFeatures(f => ({
          ...f,
          bedrooms: res.data.bedrooms ?? "",
          bathrooms: res.data.bathrooms ?? "",
          parking: res.data.parking ?? "",
          land_size: res.data.land_size ?? "",
          property_type: res.data.property_type || "House",
          year_built: res.data.year_built ?? "",
          latitude: res.data.latitude ?? "",
          longitude: res.data.longitude ?? "",
        }));
        if (Array.isArray(res.data.images) && res.data.images.length > 0) {
          setPropertyImages(res.data.images);
        } else {
          setPropertyImages(DEMO_PROPERTY_IMAGES);
        }
      } else {
        setPropertyImages(DEMO_PROPERTY_IMAGES);
      }
    } catch {
      // leave defaults
      setPropertyImages(DEMO_PROPERTY_IMAGES);
    }

  }, []);  // No auto-fetch — user clicks Search Comparables

  // ─── Toggle comparable ───
  const toggleComparable = (id) => {
    setSelectedComparables(prev => {
      const next = { ...prev };
      if (next[id]) delete next[id];
      else next[id] = true;
      return next;
    });
  };

  const toggleRentalComparable = (id) => {
    setSelectedRentalComparables(prev => {
      const next = { ...prev };
      if (next[id]) delete next[id];
      else next[id] = true;
      return next;
    });
  };

  // ─── Reset modal (create) ───
  const openCreateModal = () => {
    setEditingId(null);
    setPublishedUrl(null);
    setSelectedSlug("");
    setAddressQuery("");
    setAddressResults([]);
    setSelectedAddress(null);
    setComparables([]);
    setSelectedComparables({});
    setComparableComments({});
    setAnalysisResults({});
    setAnalysisPrompt("");
    setSelectedPropertyFeatures([]);
    setImageImprovements({});
    setImageImprovementsLoading({});
    setSelectedImprovementImages({});
    imageImprovementsAutoRan.current = false;
    setRentalComparables([]);
    setSelectedRentalComparables({});
    setRentalComparableComments({});
    setPropertyImages([]);
    setCarouselIndex(0);
    setDeselectedImages(new Set());
    setFeatures({ ...PROPERTY_FEATURE_DEFAULTS });
    setIncludeSale(true);
    setIncludeRental(false);
    setPriceLow("");
    setPriceHigh("");
    setPriceMid("");
    setRentLow("");
    setRentHigh("");
    setRentMid("");
    setSendHistory([]);
    setFilterSuburbs([]);
    setSuburbQuery("");
    setFilterDays("365");
    setFilterPropertyTypes([]);
    setFilterBedrooms([]);
    setFilterOnMarket(false);
    setFilterOffMarket(true);
    setLocationAnalysisPrompt("");
    setLocationAnalysis(null);
    setAgencyStats(null);
    setSelectedTeamMembers({});
    setSelectedRecentListings({});
    setSectionToggles({ details: true, improvements: true, comparables: true, location: true, valuation: true, team: true });
    setModalTab("details");
    setModalOpen(true);
  };

  // ─── Open modal in edit mode ───
  const openEditModal = async (appr) => {
    // Open modal immediately with loading state
    setEditingId(appr.id);
    setPublishedUrl(localizeUrl(appr.published_url) || null);
    setSelectedSlug(appr.slug || "");
    setAddressQuery(appr.address);
    setAddressResults([]);
    setAddressDropdownOpen(false);
    setSelectedAddress({ address: appr.address, property_id: appr.property_id || appr.id, slug: appr.slug || "" });
    setIncludeSale(!!appr.include_sale);
    setIncludeRental(!!appr.include_rental);
    setPriceLow(appr.price_low != null ? String(appr.price_low) : "");
    setPriceHigh(appr.price_high != null ? String(appr.price_high) : "");
    setPriceMid(appr.price_mid != null ? String(appr.price_mid) : "");
    setRentLow(appr.rent_low != null ? String(appr.rent_low) : "");
    setRentHigh(appr.rent_high != null ? String(appr.rent_high) : "");
    setRentMid(appr.rent_mid != null ? String(appr.rent_mid) : "");
    const loadedFeatures = appr.features || { ...PROPERTY_FEATURE_DEFAULTS };
    // Strip legacy boolean fields
    const { pool, granny_flat, renovated, views, property_features: featPf, ...cleanFeatures } = loadedFeatures;
    setFeatures(cleanFeatures);
    setSelectedPropertyFeatures(featPf || appr.property_features || []);
    // Restore image improvements
    setImageImprovements(loadedFeatures.image_improvements || {});
    setImageImprovementsLoading({});
    // Auto-select images that have saved improvements
    const savedImpr = loadedFeatures.image_improvements || {};
    const imprSelMap = {};
    Object.keys(savedImpr).forEach(url => { imprSelMap[url] = true; });
    setSelectedImprovementImages(imprSelMap);
    imageImprovementsAutoRan.current = true; // don't auto-run for loaded appraisals
    setLocationAnalysisPrompt(loadedFeatures.location_analysis_prompt || "");
    setLocationAnalysis(loadedFeatures.location_analysis || null);
    const loadedStats = appr.agency_stats || loadedFeatures.agency_stats || null;
    setAgencyStats(loadedStats);
    // Restore selected team members and recent listings from saved agency_stats
    if (loadedStats) {
      const teamSel = {};
      (loadedStats.team || []).forEach(m => { if (m.agent_email) teamSel[m.agent_email] = true; });
      setSelectedTeamMembers(teamSel);
      const listingSel = {};
      (loadedStats.selected_recent_listings || []).forEach(l => { if (l.id) listingSel[l.id] = true; });
      setSelectedRecentListings(listingSel);
    } else {
      setSelectedTeamMembers({});
      setSelectedRecentListings({});
    }
    setPropertyImages(appr.images && appr.images.length > 0 ? appr.images : DEMO_PROPERTY_IMAGES);
    setCarouselIndex(0);
    setDeselectedImages(new Set());
    setSendHistory(appr.send_history || []);
    setSectionToggles(appr.section_toggles || { details: true, improvements: true, comparables: true, location: true, valuation: true, team: true });
    setModalTab("details");
    setModalOpen(true);
    setModalLoading(true);

    // Restore saved comparables (renamed to sale_comparables in API)
    const savedComps = appr.sale_comparables || [];
    const selMap = {};
    const commentMap = {};
    const restoredAnalysis = {};
    savedComps.forEach(c => {
      selMap[c.id] = true;
      if (c.comment) commentMap[c.id] = c.comment;
      if (c.ai_analysis) restoredAnalysis[c.id] = c.ai_analysis;
    });
    setSelectedComparables(selMap);
    setComparableComments(commentMap);
    // Pre-populate comparables list with saved data so they're visible before searching
    setComparables(savedComps);

    // Restore saved rental comparables
    const savedRentalComps = appr.rental_comparables || [];
    const rSelMap = {};
    const rCommentMap = {};
    savedRentalComps.forEach(c => {
      rSelMap[c.id] = true;
      if (c.comment) rCommentMap[c.id] = c.comment;
      if (c.ai_analysis) restoredAnalysis[c.id] = c.ai_analysis;
    });
    setSelectedRentalComparables(rSelMap);
    setRentalComparableComments(rCommentMap);
    setAnalysisResults(restoredAnalysis);
    // Pre-populate rental comparables list with saved data
    setRentalComparables(savedRentalComps);

    // Restore listing search settings if saved, otherwise derive from address
    const saved = appr.listing_search_settings;
    if (saved && saved.suburbs && saved.suburbs.length > 0) {
      setFilterSuburbs(saved.suburbs);
      setSuburbQuery("");
      setFilterDays(saved.days || "365");
      setFilterPropertyTypes(saved.property_types || []);
      setFilterBedrooms(saved.bedrooms || []);
      setFilterOnMarket(saved.on_market ?? false);
      setFilterOffMarket(saved.off_market ?? true);
    } else {
      const suburbId = deriveSuburbId(appr.address);
      if (suburbId) {
        setFilterSuburbs([{ value: suburbId, label: formatSuburbLabel(suburbId) }]);
      } else {
        setFilterSuburbs([]);
      }
      setSuburbQuery("");
      setFilterDays("365");
      setFilterPropertyTypes([]);
      setFilterBedrooms([]);
      setFilterOnMarket(false);
      setFilterOffMarket(true);
    }

    setModalLoading(false);
  };

  const closeModal = () => {
    if (saving) return;
    setModalOpen(false);
  };

  // ─── Run Comparable Analysis ───
  const handleRunAnalysis = async () => {
    const selectedIds = Object.keys(selectedComparables);
    if (selectedIds.length === 0 || !selectedAddress) return;
    const target = {
      address: selectedAddress.address,
      bedrooms: Number(features.bedrooms) || 0,
      bathrooms: Number(features.bathrooms) || 0,
      parking: Number(features.parking) || 0,
      land_size: Number(features.land_size) || 0,
      property_type: features.property_type || "House",
    };
    // Mark all selected as loading
    const loadingMap = {};
    selectedIds.forEach(id => { loadingMap[id] = true; });
    setAnalysisLoading(prev => ({ ...prev, ...loadingMap }));

    // Fire one request per comparable in parallel
    const selectedComps = comparables.filter(c => selectedComparables[c.id]);
    await Promise.all(selectedComps.map(async (c) => {
      const compPayload = {
        id: c.id,
        address: c.display_address,
        bedrooms: c.bedrooms || 0,
        bathrooms: c.bathrooms || 0,
        parking: c.carspaces || 0,
        land_size: c.land_area || 0,
        property_type: c.property_type_display || "House",
        price: c.price || 0,
        display_price: formatCurrency(c.price),
      };
      try {
        const res = await handleTokenAndPostRequest(
          `${window.api_host}/run-comparable-analysis`,
          { target, comparables: [compPayload], prompt: analysisPrompt || undefined }
        );
        const r = res.data?.results?.[0];
        if (r) {
          setAnalysisResults(prev => ({ ...prev, [r.id]: { score: r.score, summary: r.summary, diff: r.diff } }));
        }
      } catch {
        // silently skip failed individual analysis
      }
      setAnalysisLoading(prev => { const next = { ...prev }; delete next[c.id]; return next; });
    }));
  };

  // ─── Save appraisal ───
  // ─── Analyse location ───
  const handleLocationAnalysis = async () => {
    setLocationAnalysisLoading(true);
    setLocationAnalysisProgress(0);
    const DURATION = 8000;
    const startTime = Date.now();
    if (locationAnalysisTimer.current) clearInterval(locationAnalysisTimer.current);
    locationAnalysisTimer.current = setInterval(() => {
      const elapsed = Date.now() - startTime;
      const ratio = Math.min(elapsed / DURATION, 1);
      const progress = Math.min(95, ratio * 100 * (2 - ratio));
      setLocationAnalysisProgress(progress);
    }, 200);
    try {
      const saleComps = comparables.filter(c => selectedComparables[c.id]);
      const rentComps = rentalComparables.filter(c => selectedRentalComparables[c.id]);
      const res = await handleTokenAndPostRequest(`${window.api_host}/analyse-location`, {
        address: selectedAddress?.address || addressQuery,
        subject_lat: features.latitude,
        subject_lng: features.longitude,
        map_center: mapCenter || { lat: Number(features.latitude) || 0, lng: Number(features.longitude) || 0 },
        map_zoom: mapZoom,
        sale_comparables: saleComps.map(c => ({
          id: c.id, display_address: c.display_address, price: c.price,
          latitude: c.latitude, longitude: c.longitude,
          bedrooms: c.bedrooms, bathrooms: c.bathrooms, carspaces: c.carspaces,
          property_type_display: c.property_type_display, filter_date: c.filter_date,
        })),
        rental_comparables: rentComps.map(c => ({
          id: c.id, display_address: c.display_address, price: c.price,
          latitude: c.latitude, longitude: c.longitude,
          bedrooms: c.bedrooms, bathrooms: c.bathrooms, carspaces: c.carspaces,
          property_type_display: c.property_type_display, filter_date: c.filter_date,
        })),
        prompt: locationAnalysisPrompt,
        existing_analysis: locationAnalysis,
      });
      if (res.data?.analysis) {
        setLocationAnalysis(res.data.analysis);
      }
    } catch {
      setToast({ show: true, message: "Location analysis failed. Please try again." });
      setTimeout(() => setToast({ show: false, message: "" }), 3000);
    }
    if (locationAnalysisTimer.current) clearInterval(locationAnalysisTimer.current);
    setLocationAnalysisProgress(100);
    setTimeout(() => { setLocationAnalysisProgress(0); setLocationAnalysisLoading(false); }, 500);
  };

  // ─── Fetch Team Stats ───
  const handleFetchTeamStats = async () => {
    setAgencyStatsLoading(true);
    try {
      const mode = includeRental && !includeSale ? 'rental' : 'sale';
      const res = await handleTokenAndPostRequest(`${window.api_host}/get-team-stats`, { mode });
      console.log('get-team-stats response:', res.data);
      if (res.data) {
        setAgencyStats(res.data);
        // Auto-select all team members
        const sel = {};
        (res.data.team || []).forEach(m => { if (m.agent_email) sel[m.agent_email] = true; });
        setSelectedTeamMembers(sel);
        setSelectedRecentListings({});
      }
    } catch (err) {
      console.error('get-team-stats error:', err, err?.response?.data);
      setToast({ show: true, message: "Failed to load team stats." });
      setTimeout(() => setToast({ show: false, message: "" }), 3000);
    }
    setAgencyStatsLoading(false);
  };

  const handleUploadImages = async (files) => {
    if (!files || files.length === 0 || !editingId) return;
    setUploadingImages(true);
    try {
      const formData = new FormData();
      formData.append('appraisal_id', editingId);
      Array.from(files).forEach(f => formData.append('files', f));
      const res = await handleTokenAndPostRequest(`${window.api_host}/upload-appraisal-image`, formData);
      if (res.data?.images) {
        setPropertyImages(res.data.images);
      }
    } catch (err) {
      console.error('upload-appraisal-image error:', err);
      setToast({ show: true, message: "Failed to upload images." });
      setTimeout(() => setToast({ show: false, message: "" }), 3000);
    }
    setUploadingImages(false);
    if (imageInputRef.current) imageInputRef.current.value = '';
  };

  const handleDeleteImage = async (imageUrl) => {
    if (!editingId) return;
    try {
      const res = await handleTokenAndPostRequest(`${window.api_host}/delete-appraisal-image`, {
        appraisal_id: editingId,
        image_url: imageUrl,
      });
      if (res.data?.images) {
        setPropertyImages(res.data.images);
        setDeselectedImages(prev => { const next = new Set(prev); next.delete(imageUrl); return next; });
      }
    } catch (err) {
      console.error('delete-appraisal-image error:', err);
    }
  };

  const handleSave = async () => {
    if (!selectedAddress) return;
    setSaving(true);
    const payload = {
      ...(editingId ? { id: editingId } : {}),
      address: selectedAddress.address,
      property_id: selectedAddress.property_id,
      slug: selectedSlug,
      image: propertyImages.filter(u => !deselectedImages.has(u))[0] || "",
      images: propertyImages.filter(u => !deselectedImages.has(u)),
      include_sale: includeSale,
      include_rental: includeRental,
      price_low: priceLow ? Number(priceLow) : null,
      price_high: priceHigh ? Number(priceHigh) : null,
      price_mid: priceMid ? Number(priceMid) : null,
      rent_low: includeRental && rentLow ? Number(rentLow) : null,
      rent_high: includeRental && rentHigh ? Number(rentHigh) : null,
      rent_mid: includeRental && rentMid ? Number(rentMid) : null,
      features: { ...features, property_features: selectedPropertyFeatures, image_improvements: Object.fromEntries(Object.entries(imageImprovements).filter(([url]) => selectedImprovementImages[url])), location_analysis_prompt: locationAnalysisPrompt, location_analysis: locationAnalysis },
      agency_stats: agencyStats ? {
        ...agencyStats,
        team: (agencyStats.team || []).filter(m => selectedTeamMembers[m.agent_email]),
        recent_listings: (agencyStats.recent_listings || []).filter(l => selectedRecentListings[l.id]),
        selected_recent_listings: (agencyStats.recent_listings || []).filter(l => selectedRecentListings[l.id]),
      } : null,
      sale_comparables: Object.keys(selectedComparables).map(id => {
        const c = comparables.find(x => x.id === id) || {};
        return {
          ...c,
          id,
          comment: comparableComments[id] || "",
          ...(analysisResults[id] ? { ai_analysis: analysisResults[id] } : {}),
        };
      }),
      rental_comparables: Object.keys(selectedRentalComparables).map(id => {
        const c = rentalComparables.find(x => x.id === id) || {};
        return {
          ...c,
          id,
          comment: rentalComparableComments[id] || "",
          ...(analysisResults[id] ? { ai_analysis: analysisResults[id] } : {}),
        };
      }),
      send_history: sendHistory,
      section_toggles: sectionToggles,
      listing_search_settings: {
        suburbs: filterSuburbs,
        days: filterDays,
        property_types: filterPropertyTypes,
        bedrooms: filterBedrooms,
        on_market: filterOnMarket,
        off_market: filterOffMarket,
      },
    };

    try {
      const endpoint = editingId ? `${window.api_host}/update-appraisal` : `${window.api_host}/save-appraisal`;
      const res = await handleTokenAndPostRequest(endpoint, payload);
      if (!editingId && res.data?.id) setEditingId(res.data.id);
      setToast({ show: true, message: editingId ? "Appraisal updated!" : "Appraisal saved!" });
    } catch {
      // Optimistic – treat as success with demo
      setToast({ show: true, message: editingId ? "Appraisal updated (demo)!" : "Appraisal saved (demo)!" });
    }
    setSaving(false);
    fetchAppraisals();
    setTimeout(() => setToast({ show: false, message: "" }), 3000);
  };

  // ─── Delete ───
  const handleDelete = async (id) => {
    setDeleting(true);
    try {
      await handleTokenAndPostRequest(`${window.api_host}/delete-appraisal`, { id });
    } catch {
      // remove locally
    }
    setAppraisals(prev => prev.filter(a => a.id !== id));
    setDeleting(false);
    setDeleteModal({ open: false, id: null });
    setToast({ show: true, message: "Appraisal deleted." });
    setTimeout(() => setToast({ show: false, message: "" }), 3000);
  };

  // ─── Publish appraisal ───
  const appraisalBaseUrl = window.location.hostname === "localhost"
    ? "http://localhost:3000/appraisal"
    : "https://www.homeprice.site/appraisal";

  const localizeUrl = (url) => {
    if (!url) return url;
    if (window.location.hostname === "localhost") {
      return url.replace(/https?:\/\/(www\.)?homeprice\.site/, "http://localhost:3000");
    }
    return url;
  };

  const handlePublish = async (appraisalId) => {
    setPublishing(true);
    let url;
    try {
      const res = await handleTokenAndPostRequest(`${window.api_host}/publish-appraisal`, { id: appraisalId });
      url = res.data?.url || `${appraisalBaseUrl}/${appraisalId}`;
    } catch {
      url = `${appraisalBaseUrl}/${appraisalId}`;
    }
    url = localizeUrl(url);
    setPublishedUrl(url);
    setAppraisals(prev => prev.map(a => a.id === appraisalId ? { ...a, published_url: url, status: "completed" } : a));
    setToast({ show: true, message: "Appraisal published!" });
    setPublishing(false);
    setTimeout(() => setToast({ show: false, message: "" }), 3000);
  };

  // ─── Publish from modal (saves first) ───
  const handlePublishFromModal = async () => {
    if (!selectedAddress) return;
    setSaving(true);
    const payload = {
      ...(editingId ? { id: editingId } : {}),
      address: selectedAddress.address,
      property_id: selectedAddress.property_id,
      image: propertyImages.filter(u => !deselectedImages.has(u))[0] || "",
      images: propertyImages.filter(u => !deselectedImages.has(u)),
      include_sale: includeSale,
      include_rental: includeRental,
      price_low: priceLow ? Number(priceLow) : null,
      price_high: priceHigh ? Number(priceHigh) : null,
      price_mid: priceMid ? Number(priceMid) : null,
      rent_low: includeRental && rentLow ? Number(rentLow) : null,
      rent_high: includeRental && rentHigh ? Number(rentHigh) : null,
      rent_mid: includeRental && rentMid ? Number(rentMid) : null,
      features: { ...features, property_features: selectedPropertyFeatures, image_improvements: Object.fromEntries(Object.entries(imageImprovements).filter(([url]) => selectedImprovementImages[url])), location_analysis_prompt: locationAnalysisPrompt, location_analysis: locationAnalysis },
      agency_stats: agencyStats,
      slug: selectedSlug,
      sale_comparables: Object.keys(selectedComparables).map(id => {
        const c = comparables.find(x => x.id === id) || {};
        return {
          ...c,
          id,
          comment: comparableComments[id] || "",
          ...(analysisResults[id] ? { ai_analysis: analysisResults[id] } : {}),
        };
      }),
      rental_comparables: Object.keys(selectedRentalComparables).map(id => {
        const c = rentalComparables.find(x => x.id === id) || {};
        return {
          ...c,
          id,
          comment: rentalComparableComments[id] || "",
          ...(analysisResults[id] ? { ai_analysis: analysisResults[id] } : {}),
        };
      }),
      send_history: sendHistory,
      section_toggles: sectionToggles,
      listing_search_settings: {
        suburbs: filterSuburbs,
        days: filterDays,
        property_types: filterPropertyTypes,
        bedrooms: filterBedrooms,
        on_market: filterOnMarket,
        off_market: filterOffMarket,
      },
    };
    let appraisalId = editingId;
    try {
      const endpoint = editingId ? `${window.api_host}/update-appraisal` : `${window.api_host}/save-appraisal`;
      const res = await handleTokenAndPostRequest(endpoint, payload);
      appraisalId = res.data?.id || editingId;
    } catch {
      appraisalId = editingId || `demo_${Date.now()}`;
    }
    setSaving(false);
    if (appraisalId) {
      setEditingId(appraisalId);
      await handlePublish(appraisalId);
    }
    fetchAppraisals();
  };

  // ─── Build a pre-filled appraisal message so the user doesn't start from scratch ───
  const buildAppraisalMessage = (appr, recipientName) => {
    const firstName = (recipientName || appr.contact_name || "").trim().split(/\s+/)[0];
    const greeting = firstName ? `Hi ${firstName},` : "Hi there,";
    const propertyRef = appr.address ? ` for ${appr.address}` : " for your property";
    const hasPrice = appr.include_sale && appr.price_low != null && appr.price_high != null;
    const priceLine = hasPrice
      ? ` Based on recent comparable sales, I've estimated a price range of ${formatCurrency(appr.price_low)} – ${formatCurrency(appr.price_high)}.`
      : "";
    const signOff = appr.agent_name ? `\n\nKind regards,\n${appr.agent_name}` : "\n\nKind regards";
    return `${greeting}\n\nThank you for the opportunity to prepare a property appraisal${propertyRef}.${priceLine} I've put together a detailed report covering recent local sales, market trends and what they mean for your property.\n\nPlease take a look and don't hesitate to reach out if you have any questions or would like to discuss next steps.${signOff}`;
  };

  // ─── Open send appraisal modal ───
  const openSendModal = (appr) => {
    setSendModal({ open: true, appraisalId: appr.id, address: appr.address });
    setSendRecipients([{
      name: appr.contact_name || "",
      email: appr.contact_email || "",
      mobile: appr.contact_phone || "",
      message: buildAppraisalMessage(appr, appr.contact_name),
    }]);
  };

  const handleSendAppraisal = async () => {
    const validRecipients = sendRecipients.filter(r => r.email.trim() || r.mobile.trim());
    if (validRecipients.length === 0) return;
    setSending(true);

    const now = Date.now();
    // Build send_history entries — one per channel actually sent
    const newEntries = [];
    validRecipients.forEach(r => {
      const name = r.name.trim();
      const email = r.email.trim();
      const mobile = r.mobile.trim();
      const message = r.message.trim();
      if (email) {
        newEntries.push({
          sent_at: now,
          channel: "email",
          recipient_email: email,
          recipient_name: name,
          message,
        });
      }
      if (mobile) {
        newEntries.push({
          sent_at: now,
          channel: "sms",
          recipient_mobile: mobile,
          recipient_name: name,
          message,
        });
      }
    });

    // Find the current appraisal's existing send_history
    const appr = appraisals.find(a => a.id === sendModal.appraisalId);
    const existingHistory = appr?.send_history || [];
    const updatedHistory = [...existingHistory, ...newEntries];

    let emailCount = 0;
    let smsCount = 0;
    try {
      // Send to each recipient (email and/or SMS in parallel)
      const calls = [];
      validRecipients.forEach(r => {
        const name = r.name.trim();
        const email = r.email.trim();
        const mobile = r.mobile.trim();
        const message = r.message.trim();
        if (email) {
          emailCount += 1;
          calls.push(
            handleTokenAndPostRequest(`${window.api_host}/send-appraisal`, {
              id: sendModal.appraisalId,
              recipient_email: email,
              recipient_name: name || undefined,
              message: message || undefined,
            })
          );
        }
        if (mobile) {
          smsCount += 1;
          calls.push(
            handleTokenAndPostRequest(`${window.api_host}/send-appraisal-sms`, {
              id: sendModal.appraisalId,
              recipient_mobile: mobile,
              recipient_name: name || undefined,
              message: message || undefined,
            })
          );
        }
      });
      await Promise.all(calls);
      // Persist the updated send_history via update-appraisal
      await handleTokenAndPostRequest(`${window.api_host}/update-appraisal`, {
        id: sendModal.appraisalId,
        send_history: updatedHistory,
      });
      const parts = [];
      if (emailCount) parts.push(`${emailCount} email${emailCount > 1 ? "s" : ""}`);
      if (smsCount) parts.push(`${smsCount} SMS`);
      setToast({ show: true, message: `Appraisal sent (${parts.join(" + ")})!` });
    } catch {
      setToast({ show: true, message: "Appraisal sent (demo)!" });
    }

    // Update local state
    setAppraisals(prev => prev.map(a =>
      a.id === sendModal.appraisalId ? { ...a, send_history: updatedHistory } : a
    ));
    setSendHistory(updatedHistory);

    setSending(false);
    setSendModal({ open: false, appraisalId: null, address: "" });
    setTimeout(() => setToast({ show: false, message: "" }), 3000);
  };

  // ─── Render ───
  return (
    <div style={{
      padding: isMobile ? "0 16px" : "0 32px",
      flex: 1,
      minWidth: 0,
      display: "flex",
      flexDirection: "column",
      background: "#f6f8fa",
      boxSizing: "border-box",
      width: "100%",
      position: "relative",
      overflow: "auto",
    }}>
      <div style={{ maxWidth: 900, marginLeft: "auto", marginRight: "auto", width: "100%", flex: 1, display: "flex", flexDirection: "column" }}>
        {/* ─── Sticky header ─── */}
        <div style={{
          position: "sticky", top: 0, zIndex: 10, background: "#f6f8fa",
          paddingTop: isMobile ? 72 : 32,
          marginLeft: isMobile ? -16 : -32, marginRight: isMobile ? -16 : -32,
          paddingLeft: isMobile ? 16 : 32, paddingRight: isMobile ? 16 : 32,
        }}>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 24, flexWrap: "wrap", gap: 12 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 16, flexWrap: "wrap" }}>
              <h1 style={{ fontSize: isMobile ? 26 : 32, fontWeight: 700, color: "#1f2937", margin: 0 }}>Appraisals</h1>
            </div>
            <button
              onClick={openCreateModal}
              style={{
                background: "#2563eb",
                color: "#fff",
                border: "none",
                borderRadius: 8,
                padding: "10px 20px",
                fontWeight: 700,
                cursor: "pointer",
                fontSize: 15,
                display: "flex",
                alignItems: "center",
                gap: 8,
                boxShadow: "0 1px 4px #0001",
              }}
            >
              <FaPlus /> Create Appraisal
            </button>
          </div>
        </div>

        {/* ─── List ─── */}
        {loading ? (
          <div style={{ display: "flex", flexDirection: "column", gap: 16, marginTop: 16 }}>
            {[...Array(4)].map((_, i) => (
              <div key={i} style={{
                background: "#fff", borderRadius: 16, overflow: "hidden",
                boxShadow: "0 1px 4px #0001", border: "1px solid #e5e7eb",
                display: "flex", flexDirection: isMobile ? "column" : "row",
              }}>
                <div style={{ width: isMobile ? "100%" : 200, minHeight: isMobile ? 180 : 160, position: "relative" }}>
                  <Skeleton height="100%" width="100%" borderRadius={0} style={{ display: "block", minHeight: isMobile ? 180 : 160 }} />
                </div>
                <div style={{ flex: 1, padding: "18px 22px", display: "flex", flexDirection: "column", gap: 8 }}>
                  <Skeleton width="65%" height={16} />
                  <div style={{ display: "flex", gap: 10 }}>
                    <Skeleton width={40} height={13} />
                    <Skeleton width={40} height={13} />
                    <Skeleton width={40} height={13} />
                    <Skeleton width={50} height={13} />
                  </div>
                  <Skeleton width="45%" height={14} />
                  <div style={{ display: "flex", gap: 6, marginTop: 4 }}>
                    <Skeleton width={60} height={20} borderRadius={10} />
                    <Skeleton width={70} height={20} borderRadius={10} />
                  </div>
                  <div style={{ display: "flex", gap: 8, marginTop: 6 }}>
                    <Skeleton width={70} height={30} borderRadius={8} />
                    <Skeleton width={60} height={30} borderRadius={8} />
                    <Skeleton width={60} height={30} borderRadius={8} />
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : error ? (
          <div style={{ color: "red", marginBottom: 24 }}>{error}</div>
        ) : appraisals.length === 0 ? (
          <div style={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center" }}>
          <div>
          <EmptyFeatureState
            icon={<FaHome />}
            title="No Appraisals Yet"
            description="Build stunning, agency-branded property appraisals powered by AI. Select comparable sales, analyse property photos for improvements, pull live suburb data, and publish a premium shareable presentation — all in minutes."
            features={[
              { icon: <FaMagic />, text: "AI scores and compares recent sales, and suggests property improvements with cost estimates" },
              { icon: <FaGlobe />, text: "One-click publish to a branded, shareable page your vendors will love" },
              { icon: <FaEnvelope />, text: "Send the appraisal directly to homeowners via email with a personalised message" },
            ]}
            examplePreview={
              <div style={{ padding: 0 }}>
                <img
                  src="https://realestate-reports-assets.s3.us-west-2.amazonaws.com/vendorleads-demo/appraisal_overlay.png"
                  alt="Example appraisal presentation"
                  style={{ width: "100%", display: "block", borderRadius: 0 }}
                />
              </div>
            }
            ctaLabel="Create Your First Appraisal"
            onCta={openCreateModal}
            isMobile={isMobile}
          />
          <div style={{ textAlign: "center", marginTop: -16, marginBottom: 32 }}>
            <a
              href="https://www.homeprice.site/appraisal/apr_4e8bbb8ae8be"
              target="_blank"
              rel="noopener noreferrer"
              style={{
                fontSize: 14, fontWeight: 600, color: "#2563eb",
                textDecoration: "none", display: "inline-flex", alignItems: "center", gap: 6,
              }}
            >
              <FaExternalLinkAlt style={{ fontSize: 11 }} />
              View a live demo appraisal
            </a>
          </div>
          </div>
          </div>
        ) : (
          <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
            {appraisals.map(appr => (
              <div
                key={appr.id}
                onClick={() => openEditModal(appr)}
                style={{
                  background: "#fff", borderRadius: 16, boxShadow: "0 2px 12px rgba(0,0,0,0.08)",
                  border: "1px solid #e5e7eb",
                  display: "flex", flexDirection: isMobile ? "column" : "row", overflow: "hidden",
                  cursor: "pointer",
                  transition: "box-shadow 0.2s, transform 0.15s",
                }}
                onMouseEnter={e => { e.currentTarget.style.boxShadow = "0 4px 20px rgba(0,0,0,0.12)"; e.currentTarget.style.transform = "translateY(-1px)"; }}
                onMouseLeave={e => { e.currentTarget.style.boxShadow = "0 2px 12px rgba(0,0,0,0.08)"; e.currentTarget.style.transform = "none"; }}
              >
                {/* Thumbnail */}
                <div style={{
                  position: "relative", flexShrink: 0,
                  width: isMobile ? "100%" : 200,
                  height: isMobile ? 180 : "auto",
                  minHeight: isMobile ? undefined : 160,
                  background: "#f3f4f6",
                }}>
                  {appr.image && !failedImages.has(appr.image) ? (
                    <img
                      src={appr.image}
                      alt=""
                      onError={() => setFailedImages(prev => new Set(prev).add(appr.image))}
                      style={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }}
                    />
                  ) : (
                    <div style={{
                      width: "100%", height: "100%", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center",
                      minHeight: isMobile ? 180 : 160, gap: 6,
                    }}>
                      <FaHome size={36} color="#d1d5db" />
                      <span style={{ fontSize: 11, color: "#c0c4cc", fontWeight: 500 }}>No image</span>
                    </div>
                  )}
                  {/* Status badge overlay */}
                  <span style={{
                    position: "absolute", top: 10, left: 10,
                    borderRadius: 20, padding: "3px 12px", fontWeight: 700, fontSize: 11,
                    backdropFilter: "blur(8px)", WebkitBackdropFilter: "blur(8px)",
                    background: appr.status === "published" ? "rgba(124,58,237,0.85)" : "rgba(0,0,0,0.55)",
                    color: "#fff",
                    letterSpacing: 0.3,
                  }}>
                    {appr.status === "published" ? "Published" : "Draft"}
                  </span>
                  {/* Property type badge */}
                  {appr.property_type && (
                    <span style={{
                      position: "absolute", bottom: 10, left: 10,
                      borderRadius: 10, padding: "2px 10px", fontWeight: 600, fontSize: 11,
                      background: "rgba(255,255,255,0.9)", color: "#374151",
                      backdropFilter: "blur(4px)", WebkitBackdropFilter: "blur(4px)",
                    }}>
                      {appr.property_type}
                    </span>
                  )}
                </div>

                {/* Content */}
                <div style={{ padding: isMobile ? "16px 16px 14px" : "18px 22px", display: "flex", flexDirection: "column", flex: 1, minWidth: 0, gap: 8 }}>
                  {/* Address */}
                  <div style={{ fontWeight: 700, fontSize: 16, color: "#1a1a1a", lineHeight: 1.3, display: "flex", alignItems: "center", gap: 8 }}>
                    {appr.address}
                    <AgentBadge agentName={appr.agent_name} />
                  </div>

                  {/* Bed / Bath / Car / Land row */}
                  <div style={{ display: "flex", gap: 14, flexWrap: "wrap", fontSize: 13, color: "#6b7280" }}>
                    {appr.bedrooms != null && (
                      <span style={{ display: "flex", alignItems: "center", gap: 4 }}><FaBed size={12} /> {appr.bedrooms}</span>
                    )}
                    {appr.bathrooms != null && (
                      <span style={{ display: "flex", alignItems: "center", gap: 4 }}><FaBath size={12} /> {appr.bathrooms}</span>
                    )}
                    {appr.parking != null && (
                      <span style={{ display: "flex", alignItems: "center", gap: 4 }}><FaCar size={12} /> {appr.parking}</span>
                    )}
                    {appr.land_size != null && (
                      <span style={{ display: "flex", alignItems: "center", gap: 4 }}><FaRulerCombined size={11} /> {appr.land_size}m²</span>
                    )}
                  </div>

                  {/* Price info */}
                  <div style={{ display: "flex", gap: 16, flexWrap: "wrap", fontSize: 14 }}>
                    {appr.include_sale && appr.price_low != null && (
                      <div style={{ fontWeight: 700, color: "#1a1a1a" }}>
                        {formatCurrency(appr.price_low)} – {formatCurrency(appr.price_high)}
                      </div>
                    )}
                    {appr.include_rental && appr.rent_low != null && (
                      <div style={{ fontWeight: 600, color: "#6b7280" }}>
                        {formatCurrency(appr.rent_low)} – {formatCurrency(appr.rent_high)} pw
                      </div>
                    )}
                  </div>

                  {/* Meta pills row */}
                  <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginTop: 2 }}>
                    {appr.comparables_count > 0 && (
                      <span style={{
                        background: "#f3f4f6", color: "#374151", padding: "2px 10px",
                        borderRadius: 10, fontWeight: 500, fontSize: 11,
                      }}>
                        {appr.comparables_count} comparable{appr.comparables_count !== 1 ? "s" : ""}
                      </span>
                    )}
                    <span style={{
                      display: "flex", alignItems: "center", gap: 3,
                      background: "#f3f4f6", color: "#374151", padding: "2px 10px",
                      borderRadius: 10, fontWeight: 500, fontSize: 11,
                    }}>
                      <FaCalendarAlt size={9} />
                      {epochToDate(appr.updated_at)?.toLocaleDateString() || epochToDate(appr.created_at)?.toLocaleDateString()}
                    </span>
                  </div>

                  {/* Action buttons */}
                  {appr.is_mine !== false && (
                  <div style={{ display: "flex", gap: 8, marginTop: 4, flexWrap: "wrap" }}
                    onClick={e => e.stopPropagation()}
                  >
                    <button
                      onClick={() => handlePublish(appr.id)}
                      disabled={publishing}
                      style={{
                        background: "#7c3aed", color: "#fff", border: "none",
                        borderRadius: 8, padding: "6px 14px", fontWeight: 600,
                        cursor: publishing ? "not-allowed" : "pointer", fontSize: 12,
                        display: "flex", alignItems: "center", gap: 5,
                      }}
                    >
                      <FaGlobe size={10} /> Publish
                    </button>
                    {appr.published_url && (
                      <a
                        href={localizeUrl(appr.published_url)}
                        target="_blank"
                        rel="noopener noreferrer"
                        style={{
                          background: "#f3f4f6", color: "#374151", border: "none",
                          borderRadius: 8, padding: "6px 14px", fontWeight: 600,
                          cursor: "pointer", fontSize: 12, display: "flex", alignItems: "center", gap: 5,
                          textDecoration: "none",
                        }}
                      >
                        <FaExternalLinkAlt size={10} /> View
                      </a>
                    )}
                    {appr.status === "published" && (
                      <button
                        onClick={() => openSendModal(appr)}
                        style={{
                          background: "#f3f4f6", color: "#374151", border: "none",
                          borderRadius: 8, padding: "6px 14px", fontWeight: 600,
                          cursor: "pointer", fontSize: 12, display: "flex", alignItems: "center", gap: 5,
                        }}
                      >
                        <FaPaperPlane size={10} /> Send
                      </button>
                    )}
                    <button
                      onClick={() => setDeleteModal({ open: true, id: appr.id })}
                      style={{
                        background: "transparent", color: "#9ca3af", border: "none",
                        borderRadius: 8, padding: "6px 10px", fontWeight: 600,
                        cursor: "pointer", fontSize: 12, display: "flex", alignItems: "center", gap: 5,
                        marginLeft: "auto",
                      }}
                    >
                      <FaTrash size={10} />
                    </button>
                  </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}

        <div style={{ color: "#888", fontSize: 15, marginTop: 32, marginBottom: 40 }}>
          Property appraisals help you present a professional valuation to your clients.
        </div>
      </div>

      {/* ═══════════════════════════════════════════════
          CREATE APPRAISAL MODAL (full-screen)
         ═══════════════════════════════════════════════ */}
      {modalOpen && (
        <div
          onClick={closeModal}
          style={{
            position: "fixed", top: 0, right: 0, bottom: 0,
            left: isMobile ? 0 : 260,
            background: "rgba(0,0,0,0.25)", zIndex: 1000,
            display: "flex", alignItems: "center", justifyContent: "center", padding: 0,
          }}
        >
          <div
            onClick={e => e.stopPropagation()}
            style={{
              background: "#fff", borderRadius: 0, width: "100%", height: "100%",
              boxShadow: "none", display: "flex", flexDirection: "column", overflow: "hidden",
            }}
          >
            {/* Header */}
            <div style={{
              fontSize: 20, fontWeight: 700, padding: "20px 28px 12px 28px",
              borderBottom: "1px solid #e5e7eb", flexShrink: 0,
              display: "flex", alignItems: "center", justifyContent: "space-between",
            }}>
              {editingId ? "Edit Appraisal" : "Create Appraisal"}
              <button
                onClick={closeModal}
                disabled={saving}
                style={{ background: "none", border: "none", cursor: "pointer", fontSize: 22, color: "#9ca3af", padding: "0 4px", lineHeight: 1 }}
                title="Close"
              >×</button>
            </div>

            {/* Tab bar */}
            <div style={{
              display: "flex", borderBottom: "1px solid #e5e7eb", flexShrink: 0,
              padding: "0 28px", background: "#f9fafb",
              overflowX: "auto", WebkitOverflowScrolling: "touch",
              msOverflowStyle: "none", scrollbarWidth: "none",
            }}>
              {[
                { key: "details", label: "Details" },
                { key: "improvements", label: "Improvements" },
                { key: "comparables", label: "Comparables" },
                { key: "location", label: "Location" },
                { key: "valuation", label: "Valuation" },
                { key: "team", label: "Team" },
              ].map(tab => (
                  <div key={tab.key} style={{ display: "flex", alignItems: "center", flexShrink: 0 }}>
                    <input
                      type="checkbox"
                      checked={sectionToggles[tab.key]}
                      onChange={(e) => { e.stopPropagation(); toggleSection(tab.key); }}
                      data-track-event="false"
                      style={{ margin: "0 2px 0 8px", cursor: "pointer", accentColor: "#2563eb" }}
                    />
                    <button
                      onClick={() => setModalTab(tab.key)}
                      style={{
                        background: "none", border: "none", borderBottom: modalTab === tab.key ? "2px solid #2563eb" : "2px solid transparent",
                        padding: "10px 10px 10px 4px", fontWeight: modalTab === tab.key ? 700 : 500,
                        fontSize: 14, color: !sectionToggles[tab.key] ? "#d1d5db" : modalTab === tab.key ? "#2563eb" : "#6b7280",
                        cursor: "pointer", transition: "color 0.15s, border-color 0.15s",
                        whiteSpace: "nowrap", flexShrink: 0,
                        textDecoration: !sectionToggles[tab.key] ? "line-through" : "none",
                      }}
                    >
                      {tab.label}
                    </button>
                  </div>
              ))}
            </div>

            {/* Scrollable body */}
            <div style={{ flex: 1, overflowY: "auto", padding: "16px 28px", display: "flex", flexDirection: "column", gap: 20, position: "relative" }}>

              {/* Loading overlay */}
              {modalLoading && (
                <div style={{
                  position: "absolute", top: 0, left: 0, right: 0, bottom: 0,
                  background: "rgba(255,255,255,0.85)", zIndex: 10,
                  display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: 12,
                }}>
                  <Spinner animation="border" style={{ width: 40, height: 40, color: "#2563eb" }} />
                  <div style={{ fontSize: 15, color: "#6b7280", fontWeight: 500 }}>Loading appraisal data…</div>
                </div>
              )}

              {modalTab === "details" && !sectionToggles.details && (
                <div style={{ textAlign: "center", padding: "60px 20px", color: "#9ca3af" }}>
                  <div style={{ fontSize: 32, marginBottom: 8 }}>🙈</div>
                  <div style={{ fontSize: 14 }}>This section is excluded from the appraisal. Check the box above to include it.</div>
                </div>
              )}
              {modalTab === "details" && sectionToggles.details && (<>
              {/* ─── Data type toggles ─── */}
              <div>
                <label style={{ fontWeight: 600, fontSize: 14, color: "#374151", marginBottom: 2, display: "block" }}>Appraisal Type</label>
                <div style={{ fontSize: 12, color: "#6b7280", lineHeight: 1.4, marginBottom: 8 }}>Choose whether to include sale and/or rental data in this appraisal.</div>
                <div style={{ display: "flex", gap: 16 }}>
                  <label style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 15, cursor: "pointer", color: "#374151", fontWeight: 600 }}>
                    <input type="checkbox" checked={includeSale} onChange={e => setIncludeSale(e.target.checked)} style={{ width: 18, height: 18 }} />
                    Sale
                  </label>
                  <label style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 15, cursor: "pointer", color: "#374151", fontWeight: 600 }}>
                    <input type="checkbox" checked={includeRental} onChange={e => setIncludeRental(e.target.checked)} style={{ width: 18, height: 18 }} />
                    Rental
                  </label>
                </div>
              </div>

              {/* ─── Address search ─── */}
              <div style={{ position: "relative" }}>
                <label style={{ fontWeight: 600, fontSize: 14, color: "#374151", marginBottom: 2, display: "block" }}>Property Address</label>
                <div style={{ fontSize: 12, color: "#6b7280", lineHeight: 1.4, marginBottom: 8 }}>Search for the property address to appraise.</div>
                <div style={{ position: "relative" }}>
                  <input
                    ref={addressInputRef}
                    value={addressQuery}
                    autoComplete="off"
                    onChange={e => handleAddressSearch(e.target.value)}
                    onFocus={() => { if (addressResults.length > 0) setAddressDropdownOpen(true); }}
                    onBlur={() => setTimeout(() => setAddressDropdownOpen(false), 200)}
                    placeholder="Type an address…"
                    style={{
                      width: "100%", padding: "10px 12px", borderRadius: 6,
                      border: "1px solid #e5e7eb", fontSize: 15, boxSizing: "border-box",
                    }}
                  />
                  {addressSearching && (
                    <div style={{ position: "absolute", right: 12, top: "50%", transform: "translateY(-50%)" }}>
                      <Spinner animation="border" size="sm" style={{ width: 16, height: 16 }} />
                    </div>
                  )}
                </div>
                {addressDropdownOpen && addressResults.length > 0 && (
                  <div style={{
                    position: "absolute", left: 0, right: 0, top: "100%", zIndex: 10,
                    background: "#fff", border: "1px solid #e5e7eb", borderRadius: 8,
                    boxShadow: "0 8px 24px #0002", maxHeight: 220, overflowY: "auto", marginTop: 4,
                  }}>
                    {addressResults.map((item, idx) => (
                      <div
                        key={item.property_id || idx}
                        onMouseDown={e => { e.preventDefault(); handleSelectAddress(item); }}
                        style={{
                          padding: "10px 14px", cursor: "pointer", fontSize: 14,
                          borderBottom: idx < addressResults.length - 1 ? "1px solid #f3f4f6" : "none",
                        }}
                        onMouseOver={e => e.currentTarget.style.background = "#f0f7ff"}
                        onMouseOut={e => e.currentTarget.style.background = "#fff"}
                      >
                        {item.address || item.address_text}
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* ─── Image Carousel ─── */}
              {selectedAddress && (() => {
                const allImages = [
                  ...propertyImages.filter(url => !failedImages.has(url)).map(url => ({ url, label: addressQuery || "Subject Property" })),
                  ...comparables.filter(c => c.photo_url_main && !failedImages.has(c.photo_url_main)).map(c => ({ url: c.photo_url_main, label: c.display_address })),
                  ...rentalComparables.filter(c => c.photo_url_main && !failedImages.has(c.photo_url_main)).map(c => ({ url: c.photo_url_main, label: c.display_address })),
                ];
                if (allImages.length === 0) return (
                  <div style={{ borderRadius: 10, overflow: "hidden", background: "#f3f4f6", minHeight: isMobile ? 200 : 320, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: 8 }}>
                    <FaHome style={{ fontSize: 48, color: "#d1d5db" }} />
                    <span style={{ fontSize: 12, color: "#c0c4cc", fontWeight: 500 }}>No images available</span>
                  </div>
                );
                const safeIdx = Math.min(carouselIndex, allImages.length - 1);
                return (
                  <div style={{ position: "relative", borderRadius: 10, overflow: "hidden", background: "#000", minHeight: isMobile ? 200 : 320, flexShrink: 0 }}>
                    <img
                      src={allImages[safeIdx].url}
                      alt=""
                      onError={() => setFailedImages(prev => new Set(prev).add(allImages[safeIdx].url))}
                      style={{
                        width: "100%",
                        height: isMobile ? 200 : 320,
                        objectFit: "cover",
                        display: "block",
                      }}
                    />
                    {/* Label */}
                    <div style={{
                      position: "absolute", bottom: 0, left: 0, right: 0,
                      background: "linear-gradient(transparent, rgba(0,0,0,0.7))",
                      padding: "24px 16px 12px",
                      color: "#fff", fontSize: 13, fontWeight: 600,
                      display: "flex", alignItems: "center", justifyContent: "space-between",
                    }}>
                      <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", flex: 1 }}>
                        {safeIdx < propertyImages.length ? "Subject Property" : allImages[safeIdx].label}
                      </span>
                      <span style={{ flexShrink: 0, marginLeft: 8, opacity: 0.8, fontSize: 12 }}>
                        {safeIdx + 1} / {allImages.length}
                      </span>
                    </div>
                    {/* Prev */}
                    {allImages.length > 1 && (
                      <button
                        onClick={() => setCarouselIndex(i => (i - 1 + allImages.length) % allImages.length)}
                        style={{
                          position: "absolute", left: 8, top: "50%", transform: "translateY(-50%)",
                          background: "rgba(0,0,0,0.45)", color: "#fff", border: "none",
                          borderRadius: "50%", width: 36, height: 36, cursor: "pointer",
                          display: "flex", alignItems: "center", justifyContent: "center", fontSize: 16,
                        }}
                      >
                        <FaChevronLeft />
                      </button>
                    )}
                    {/* Next */}
                    {allImages.length > 1 && (
                      <button
                        onClick={() => setCarouselIndex(i => (i + 1) % allImages.length)}
                        style={{
                          position: "absolute", right: 8, top: "50%", transform: "translateY(-50%)",
                          background: "rgba(0,0,0,0.45)", color: "#fff", border: "none",
                          borderRadius: "50%", width: 36, height: 36, cursor: "pointer",
                          display: "flex", alignItems: "center", justifyContent: "center", fontSize: 16,
                        }}
                      >
                        <FaChevronRight />
                      </button>
                    )}
                    {/* Dots */}
                    {allImages.length > 1 && allImages.length <= 20 && (
                      <div style={{
                        position: "absolute", bottom: 40, left: 0, right: 0,
                        display: "flex", justifyContent: "center", gap: 5,
                      }}>
                        {allImages.map((_, i) => (
                          <button
                            key={i}
                            onClick={() => setCarouselIndex(i)}
                            style={{
                              width: i === safeIdx ? 18 : 8, height: 8,
                              borderRadius: 4, border: "none", cursor: "pointer",
                              background: i === safeIdx ? "#fff" : "rgba(255,255,255,0.45)",
                              transition: "width 0.2s, background 0.2s",
                              padding: 0,
                            }}
                          />
                        ))}
                      </div>
                    )}
                  </div>
                );
              })()}
              </>)}

              {/* ─── Image Management ─── */}
              {selectedAddress && modalTab === "details" && (
                <div style={{ marginBottom: 16 }}>
                  <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 8 }}>
                    <label style={{ fontWeight: 600, fontSize: 14, color: "#374151", display: "block" }}>Property Images</label>
                    <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                      {propertyImages.length > 0 && (
                        <button
                          onClick={() => {
                            if (deselectedImages.size === 0) {
                              setDeselectedImages(new Set(propertyImages));
                            } else {
                              setDeselectedImages(new Set());
                            }
                          }}
                          style={{ background: "none", border: "none", fontSize: 12, color: "#6366f1", cursor: "pointer", fontWeight: 500 }}
                        >
                          {deselectedImages.size === 0 ? "Deselect All" : "Select All"}
                        </button>
                      )}
                      <input
                        ref={imageInputRef}
                        type="file"
                        accept="image/png,image/jpeg,image/gif"
                        multiple
                        style={{ display: "none" }}
                        onChange={e => handleUploadImages(e.target.files)}
                      />
                      <button
                        onClick={() => {
                          if (!editingId) {
                            setToast({ show: true, message: "Save the appraisal first before uploading images." });
                            setTimeout(() => setToast({ show: false, message: "" }), 3000);
                            return;
                          }
                          imageInputRef.current?.click();
                        }}
                        disabled={uploadingImages}
                        style={{
                          display: "flex", alignItems: "center", gap: 5,
                          background: "linear-gradient(135deg, #6366f1, #818cf8)", color: "#fff",
                          border: "none", borderRadius: 8, padding: "6px 14px",
                          fontSize: 12, fontWeight: 600, cursor: "pointer",
                          opacity: uploadingImages ? 0.6 : 1,
                        }}
                      >
                        {uploadingImages ? <Spinner animation="border" size="sm" /> : <FaUpload size={11} />}
                        {uploadingImages ? "Uploading…" : "Upload"}
                      </button>
                    </div>
                  </div>
                  <div style={{ fontSize: 12, color: "#6b7280", marginBottom: 10 }}>
                    Click images to select or deselect which ones appear on the appraisal.
                  </div>
                  {propertyImages.length > 0 ? (
                    <div style={{ display: "grid", gridTemplateColumns: isMobile ? "repeat(3, 1fr)" : "repeat(5, 1fr)", gap: 8 }}>
                      {propertyImages.map((url, idx) => {
                        const isSelected = !deselectedImages.has(url);
                        return (
                          <div
                            key={idx}
                            onClick={() => {
                              setDeselectedImages(prev => {
                                const next = new Set(prev);
                                if (isSelected) next.add(url);
                                else next.delete(url);
                                return next;
                              });
                            }}
                            style={{
                              position: "relative", borderRadius: 8, overflow: "hidden",
                              cursor: "pointer", border: isSelected ? "2px solid #6366f1" : "2px solid #e5e7eb",
                              opacity: isSelected ? 1 : 0.45,
                              transition: "opacity 0.15s, border-color 0.15s",
                            }}
                          >
                            <img
                              src={url}
                              alt={`Property ${idx + 1}`}
                              style={{ width: "100%", height: 80, objectFit: "cover", display: "block" }}
                              onError={() => setFailedImages(prev => new Set(prev).add(url))}
                            />
                            <div style={{
                              position: "absolute", top: 4, left: 4,
                              width: 20, height: 20, borderRadius: 4,
                              background: isSelected ? "#6366f1" : "rgba(255,255,255,0.85)",
                              border: isSelected ? "none" : "1px solid #d1d5db",
                              display: "flex", alignItems: "center", justifyContent: "center",
                            }}>
                              {isSelected && <FaCheck size={10} style={{ color: "#fff" }} />}
                            </div>
                            <button
                              onClick={e => { e.stopPropagation(); handleDeleteImage(url); }}
                              style={{
                                position: "absolute", top: 4, right: 4,
                                width: 20, height: 20, borderRadius: "50%",
                                background: "rgba(0,0,0,0.55)", color: "#fff",
                                border: "none", cursor: "pointer",
                                display: "flex", alignItems: "center", justifyContent: "center",
                                fontSize: 10,
                              }}
                            >
                              <FaTimes size={8} />
                            </button>
                          </div>
                        );
                      })}
                    </div>
                  ) : (
                    <div style={{
                      background: "#f9fafb", border: "2px dashed #e5e7eb", borderRadius: 10,
                      padding: 32, textAlign: "center", color: "#9ca3af",
                    }}>
                      <FaImage size={28} style={{ marginBottom: 8 }} />
                      <div style={{ fontSize: 13, fontWeight: 500 }}>No images yet</div>
                      <div style={{ fontSize: 11, marginTop: 4 }}>Upload images or search for a property to load images automatically</div>
                    </div>
                  )}
                </div>
              )}

              {/* ─── Property features ─── */}
              {selectedAddress && (
                <>
                  {modalTab === "details" && sectionToggles.details && (<>
                  <div>
                    <label style={{ fontWeight: 600, fontSize: 14, color: "#374151", marginBottom: 2, display: "block" }}>Property Features</label>
                    <div style={{ fontSize: 12, color: "#6b7280", lineHeight: 1.4, marginBottom: 8 }}>Edit or add property details. These will appear on the appraisal.</div>

                    <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr 1fr" : "1fr 1fr 1fr 1fr", gap: 12 }}>
                      <div>
                        <label style={{ fontSize: 12, color: "#6b7280" }}>Bedrooms</label>
                        <input type="number" min="0" value={features.bedrooms} onChange={e => setFeatures(f => ({ ...f, bedrooms: e.target.value }))}
                          style={{ width: "100%", padding: "8px 10px", borderRadius: 6, border: "1px solid #e5e7eb", fontSize: 14, boxSizing: "border-box" }} />
                      </div>
                      <div>
                        <label style={{ fontSize: 12, color: "#6b7280" }}>Bathrooms</label>
                        <input type="number" min="0" value={features.bathrooms} onChange={e => setFeatures(f => ({ ...f, bathrooms: e.target.value }))}
                          style={{ width: "100%", padding: "8px 10px", borderRadius: 6, border: "1px solid #e5e7eb", fontSize: 14, boxSizing: "border-box" }} />
                      </div>
                      <div>
                        <label style={{ fontSize: 12, color: "#6b7280" }}>Parking</label>
                        <input type="number" min="0" value={features.parking} onChange={e => setFeatures(f => ({ ...f, parking: e.target.value }))}
                          style={{ width: "100%", padding: "8px 10px", borderRadius: 6, border: "1px solid #e5e7eb", fontSize: 14, boxSizing: "border-box" }} />
                      </div>
                      <div>
                        <label style={{ fontSize: 12, color: "#6b7280" }}>Land Size</label>
                        <input value={features.land_size} onChange={e => setFeatures(f => ({ ...f, land_size: e.target.value }))} placeholder="e.g. 450 sqm"
                          style={{ width: "100%", padding: "8px 10px", borderRadius: 6, border: "1px solid #e5e7eb", fontSize: 14, boxSizing: "border-box" }} />
                      </div>
                    </div>

                    <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr 1fr" : "1fr 1fr 1fr 1fr", gap: 12, marginTop: 12 }}>
                      <div>
                        <label style={{ fontSize: 12, color: "#6b7280" }}>Property Type</label>
                        <select value={features.property_type} onChange={e => setFeatures(f => ({ ...f, property_type: e.target.value }))}
                          style={{ width: "100%", padding: "8px 10px", borderRadius: 6, border: "1px solid #e5e7eb", fontSize: 14, boxSizing: "border-box", background: "#fff" }}>
                          {PROPERTY_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
                        </select>
                      </div>
                      <div>
                        <label style={{ fontSize: 12, color: "#6b7280" }}>Year Built</label>
                        <input type="number" value={features.year_built} onChange={e => setFeatures(f => ({ ...f, year_built: e.target.value }))}
                          placeholder="e.g. 1990"
                          style={{ width: "100%", padding: "8px 10px", borderRadius: 6, border: "1px solid #e5e7eb", fontSize: 14, boxSizing: "border-box" }} />
                      </div>
                      <div>
                        <label style={{ fontSize: 12, color: "#6b7280" }}>Condition</label>
                        <select value={features.condition} onChange={e => setFeatures(f => ({ ...f, condition: e.target.value }))}
                          style={{ width: "100%", padding: "8px 10px", borderRadius: 6, border: "1px solid #e5e7eb", fontSize: 14, boxSizing: "border-box", background: "#fff" }}>
                          {CONDITIONS.map(c => <option key={c} value={c}>{c}</option>)}
                        </select>
                      </div>
                    </div>

                    <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr 1fr" : "1fr 1fr 1fr 1fr", gap: 12, marginTop: 12 }}>
                      <div>
                        <label style={{ fontSize: 12, color: "#6b7280" }}>Latitude</label>
                        <input type="number" step="any" value={features.latitude} onChange={e => setFeatures(f => ({ ...f, latitude: e.target.value }))}
                          placeholder="e.g. -33.920"
                          style={{ width: "100%", padding: "8px 10px", borderRadius: 6, border: "1px solid #e5e7eb", fontSize: 14, boxSizing: "border-box" }} />
                      </div>
                      <div>
                        <label style={{ fontSize: 12, color: "#6b7280" }}>Longitude</label>
                        <input type="number" step="any" value={features.longitude} onChange={e => setFeatures(f => ({ ...f, longitude: e.target.value }))}
                          placeholder="e.g. 151.255"
                          style={{ width: "100%", padding: "8px 10px", borderRadius: 6, border: "1px solid #e5e7eb", fontSize: 14, boxSizing: "border-box" }} />
                      </div>
                    </div>

                    {/* AI Property Feature Analysis */}
                    <div style={{ marginTop: 16, padding: 16, borderRadius: 10, border: "1px solid #e0e7ff", background: "#f5f7ff" }}>
                      <label style={{ fontSize: 13, fontWeight: 600, color: "#4338ca", marginBottom: 6, display: "flex", alignItems: "center", gap: 6 }}>
                        <FaMagic style={{ fontSize: 13 }} />
                        AI Property Feature Analysis
                      </label>
                      <div style={{ fontSize: 12, color: "#6b7280", lineHeight: 1.4, marginBottom: 8 }}>Analyses property images with AI to detect features and generate a description. The prompt is optional.</div>
                      <textarea
                        value={featureAnalysisPrompt}
                        onChange={e => setFeatureAnalysisPrompt(e.target.value)}
                        placeholder="Optional: e.g. Highlight the renovated kitchen, mention the pool and outdoor entertaining area…"
                        rows={2}
                        style={{ width: "100%", padding: "8px 10px", borderRadius: 6, border: "1px solid #c7d2fe", fontSize: 14, boxSizing: "border-box", resize: "vertical", fontFamily: "inherit", marginBottom: 8 }}
                      />
                      <button
                        onClick={async () => {
                          if (!propertyImages.length) return;
                          setFeatureAnalysisLoading(true);
                          // Start progress bar — ~3s per image, up to 5 images
                          setFeatureAnalysisProgress(0);
                          const imgCount = Math.min(propertyImages.length, 5);
                          const DURATION = imgCount * 3000;
                          const startTime = Date.now();
                          if (featureAnalysisTimer.current) clearInterval(featureAnalysisTimer.current);
                          featureAnalysisTimer.current = setInterval(() => {
                            const elapsed = Date.now() - startTime;
                            const ratio = Math.min(elapsed / DURATION, 1);
                            const progress = Math.min(95, ratio * 100 * (2 - ratio));
                            setFeatureAnalysisProgress(progress);
                          }, 200);
                          try {
                            const res = await handleTokenAndPostRequest(
                              `${window.api_host}/analyse-property-images`,
                              { image_urls: propertyImages.slice(0, 5), prompt: featureAnalysisPrompt }
                            );
                            const data = res.data;
                            if (data?.features && Array.isArray(data.features)) {
                              setSelectedPropertyFeatures(prev => {
                                const merged = new Set([...prev, ...data.features]);
                                return [...merged];
                              });
                            }
                            if (data?.description) {
                              setFeatures(prev => ({ ...prev, notes: data.description }));
                            }
                          } catch (err) {
                            console.error("Feature analysis failed", err);
                          } finally {
                            if (featureAnalysisTimer.current) clearInterval(featureAnalysisTimer.current);
                            setFeatureAnalysisProgress(100);
                            setTimeout(() => { setFeatureAnalysisProgress(0); setFeatureAnalysisLoading(false); }, 500);
                          }
                        }}
                        disabled={featureAnalysisLoading || !propertyImages.length}
                        style={{
                          display: "inline-flex", alignItems: "center", gap: 6,
                          padding: "8px 18px", borderRadius: 8, border: "none", fontSize: 14, fontWeight: 600, cursor: "pointer",
                          background: featureAnalysisLoading || !propertyImages.length ? "#c7d2fe" : "linear-gradient(135deg, #6366f1, #818cf8)",
                          color: "#fff", transition: "all 0.15s",
                        }}
                      >
                        {featureAnalysisLoading ? <><Spinner animation="border" size="sm" style={{ width: 14, height: 14 }} /> Analysing…</> : <><FaMagic /> Analyse</>}
                      </button>
                      {/* Progress bar */}
                      {featureAnalysisLoading && (
                        <div style={{ marginTop: 10 }}>
                          <div style={{ width: "100%", height: 4, background: "#e5e7eb", borderRadius: 2, overflow: "hidden" }}>
                            <div style={{
                              height: "100%",
                              width: `${featureAnalysisProgress}%`,
                              background: "linear-gradient(90deg, #6366f1, #818cf8)",
                              borderRadius: 2,
                              transition: featureAnalysisProgress === 100 ? "width 0.3s ease" : "width 0.4s linear",
                            }} />
                          </div>
                          <div style={{ fontSize: 11, color: "#9ca3af", marginTop: 4, textAlign: "center" }}>
                            {featureAnalysisProgress < 95
                              ? `Analysing ${Math.min(propertyImages.length, 5)} image${Math.min(propertyImages.length, 5) !== 1 ? "s" : ""}… ~${Math.min(propertyImages.length, 5) * 3}s`
                              : "Almost there…"}
                          </div>
                        </div>
                      )}
                    </div>

                    {/* Features multiselect dropdown */}
                    <div style={{ marginTop: 12, position: "relative" }} ref={featuresDropdownRef}>
                      <label style={{ fontSize: 12, color: "#6b7280", marginBottom: 6, display: "block" }}>Features</label>
                      {/* Selected feature pills */}
                      {selectedPropertyFeatures.length > 0 && (
                        <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginBottom: 8 }}>
                          {selectedPropertyFeatures.map(f => (
                            <span key={f} style={{
                              display: "inline-flex", alignItems: "center", gap: 4,
                              padding: "4px 10px", borderRadius: 20, fontSize: 12, fontWeight: 500,
                              background: "#eef2ff", color: "#4338ca", border: "1px solid #c7d2fe",
                            }}>
                              {f}
                              <FaTimes style={{ fontSize: 9, cursor: "pointer" }} onClick={() => setSelectedPropertyFeatures(prev => prev.filter(x => x !== f))} />
                            </span>
                          ))}
                        </div>
                      )}
                      {/* Dropdown trigger */}
                      <button
                        type="button"
                        onClick={() => { setFeaturesDropdownOpen(prev => !prev); setFeatureSearchQuery(""); }}
                        style={{
                          display: "flex", alignItems: "center", justifyContent: "space-between", width: "100%",
                          padding: "8px 12px", borderRadius: 6, fontSize: 14, cursor: "pointer",
                          border: "1px solid #e5e7eb", background: "#fff", color: "#374151",
                          boxSizing: "border-box",
                        }}
                      >
                        <span style={{ color: selectedPropertyFeatures.length > 0 ? "#374151" : "#9ca3af" }}>
                          {selectedPropertyFeatures.length > 0 ? `${selectedPropertyFeatures.length} feature${selectedPropertyFeatures.length !== 1 ? "s" : ""} selected` : "Select features…"}
                        </span>
                        <FaChevronDown style={{ fontSize: 10, color: "#9ca3af", transition: "transform .2s", transform: featuresDropdownOpen ? "rotate(180deg)" : "rotate(0)" }} />
                      </button>
                      {/* Dropdown panel */}
                      {featuresDropdownOpen && (
                        <div style={{
                          position: "absolute", top: "100%", left: 0, right: 0, zIndex: 20,
                          background: "#fff", border: "1px solid #e5e7eb", borderRadius: 10,
                          boxShadow: "0 8px 24px rgba(0,0,0,.12)", marginTop: 4,
                          maxHeight: 300, display: "flex", flexDirection: "column",
                        }}>
                          {/* Search input */}
                          <div style={{ padding: "8px 8px 4px", borderBottom: "1px solid #f3f4f6" }}>
                            <input
                              autoFocus
                              value={featureSearchQuery}
                              onChange={e => setFeatureSearchQuery(e.target.value)}
                              placeholder="Search or type custom feature…"
                              style={{
                                width: "100%", padding: "7px 10px", borderRadius: 6, border: "1px solid #e5e7eb",
                                fontSize: 13, boxSizing: "border-box", outline: "none",
                              }}
                              onKeyDown={e => {
                                if (e.key === "Enter" && featureSearchQuery.trim()) {
                                  const trimmed = featureSearchQuery.trim();
                                  const match = PROPERTY_FEATURES_LIST.find(f => f.toLowerCase() === trimmed.toLowerCase());
                                  const toAdd = match || trimmed;
                                  if (!selectedPropertyFeatures.includes(toAdd)) {
                                    setSelectedPropertyFeatures(prev => [...prev, toAdd]);
                                  }
                                  setFeatureSearchQuery("");
                                }
                              }}
                            />
                          </div>
                          {/* Options list */}
                          <div style={{ overflowY: "auto", maxHeight: 220, padding: 4 }}>
                            {PROPERTY_FEATURES_LIST
                              .filter(f => !featureSearchQuery || f.toLowerCase().includes(featureSearchQuery.toLowerCase()))
                              .map(f => {
                                const isSelected = selectedPropertyFeatures.includes(f);
                                return (
                                  <button
                                    key={f}
                                    type="button"
                                    onClick={() => setSelectedPropertyFeatures(prev => isSelected ? prev.filter(x => x !== f) : [...prev, f])}
                                    style={{
                                      display: "flex", alignItems: "center", gap: 8, width: "100%",
                                      padding: "7px 10px", borderRadius: 6, fontSize: 13, fontWeight: 500,
                                      cursor: "pointer", border: "none", textAlign: "left",
                                      background: isSelected ? "#eef2ff" : "transparent",
                                      color: isSelected ? "#4338ca" : "#374151",
                                      transition: "background 0.1s",
                                    }}
                                    onMouseEnter={e => { if (!isSelected) e.currentTarget.style.background = "#f9fafb"; }}
                                    onMouseLeave={e => { if (!isSelected) e.currentTarget.style.background = isSelected ? "#eef2ff" : "transparent"; }}
                                  >
                                    <span style={{ width: 16, height: 16, borderRadius: 4, border: isSelected ? "none" : "1.5px solid #d1d5db", background: isSelected ? "#6366f1" : "#fff", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                                      {isSelected && <FaCheck style={{ color: "#fff", fontSize: 9 }} />}
                                    </span>
                                    {f}
                                  </button>
                                );
                              })}
                            {/* Show "Add custom" option if search doesn't match any preset */}
                            {featureSearchQuery.trim() && !PROPERTY_FEATURES_LIST.some(f => f.toLowerCase() === featureSearchQuery.trim().toLowerCase()) && (
                              <button
                                type="button"
                                onClick={() => {
                                  const trimmed = featureSearchQuery.trim();
                                  if (!selectedPropertyFeatures.includes(trimmed)) {
                                    setSelectedPropertyFeatures(prev => [...prev, trimmed]);
                                  }
                                  setFeatureSearchQuery("");
                                }}
                                style={{
                                  display: "flex", alignItems: "center", gap: 8, width: "100%",
                                  padding: "7px 10px", borderRadius: 6, fontSize: 13, fontWeight: 600,
                                  cursor: "pointer", border: "none", textAlign: "left",
                                  background: "transparent", color: "#6366f1",
                                  borderTop: "1px solid #f3f4f6", marginTop: 2,
                                }}
                                onMouseEnter={e => e.currentTarget.style.background = "#f0f0ff"}
                                onMouseLeave={e => e.currentTarget.style.background = "transparent"}
                              >
                                <FaPlus style={{ fontSize: 10 }} />
                                Add "{featureSearchQuery.trim()}"
                              </button>
                            )}
                            {featureSearchQuery && PROPERTY_FEATURES_LIST.filter(f => f.toLowerCase().includes(featureSearchQuery.toLowerCase())).length === 0 && !featureSearchQuery.trim() && (
                              <div style={{ padding: "12px 10px", fontSize: 13, color: "#9ca3af", textAlign: "center" }}>No matching features</div>
                            )}
                          </div>
                        </div>
                      )}
                    </div>

                    <div style={{ marginTop: 12 }}>
                      <label style={{ fontSize: 12, color: "#6b7280" }}>Property Description</label>
                      <textarea value={features.notes} onChange={e => { setFeatures(f => ({ ...f, notes: e.target.value })); e.target.style.height = "auto"; e.target.style.height = e.target.scrollHeight + "px"; }}
                        ref={el => { if (el && features.notes) { el.style.height = "auto"; el.style.height = el.scrollHeight + "px"; } }}
                        placeholder="Describe the property…"
                        rows={3}
                        style={{ width: "100%", padding: "8px 10px", borderRadius: 6, border: "1px solid #e5e7eb", fontSize: 14, boxSizing: "border-box", resize: "vertical", fontFamily: "inherit", overflow: "hidden" }} />
                    </div>
                  </div>
                  </>)}

                  {modalTab === "improvements" && !sectionToggles.improvements && (
                    <div style={{ textAlign: "center", padding: "60px 20px", color: "#9ca3af" }}>
                      <div style={{ fontSize: 32, marginBottom: 8 }}>🙈</div>
                      <div style={{ fontSize: 14 }}>This section is excluded from the appraisal. Check the box above to include it.</div>
                    </div>
                  )}
                  {modalTab === "improvements" && sectionToggles.improvements && (<>
                  {/* ─── Property Improvements ─── */}
                  {propertyImages.length > 0 && (
                    <div>
                      <label style={{ fontWeight: 600, fontSize: 14, color: "#374151", marginBottom: 2, display: "block" }}>Property Improvements</label>
                      <div style={{ fontSize: 12, color: "#6b7280", lineHeight: 1.4, marginBottom: 12 }}>AI analyses each image to suggest improvements. The first 5 images are analysed automatically. Edit any suggestion below.</div>

                      {/* Auto-analyse first 5 on mount */}
                      {(() => {
                        if (!imageImprovementsAutoRan.current && propertyImages.length > 0) {
                          imageImprovementsAutoRan.current = true;
                          const toAnalyse = propertyImages.slice(0, 5);
                          // Auto-select first 5
                          const autoSel = {};
                          toAnalyse.forEach(url => { autoSel[url] = true; });
                          setSelectedImprovementImages(prev => ({ ...prev, ...autoSel }));
                          toAnalyse.forEach(url => {
                            if (imageImprovements[url]) return;
                            setImageImprovementsLoading(prev => ({ ...prev, [url]: true }));
                            handleTokenAndPostRequest(`${window.api_host}/analyse-image-improvements`, { image_url: url })
                              .then(res => {
                                if (res.data) setImageImprovements(prev => ({ ...prev, [url]: res.data }));
                              })
                              .catch(() => {})
                              .finally(() => setImageImprovementsLoading(prev => { const n = { ...prev }; delete n[url]; return n; }));
                          });
                        }
                        return null;
                      })()}

                      <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr", gap: 16 }}>
                        {propertyImages.map((url, imgIdx) => {
                          const data = imageImprovements[url];
                          const loading = imageImprovementsLoading[url];
                          const isSelected = !!selectedImprovementImages[url];
                          const tiers = [
                            { key: "quick_wins", label: "Quick Wins", color: "#16a34a", bg: "#f0fdf4", border: "#bbf7d0" },
                            { key: "moderate_upgrades", label: "Moderate Upgrades", color: "#d97706", bg: "#fffbeb", border: "#fde68a" },
                            { key: "major_improvements", label: "Major Improvements", color: "#dc2626", bg: "#fef2f2", border: "#fecaca" },
                          ];
                          return (
                            <div key={url} onClick={() => setSelectedImprovementImages(prev => {
                              const n = { ...prev };
                              if (n[url]) delete n[url]; else n[url] = true;
                              return n;
                            })} style={{
                              borderRadius: 14, overflow: "hidden", background: "#fff",
                              boxShadow: isSelected ? "0 0 0 2px #2563eb, 0 8px 25px rgba(37,99,235,.15)" : "0 1px 6px rgba(0,0,0,.08)",
                              transition: "all 0.2s ease", cursor: "pointer", position: "relative",
                            }}>
                              {/* Selection indicator */}
                              {isSelected && (
                                <div style={{ position: "absolute", top: 10, left: 10, zIndex: 2, background: "#2563eb", borderRadius: "50%", width: 26, height: 26, display: "flex", alignItems: "center", justifyContent: "center", boxShadow: "0 2px 8px rgba(37,99,235,.4)" }}>
                                  <FaCheck style={{ color: "#fff", fontSize: 12 }} />
                                </div>
                              )}
                              {/* Image */}
                              <div style={{ position: "relative", height: 180, overflow: "hidden" }}>
                                <img src={url} alt="" style={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }} />
                                <div style={{ position: "absolute", bottom: 0, left: 0, right: 0, height: 50, background: "linear-gradient(transparent, rgba(0,0,0,.3))", pointerEvents: "none" }} />
                                <span style={{ position: "absolute", bottom: 8, left: 10, color: "#fff", fontSize: 12, fontWeight: 600, textShadow: "0 1px 3px rgba(0,0,0,.5)" }}>Image {imgIdx + 1}</span>
                              </div>

                              {/* Content */}
                              <div style={{ padding: "12px 14px 16px" }}>
                                {loading && (
                                  <div style={{ display: "flex", alignItems: "center", gap: 8, padding: "12px 0" }}>
                                    <Spinner animation="border" size="sm" style={{ color: "#6b7280" }} />
                                    <span style={{ fontSize: 13, color: "#6b7280" }}>Analysing image…</span>
                                  </div>
                                )}

                                {!loading && !data && (
                                  <button
                                    onClick={() => {
                                      setImageImprovementsLoading(prev => ({ ...prev, [url]: true }));
                                      handleTokenAndPostRequest(`${window.api_host}/analyse-image-improvements`, { image_url: url })
                                        .then(res => { if (res.data) setImageImprovements(prev => ({ ...prev, [url]: res.data })); })
                                        .catch(() => {})
                                        .finally(() => setImageImprovementsLoading(prev => { const n = { ...prev }; delete n[url]; return n; }));
                                    }}
                                    style={{
                                      display: "flex", alignItems: "center", gap: 6, padding: "7px 14px",
                                      borderRadius: 8, border: "none", fontSize: 13, fontWeight: 600, cursor: "pointer",
                                      background: "linear-gradient(135deg, #1f2937 0%, #374151 100%)", color: "#fff",
                                      boxShadow: "0 1px 3px rgba(0,0,0,.12)",
                                    }}
                                  >
                                    <FaMagic /> Analyse with AI
                                  </button>
                                )}

                                {!loading && data && (
                                  <>
                                    {/* Tier sections */}
                                    {tiers.map(tier => {
                                      const items = data[tier.key];
                                      if (!items || !items.length) return null;
                                      return (
                                        <div key={tier.key} style={{ marginBottom: 12 }}>
                                          <div style={{ fontSize: 12, fontWeight: 700, color: tier.color, textTransform: "uppercase", letterSpacing: "0.5px", marginBottom: 6 }}>{tier.label}</div>
                                          {items.map((item, i) => (
                                            <div key={i} style={{ background: tier.bg, border: `1px solid ${tier.border}`, borderRadius: 8, padding: "8px 10px", marginBottom: 6 }}>
                                              <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 4 }}>
                                                <input
                                                  value={item.action || ""}
                                                  onClick={e => e.stopPropagation()}
                                                  onChange={e => {
                                                    const val = e.target.value;
                                                    setImageImprovements(prev => {
                                                      const updated = { ...prev[url] };
                                                      updated[tier.key] = [...updated[tier.key]];
                                                      updated[tier.key][i] = { ...updated[tier.key][i], action: val };
                                                      return { ...prev, [url]: updated };
                                                    });
                                                  }}
                                                  style={{ flex: 1, fontSize: 13, fontWeight: 600, color: "#1f2937", border: "none", background: "transparent", outline: "none", padding: 0 }}
                                                />
                                                {item.worth_it && <span style={{ fontSize: 10, fontWeight: 700, color: "#16a34a", background: "#dcfce7", borderRadius: 4, padding: "1px 6px", flexShrink: 0 }}>Worth it</span>}
                                                <button
                                                  onClick={() => setImageImprovements(prev => {
                                                    const updated = { ...prev[url] };
                                                    updated[tier.key] = updated[tier.key].filter((_, j) => j !== i);
                                                    return { ...prev, [url]: updated };
                                                  })}
                                                  style={{ background: "none", border: "none", cursor: "pointer", color: "#9ca3af", fontSize: 11, padding: 2, flexShrink: 0 }}
                                                ><FaTimes /></button>
                                              </div>
                                              <div style={{ display: "flex", gap: 8, flexWrap: "wrap", fontSize: 11, color: "#6b7280" }}>
                                                <span>Cost: <strong style={{ color: "#374151" }}>{item.estimated_cost || "—"}</strong></span>
                                                <span>Effort: <strong style={{ color: "#374151" }}>{item.effort || "—"}</strong></span>
                                              </div>
                                              <input
                                                value={item.value_impact || ""}
                                                onChange={e => {
                                                  const val = e.target.value;
                                                  setImageImprovements(prev => {
                                                    const updated = { ...prev[url] };
                                                    updated[tier.key] = [...updated[tier.key]];
                                                    updated[tier.key][i] = { ...updated[tier.key][i], value_impact: val };
                                                    return { ...prev, [url]: updated };
                                                  });
                                                }}
                                                style={{ width: "100%", fontSize: 12, color: "#4b5563", border: "none", background: "transparent", outline: "none", padding: 0, marginTop: 4 }}
                                                placeholder="Value impact…"
                                              />
                                            </div>
                                          ))}
                                        </div>
                                      );
                                    })}

                                    {/* Overall assessment */}
                                    {data.overall_assessment && (
                                      <div style={{ marginBottom: 8 }}>
                                        <div style={{ fontSize: 11, fontWeight: 700, color: "#374151", textTransform: "uppercase", letterSpacing: "0.5px", marginBottom: 4 }}>Overall Assessment</div>
                                        <textarea
                                          value={data.overall_assessment}
                                          onChange={e => setImageImprovements(prev => ({ ...prev, [url]: { ...prev[url], overall_assessment: e.target.value } }))}
                                          ref={el => { if (el) { el.style.height = "auto"; el.style.height = el.scrollHeight + "px"; } }}
                                          style={{ width: "100%", fontSize: 12, color: "#4b5563", lineHeight: 1.5, border: "1px solid #e5e7eb", borderRadius: 6, padding: "6px 8px", background: "#f9fafb", resize: "none", overflow: "hidden", outline: "none", fontFamily: "inherit", boxSizing: "border-box" }}
                                        />
                                      </div>
                                    )}

                                    {/* Investment & Uplift */}
                                    <div style={{ display: "flex", gap: 12, flexWrap: "wrap", fontSize: 12 }}>
                                      {data.estimated_total_investment && (
                                        <span style={{ color: "#6b7280" }}>Investment: <strong style={{ color: "#1f2937" }}>{data.estimated_total_investment}</strong></span>
                                      )}
                                      {data.estimated_value_uplift && (
                                        <span style={{ color: "#6b7280" }}>Est. Uplift: <strong style={{ color: "#16a34a" }}>{data.estimated_value_uplift}</strong></span>
                                      )}
                                    </div>

                                    {/* Re-analyse button */}
                                    <button
                                      onClick={() => {
                                        setImageImprovementsLoading(prev => ({ ...prev, [url]: true }));
                                        setImageImprovements(prev => { const n = { ...prev }; delete n[url]; return n; });
                                        handleTokenAndPostRequest(`${window.api_host}/analyse-image-improvements`, { image_url: url })
                                          .then(res => { if (res.data) setImageImprovements(prev => ({ ...prev, [url]: res.data })); })
                                          .catch(() => {})
                                          .finally(() => setImageImprovementsLoading(prev => { const n = { ...prev }; delete n[url]; return n; }));
                                      }}
                                      style={{ marginTop: 8, display: "flex", alignItems: "center", gap: 5, padding: "5px 12px", borderRadius: 6, border: "1px solid #d1d5db", background: "#fff", color: "#374151", fontSize: 12, fontWeight: 500, cursor: "pointer" }}
                                    >
                                      <FaMagic style={{ fontSize: 10 }} /> Re-analyse
                                    </button>
                                  </>
                                )}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}
                  </>)}

                  {modalTab === "valuation" && !sectionToggles.valuation && (
                    <div style={{ textAlign: "center", padding: "60px 20px", color: "#9ca3af" }}>
                      <div style={{ fontSize: 32, marginBottom: 8 }}>🙈</div>
                      <div style={{ fontSize: 14 }}>This section is excluded from the appraisal. Check the box above to include it.</div>
                    </div>
                  )}
                  {modalTab === "valuation" && sectionToggles.valuation && (<>
                  {/* ─── Price range (Sale) ─── */}
                  {includeSale && (
                    <div>
                      <label style={{ fontWeight: 600, fontSize: 14, color: "#374151", marginBottom: 2, display: "block" }}>Sale Price Range</label>
                      <div style={{ fontSize: 12, color: "#6b7280", lineHeight: 1.4, marginBottom: 8 }}>Set your estimated sale price range and expected midpoint.</div>
                      <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr 1fr", gap: 12 }}>
                        <div>
                          <label style={{ fontSize: 12, color: "#6b7280" }}>Low ($)</label>
                          <input type="number" value={priceLow} onChange={e => setPriceLow(e.target.value)} placeholder="e.g. 900000"
                            style={{ width: "100%", padding: "8px 10px", borderRadius: 6, border: "1px solid #e5e7eb", fontSize: 14, boxSizing: "border-box" }} />
                        </div>
                        <div>
                          <label style={{ fontSize: 12, color: "#6b7280" }}>Mid ($)</label>
                          <input type="number" value={priceMid} onChange={e => setPriceMid(e.target.value)} placeholder="e.g. 950000"
                            style={{ width: "100%", padding: "8px 10px", borderRadius: 6, border: "1px solid #e5e7eb", fontSize: 14, boxSizing: "border-box" }} />
                        </div>
                        <div>
                          <label style={{ fontSize: 12, color: "#6b7280" }}>High ($)</label>
                          <input type="number" value={priceHigh} onChange={e => setPriceHigh(e.target.value)} placeholder="e.g. 1050000"
                            style={{ width: "100%", padding: "8px 10px", borderRadius: 6, border: "1px solid #e5e7eb", fontSize: 14, boxSizing: "border-box" }} />
                        </div>
                      </div>
                    </div>
                  )}
                  </>)}

                  {modalTab === "details" && sectionToggles.details && (<>
                  {/* ─── Rent range ─── */}
                  {includeRental && (
                    <div>
                      <label style={{ fontWeight: 600, fontSize: 14, color: "#374151", marginBottom: 2, display: "block" }}>Rental Price Range (per week)</label>
                      <div style={{ fontSize: 12, color: "#6b7280", lineHeight: 1.4, marginBottom: 8 }}>Set your estimated weekly rental range and expected midpoint.</div>
                      <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr 1fr", gap: 12 }}>
                        <div>
                          <label style={{ fontSize: 12, color: "#6b7280" }}>Low ($/wk)</label>
                          <input type="number" value={rentLow} onChange={e => setRentLow(e.target.value)} placeholder="e.g. 600"
                            style={{ width: "100%", padding: "8px 10px", borderRadius: 6, border: "1px solid #e5e7eb", fontSize: 14, boxSizing: "border-box" }} />
                        </div>
                        <div>
                          <label style={{ fontSize: 12, color: "#6b7280" }}>Mid ($/wk)</label>
                          <input type="number" value={rentMid} onChange={e => setRentMid(e.target.value)} placeholder="e.g. 650"
                            style={{ width: "100%", padding: "8px 10px", borderRadius: 6, border: "1px solid #e5e7eb", fontSize: 14, boxSizing: "border-box" }} />
                        </div>
                        <div>
                          <label style={{ fontSize: 12, color: "#6b7280" }}>High ($/wk)</label>
                          <input type="number" value={rentHigh} onChange={e => setRentHigh(e.target.value)} placeholder="e.g. 700"
                            style={{ width: "100%", padding: "8px 10px", borderRadius: 6, border: "1px solid #e5e7eb", fontSize: 14, boxSizing: "border-box" }} />
                        </div>
                      </div>
                    </div>
                  )}
                  </>)}

                  {modalTab === "comparables" && !sectionToggles.comparables && (
                    <div style={{ textAlign: "center", padding: "60px 20px", color: "#9ca3af" }}>
                      <div style={{ fontSize: 32, marginBottom: 8 }}>🙈</div>
                      <div style={{ fontSize: 14 }}>This section is excluded from the appraisal. Check the box above to include it.</div>
                    </div>
                  )}
                  {modalTab === "comparables" && sectionToggles.comparables && (<>
                  {/* ─── Comparable Search Filters ─── */}
                  <div>
                    <label style={{ fontWeight: 600, fontSize: 14, color: "#374151", marginBottom: 2, display: "block" }}>Comparable Search</label>
                    <div style={{ fontSize: 12, color: "#6b7280", lineHeight: 1.4, marginBottom: 10 }}>
                      Adjust filters to find the most relevant comparables. The suburb is auto-filled from the property address.
                    </div>

                    {/* Suburb search */}
                    <div style={{ marginBottom: 12, position: "relative" }}>
                      <label style={{ fontSize: 12, color: "#6b7280", marginBottom: 2, display: "block" }}>Suburbs</label>
                      {/* Selected suburb chips */}
                      {filterSuburbs.length > 0 && (
                        <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginBottom: 6 }}>
                          {filterSuburbs.map((s, idx) => (
                            <span key={s.value} style={{
                              display: "inline-flex", alignItems: "center", gap: 4,
                              background: "#e0e7ff", color: "#3730a3", borderRadius: 16,
                              padding: "3px 10px", fontSize: 13, fontWeight: 600,
                            }}>
                              {s.label}
                              <button
                                onClick={() => handleRemoveFilterSuburb(idx)}
                                style={{
                                  background: "none", border: "none", cursor: "pointer",
                                  color: "#6366f1", fontSize: 15, padding: 0, lineHeight: 1,
                                  fontWeight: 700, marginLeft: 2,
                                }}
                              >×</button>
                            </span>
                          ))}
                        </div>
                      )}
                      <div style={{ position: "relative" }}>
                        <input
                          ref={suburbInputRef}
                          value={suburbQuery}
                          autoComplete="off"
                          onChange={e => { setSuburbQuery(e.target.value); searchFilterSuburbs(e.target.value); }}
                          onFocus={() => { if (suburbResults.length > 0) setSuburbDropdownOpen(true); }}
                          onBlur={() => setTimeout(() => setSuburbDropdownOpen(false), 200)}
                          placeholder={filterSuburbs.length > 0 ? "Add another suburb…" : "Type a suburb…"}
                          style={{
                            width: "100%", padding: "8px 10px", borderRadius: 6,
                            border: "1px solid #e5e7eb", fontSize: 14, boxSizing: "border-box",
                          }}
                        />
                        {suburbSearching && (
                          <div style={{ position: "absolute", right: 10, top: "50%", transform: "translateY(-50%)" }}>
                            <Spinner animation="border" size="sm" style={{ width: 14, height: 14 }} />
                          </div>
                        )}
                      </div>
                      {suburbDropdownOpen && suburbResults.length > 0 && (
                        <div style={{
                          position: "absolute", left: 0, right: 0, top: "100%", zIndex: 10,
                          background: "#fff", border: "1px solid #e5e7eb", borderRadius: 8,
                          boxShadow: "0 8px 24px #0002", maxHeight: 200, overflowY: "auto", marginTop: 4,
                        }}>
                          {suburbResults.map((item, idx) => (
                            <div
                              key={`${item.type}-${item.value}-${idx}`}
                              onMouseDown={e => { e.preventDefault(); handleSelectFilterSuburb(item); }}
                              style={{
                                padding: "8px 12px", cursor: "pointer", fontSize: 14,
                                borderBottom: idx < suburbResults.length - 1 ? "1px solid #f3f4f6" : "none",
                              }}
                              onMouseOver={e => e.currentTarget.style.background = "#f0f7ff"}
                              onMouseOut={e => e.currentTarget.style.background = "#fff"}
                            >
                              {item.label || item.value}
                            </div>
                          ))}
                        </div>
                      )}
                    </div>

                    <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr 1fr" : "1fr 1fr 1fr", gap: 12, marginBottom: 12 }}>
                      {/* Days */}
                      <div>
                        <label style={{ fontSize: 12, color: "#6b7280", marginBottom: 4, display: "block" }}>Time Period</label>
                        <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
                          {[
                            { value: "90", label: "3 months" },
                            { value: "180", label: "6 months" },
                            { value: "365", label: "12 months" },
                            { value: "730", label: "2 years" },
                          ].map(opt => {
                            const active = filterDays === opt.value;
                            return (
                              <button
                                key={opt.value}
                                type="button"
                                onClick={() => setFilterDays(opt.value)}
                                style={{
                                  padding: "5px 12px", borderRadius: 99, fontSize: 13, fontWeight: 500,
                                  border: active ? "1.5px solid #2563eb" : "1px solid #e5e7eb",
                                  background: active ? "#eff6ff" : "#fff",
                                  color: active ? "#2563eb" : "#374151",
                                  cursor: "pointer", transition: "all 0.15s",
                                }}
                              >{opt.label}</button>
                            );
                          })}
                        </div>
                      </div>

                      {/* Property types (multi-select chips) */}
                      <div>
                        <label style={{ fontSize: 12, color: "#6b7280", marginBottom: 4, display: "block" }}>Property Type</label>
                        <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
                          {PROPERTY_TYPES.map(t => {
                            const active = filterPropertyTypes.includes(t);
                            return (
                              <button
                                key={t}
                                type="button"
                                onClick={() => setFilterPropertyTypes(prev =>
                                  active ? prev.filter(v => v !== t) : [...prev, t]
                                )}
                                style={{
                                  padding: "5px 12px", borderRadius: 16, fontSize: 13, fontWeight: 600,
                                  border: active ? "1.5px solid #6366f1" : "1px solid #d1d5db",
                                  background: active ? "#e0e7ff" : "#fff",
                                  color: active ? "#3730a3" : "#374151",
                                  cursor: "pointer", transition: "all .15s",
                                }}
                              >{t}</button>
                            );
                          })}
                        </div>
                        {filterPropertyTypes.length > 0 && (
                          <button
                            type="button"
                            onClick={() => setFilterPropertyTypes([])}
                            style={{ fontSize: 11, color: "#6366f1", background: "none", border: "none", cursor: "pointer", marginTop: 4, padding: 0 }}
                          >Clear all</button>
                        )}
                      </div>

                      {/* Bedrooms (multi-select chips) */}
                      <div>
                        <label style={{ fontSize: 12, color: "#6b7280", marginBottom: 4, display: "block" }}>Bedrooms</label>
                        <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
                          {["1","2","3","4","5","6+"].map(b => {
                            const active = filterBedrooms.includes(b);
                            return (
                              <button
                                key={b}
                                type="button"
                                onClick={() => setFilterBedrooms(prev =>
                                  active ? prev.filter(v => v !== b) : [...prev, b]
                                )}
                                style={{
                                  padding: "5px 14px", borderRadius: 16, fontSize: 13, fontWeight: 600,
                                  border: active ? "1.5px solid #6366f1" : "1px solid #d1d5db",
                                  background: active ? "#e0e7ff" : "#fff",
                                  color: active ? "#3730a3" : "#374151",
                                  cursor: "pointer", transition: "all .15s",
                                  minWidth: 36, textAlign: "center",
                                }}
                              >{b}</button>
                            );
                          })}
                        </div>
                        {filterBedrooms.length > 0 && (
                          <button
                            type="button"
                            onClick={() => setFilterBedrooms([])}
                            style={{ fontSize: 11, color: "#6366f1", background: "none", border: "none", cursor: "pointer", marginTop: 4, padding: 0 }}
                          >Clear all</button>
                        )}
                      </div>
                    </div>

                    {/* On/Off market checkboxes */}
                    <div style={{ display: "flex", gap: 20, marginBottom: 12 }}>
                      <label style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 14, cursor: "pointer", color: "#374151" }}>
                        <input type="checkbox" checked={filterOnMarket} onChange={e => setFilterOnMarket(e.target.checked)} style={{ width: 16, height: 16 }} />
                        On Market
                      </label>
                      <label style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 14, cursor: "pointer", color: "#374151" }}>
                        <input type="checkbox" checked={filterOffMarket} onChange={e => setFilterOffMarket(e.target.checked)} style={{ width: 16, height: 16 }} />
                        Off Market
                      </label>
                    </div>

                    {/* Search button */}
                    <button
                      onClick={() => fetchFilteredComparables(undefined, Object.keys(selectedComparables), Object.keys(selectedRentalComparables))}
                      disabled={filterSuburbs.length === 0 || comparablesLoading || rentalComparablesLoading}
                      style={{
                        background: (filterSuburbs.length === 0 || comparablesLoading) ? "#93c5fd" : "#2563eb",
                        color: "#fff", border: "none", borderRadius: 6,
                        padding: "8px 20px", fontWeight: 600, fontSize: 14,
                        cursor: (filterSuburbs.length === 0 || comparablesLoading) ? "not-allowed" : "pointer",
                        display: "flex", alignItems: "center", gap: 8,
                      }}
                    >
                      {(comparablesLoading || rentalComparablesLoading) && <Spinner animation="border" size="sm" style={{ width: 14, height: 14 }} />}
                      <FaSearch style={{ fontSize: 13 }} /> Search Comparables
                    </button>
                  </div>

                  {/* ─── Comparable Sales ─── */}
                  <div>
                    <div style={{ display: "flex", flexDirection: "column", gap: 8, marginBottom: 2 }}>
                      <label style={{ fontWeight: 600, fontSize: 14, color: "#374151", display: "block" }}>
                        Comparable Sales
                        {comparables.length > 0 && (
                          <span style={{ fontWeight: 500, color: "#6b7280", marginLeft: 8, fontSize: 13 }}>
                            {comparables.length} results
                          </span>
                        )}
                        {Object.keys(selectedComparables).length > 0 && (
                          <span style={{ fontWeight: 500, color: "#2563eb", marginLeft: 6, fontSize: 13 }}>
                            · {Object.keys(selectedComparables).length} selected
                          </span>
                        )}
                        {(() => {
                          const outsideCount = comparables.filter(c => c._outsideSearch && selectedComparables[c.id]).length;
                          return outsideCount > 0 ? (
                            <span style={{ fontWeight: 500, color: "#f59e0b", marginLeft: 6, fontSize: 13 }}>
                              · {outsideCount} outside criteria
                            </span>
                          ) : null;
                        })()}
                      </label>
                      {Object.keys(selectedComparables).length > 0 && (
                        <div style={{ display: "flex", flexDirection: "column", gap: 8, width: "100%" }}>
                          <textarea
                            value={analysisPrompt}
                            onChange={e => {
                              setAnalysisPrompt(e.target.value);
                              e.target.style.height = "auto";
                              e.target.style.height = e.target.scrollHeight + "px";
                            }}
                            placeholder="Optional: Add instructions for the AI analysis…"
                            rows={2}
                            style={{
                              width: "100%", boxSizing: "border-box", padding: "7px 12px", borderRadius: 8,
                              border: "1px solid #d1d5db", fontSize: 13, outline: "none",
                              transition: "border-color .15s", resize: "none", overflow: "hidden",
                              lineHeight: 1.5, fontFamily: "inherit",
                            }}
                            onFocus={e => e.target.style.borderColor = "#6366f1"}
                            onBlur={e => e.target.style.borderColor = "#d1d5db"}
                          />
                          <button
                            onClick={handleRunAnalysis}
                            disabled={Object.keys(analysisLoading).length > 0}
                            style={{
                              background: Object.keys(analysisLoading).length > 0 ? "#9ca3af" : "linear-gradient(135deg, #1f2937 0%, #374151 100%)", color: "#fff",
                              border: "none", borderRadius: 8, padding: "7px 16px",
                              fontSize: 13, fontWeight: 600, cursor: Object.keys(analysisLoading).length > 0 ? "not-allowed" : "pointer",
                              display: "flex", alignItems: "center", justifyContent: "center", gap: 6, whiteSpace: "nowrap",
                              boxShadow: "0 1px 3px rgba(0,0,0,.12)", transition: "all .2s",
                              width: "100%",
                            }}
                          >
                            {Object.keys(analysisLoading).length > 0 ? <Spinner animation="border" size="sm" /> : <FaMagic />}
                            {Object.keys(analysisLoading).length > 0 ? "Analysing…" : "Run AI Analysis"}
                          </button>
                        </div>
                      )}
                    </div>
                    <div style={{ fontSize: 12, color: "#6b7280", lineHeight: 1.4, marginBottom: 8 }}>
                      Recent sold listings nearby. Tap a card to select it as a comparable — selected properties will be highlighted with a blue border and checkmark.
                    </div>

                    {comparablesLoading ? (
                      <div style={{ display: "flex", justifyContent: "center", padding: 24 }}>
                        <Spinner animation="border" />
                      </div>
                    ) : comparables.length === 0 ? (
                      <div style={{ color: "#9ca3af", fontSize: 14, padding: 12 }}>
                        No comparables found. Enter an address in the Details tab and click Search Comparables.
                      </div>
                    ) : (
                      <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr", gap: 16 }}>
                        {[...comparables].sort((a, b) => {
                          const aS = selectedComparables[a.id] ? 1 : 0;
                          const bS = selectedComparables[b.id] ? 1 : 0;
                          return bS - aS;
                        }).map(comp => {
                          const isSelected = !!selectedComparables[comp.id];
                          return (
                            <div key={comp.id} style={{
                              borderRadius: 14, overflow: "hidden",
                              background: "#fff",
                              boxShadow: isSelected ? "0 0 0 2px #2563eb, 0 8px 25px rgba(37,99,235,.15)" : "0 1px 6px rgba(0,0,0,.08)",
                              transition: "all 0.2s ease",
                              cursor: "pointer",
                              position: "relative",
                            }} onClick={() => toggleComparable(comp.id)}>
                              {/* Selection indicator */}
                              {isSelected && (
                                <div style={{ position: "absolute", top: 10, left: 10, zIndex: 2, background: "#2563eb", borderRadius: "50%", width: 26, height: 26, display: "flex", alignItems: "center", justifyContent: "center", boxShadow: "0 2px 8px rgba(37,99,235,.4)" }}>
                                  <FaCheck style={{ color: "#fff", fontSize: 12 }} />
                                </div>
                              )}
                              {/* Outside search badge */}
                              {comp._outsideSearch && (
                                <div style={{ position: "absolute", top: 10, left: isSelected ? 44 : 10, zIndex: 2, background: "rgba(245,158,11,.9)", color: "#fff", fontSize: 10, fontWeight: 700, borderRadius: 6, padding: "3px 8px", backdropFilter: "blur(4px)", letterSpacing: "0.3px" }}>
                                  Outside search criteria
                                </div>
                              )}
                              {/* Image carousel */}
                              {(() => {
                                const mediaImgs = parseMediaUrls(comp.media);
                                const allImgs = (mediaImgs.length > 0 ? mediaImgs : comp.photo_urls?.length ? comp.photo_urls : comp.photo_url_main ? [comp.photo_url_main] : []).filter(u => !failedImages.has(u));
                                if (allImgs.length === 0) return <div style={{ height: 200, background: "linear-gradient(135deg, #e0e7ff 0%, #dbeafe 100%)", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: 6 }}><FaHome style={{ fontSize: 40, color: "#93c5fd" }} /><span style={{ fontSize: 11, color: "#93c5fd", fontWeight: 500 }}>No image</span></div>;
                                const loadedCount = compLoadedImages[comp.id] || 2;
                                const imgs = allImgs.slice(0, loadedCount);
                                const idx = compCarouselIndexes[comp.id] || 0;
                                const safeIdx = Math.min(idx, imgs.length - 1);
                                const hasMore = loadedCount < allImgs.length;
                                const handleNext = (e) => {
                                  e.stopPropagation();
                                  if (safeIdx === imgs.length - 1 && hasMore) {
                                    setCompLoadedImages(prev => ({ ...prev, [comp.id]: Math.min(loadedCount + 2, allImgs.length) }));
                                    setCompCarouselIndexes(prev => ({ ...prev, [comp.id]: safeIdx + 1 }));
                                  } else {
                                    setCompCarouselIndexes(prev => ({ ...prev, [comp.id]: (safeIdx + 1) % imgs.length }));
                                  }
                                };
                                const handlePrev = (e) => {
                                  e.stopPropagation();
                                  setCompCarouselIndexes(prev => ({ ...prev, [comp.id]: (safeIdx - 1 + imgs.length) % imgs.length }));
                                };
                                return (
                                  <div style={{ position: "relative", width: "100%", height: 200, overflow: "hidden" }}>
                                    <img src={imgs[safeIdx]} alt="" onError={() => setFailedImages(prev => new Set(prev).add(imgs[safeIdx]))} style={{ width: "100%", height: "100%", objectFit: "cover", display: "block", transition: "opacity .2s" }} />
                                    {/* Bottom gradient overlay */}
                                    <div style={{ position: "absolute", bottom: 0, left: 0, right: 0, height: 60, background: "linear-gradient(transparent, rgba(0,0,0,.35))", pointerEvents: "none" }} />
                                    {(imgs.length > 1 || hasMore) && (
                                      <>
                                        <button onClick={handlePrev}
                                          style={{ position: "absolute", left: 8, top: "50%", transform: "translateY(-50%)", background: "rgba(255,255,255,.85)", color: "#1f2937", border: "none", borderRadius: "50%", width: 32, height: 32, display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer", fontSize: 12, padding: 0, boxShadow: "0 2px 6px rgba(0,0,0,.15)", backdropFilter: "blur(4px)" }}
                                        ><FaChevronLeft /></button>
                                        <button onClick={handleNext}
                                          style={{ position: "absolute", right: 8, top: "50%", transform: "translateY(-50%)", background: "rgba(255,255,255,.85)", color: "#1f2937", border: "none", borderRadius: "50%", width: 32, height: 32, display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer", fontSize: 12, padding: 0, boxShadow: "0 2px 6px rgba(0,0,0,.15)", backdropFilter: "blur(4px)" }}
                                        ><FaChevronRight /></button>
                                      </>
                                    )}
                                    <span style={{ position: "absolute", bottom: 8, right: 8, background: "rgba(0,0,0,.55)", color: "#fff", fontSize: 11, fontWeight: 600, borderRadius: 8, padding: "3px 8px", backdropFilter: "blur(4px)" }}>
                                      {safeIdx + 1} / {allImgs.length}
                                    </span>
                                    {/* Property type badge */}
                                    {comp.property_type_display && (
                                      <span style={{ position: "absolute", top: 10, right: 10, background: "rgba(255,255,255,.9)", color: "#374151", fontSize: 11, fontWeight: 600, borderRadius: 6, padding: "3px 8px", backdropFilter: "blur(4px)" }}>
                                        {comp.property_type_display}
                                      </span>
                                    )}
                                  </div>
                                );
                              })()}
                              {/* Card body */}
                              <div style={{ padding: "14px 16px 16px" }}>
                                {/* Price */}
                                <div style={{ fontSize: 20, fontWeight: 800, color: "#1e3a5f", marginBottom: 4, letterSpacing: "-0.3px" }}>
                                  {formatCurrency(comp.price)}
                                </div>
                                {/* Address */}
                                <div style={{ fontSize: 14, color: "#4b5563", marginBottom: 10, display: "flex", alignItems: "flex-start", gap: 5, lineHeight: 1.3 }}>
                                  <FaMapMarkerAlt style={{ color: "#9ca3af", fontSize: 12, marginTop: 2, flexShrink: 0 }} />
                                  <span>{comp.display_address}</span>
                                </div>
                                {/* Features row */}
                                <div style={{ display: "flex", gap: 16, marginBottom: 10 }}>
                                  {comp.bedrooms != null && (
                                    <div style={{ display: "flex", alignItems: "center", gap: 5, fontSize: 13, color: "#6b7280" }}>
                                      <FaBed style={{ fontSize: 14, color: "#9ca3af" }} />
                                      <span style={{ fontWeight: 600, color: "#374151" }}>{comp.bedrooms}</span>
                                    </div>
                                  )}
                                  {comp.bathrooms != null && (
                                    <div style={{ display: "flex", alignItems: "center", gap: 5, fontSize: 13, color: "#6b7280" }}>
                                      <FaBath style={{ fontSize: 13, color: "#9ca3af" }} />
                                      <span style={{ fontWeight: 600, color: "#374151" }}>{comp.bathrooms}</span>
                                    </div>
                                  )}
                                  {comp.carspaces != null && (
                                    <div style={{ display: "flex", alignItems: "center", gap: 5, fontSize: 13, color: "#6b7280" }}>
                                      <FaCar style={{ fontSize: 13, color: "#9ca3af" }} />
                                      <span style={{ fontWeight: 600, color: "#374151" }}>{comp.carspaces}</span>
                                    </div>
                                  )}
                                  {comp.land_area != null && comp.land_area > 0 && (
                                    <div style={{ display: "flex", alignItems: "center", gap: 5, fontSize: 13, color: "#6b7280" }}>
                                      <FaRulerCombined style={{ fontSize: 13, color: "#9ca3af" }} />
                                      <span style={{ fontWeight: 600, color: "#374151" }}>{comp.land_area}</span>
                                      <span style={{ fontSize: 11, color: "#9ca3af" }}>m²</span>
                                    </div>
                                  )}
                                </div>
                                {/* Sold date pill */}
                                <div style={{ display: "flex", alignItems: "center", gap: 5, fontSize: 12, color: "#6b7280" }}>
                                  <FaCalendarAlt style={{ fontSize: 11, color: "#9ca3af" }} />
                                  <span>Sold {comp.filter_date ? new Date(comp.filter_date).toLocaleDateString() : ""}</span>
                                </div>
                                {/* Features */}
                                {(() => {
                                  let feats = [];
                                  try { feats = typeof comp.features === "string" ? JSON.parse(comp.features) : Array.isArray(comp.features) ? comp.features : []; } catch {}
                                  if (!feats.length) return null;
                                  return (
                                    <div style={{ display: "flex", flexWrap: "wrap", gap: 5, marginTop: 10 }}>
                                      {feats.map((f, i) => (
                                        <span key={i} style={{
                                          fontSize: 11, fontWeight: 500, color: "#4b5563",
                                          background: "#f3f4f6", borderRadius: 5, padding: "2px 8px",
                                          whiteSpace: "nowrap",
                                        }}>
                                          {f.replace(/([A-Z])/g, " $1").trim()}
                                        </span>
                                      ))}
                                    </div>
                                  );
                                })()}
                              </div>
                              {/* Analysis result / loading */}
                              {isSelected && analysisLoading[comp.id] && (
                                <div style={{ padding: "14px 16px", borderTop: "1px solid #e5e7eb", background: "#f9fafb", display: "flex", alignItems: "center", gap: 10 }}>
                                  <Spinner animation="border" size="sm" style={{ color: "#6b7280" }} />
                                  <span style={{ fontSize: 13, color: "#6b7280", fontWeight: 500 }}>Analysing comparable…</span>
                                </div>
                              )}
                              {isSelected && !analysisLoading[comp.id] && analysisResults[comp.id] && (() => {
                                const a = analysisResults[comp.id];
                                const diffLabels = { bedrooms: "Beds", bathrooms: "Baths", parking: "Parking", land_size: "Land" };
                                return (
                                  <div style={{ padding: "14px 16px 16px", borderTop: "1px solid #e5e7eb", background: "#f9fafb" }}>
                                    {/* Score row */}
                                    <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap", marginBottom: 10 }}>
                                      <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                                        <span style={{ fontSize: 11, fontWeight: 700, color: "#374151", textTransform: "uppercase", letterSpacing: "0.6px" }}>Score</span>
                                        <div style={{ display: "flex", alignItems: "center", gap: 0, background: "#fff", borderRadius: 8, border: "1px solid #d1d5db", overflow: "hidden" }}>
                                          <button
                                            onClick={e => { e.stopPropagation(); setAnalysisResults(prev => ({ ...prev, [comp.id]: { ...prev[comp.id], score: Math.max(0, (prev[comp.id]?.score || 0) - 1) } })); }}
                                            style={{ background: "transparent", border: "none", borderRight: "1px solid #e5e7eb", width: 28, height: 28, cursor: "pointer", fontSize: 15, fontWeight: 600, color: "#6b7280", display: "flex", alignItems: "center", justifyContent: "center" }}
                                          >−</button>
                                          <span style={{
                                            display: "inline-flex", alignItems: "center", justifyContent: "center",
                                            width: 34, height: 28,
                                            background: a.score >= 4 ? "#16a34a" : a.score >= 3 ? "#f59e0b" : "#ef4444",
                                            color: "#fff", fontWeight: 800, fontSize: 14,
                                          }}>{a.score}</span>
                                          <button
                                            onClick={e => { e.stopPropagation(); setAnalysisResults(prev => ({ ...prev, [comp.id]: { ...prev[comp.id], score: Math.min(5, (prev[comp.id]?.score || 0) + 1) } })); }}
                                            style={{ background: "transparent", border: "none", borderLeft: "1px solid #e5e7eb", width: 28, height: 28, cursor: "pointer", fontSize: 15, fontWeight: 600, color: "#6b7280", display: "flex", alignItems: "center", justifyContent: "center" }}
                                          >+</button>
                                        </div>
                                        <span style={{ fontSize: 11, color: "#9ca3af", fontWeight: 500 }}>/5</span>
                                      </div>
                                      {/* Diff pills */}
                                      {a.diff && Object.entries(a.diff).map(([key, val]) => {
                                        const label = diffLabels[key] || key;
                                        const isPos = val > 0;
                                        const isMatch = val === 0;
                                        return (
                                          <span key={key} style={{
                                            display: "inline-flex", alignItems: "center", gap: 3,
                                            fontSize: 11, fontWeight: 600, padding: "3px 10px", borderRadius: 6,
                                            background: isMatch ? "#f0fdf4" : isPos ? "#eff6ff" : "#fef2f2",
                                            color: isMatch ? "#15803d" : isPos ? "#1d4ed8" : "#b91c1c",
                                            border: `1px solid ${isMatch ? "#bbf7d0" : isPos ? "#bfdbfe" : "#fecaca"}`,
                                          }}>
                                            {isMatch ? "✓ " : ""}{label}: {isMatch ? "Match" : `${isPos ? "+" : ""}${val}${key === "land_size" ? "sqm" : ""}`}
                                          </span>
                                        );
                                      })}
                                    </div>
                                    <textarea
                                      ref={el => {
                                        if (el) {
                                          el.style.height = "auto";
                                          el.style.height = el.scrollHeight + "px";
                                        }
                                      }}
                                      value={a.summary || ""}
                                      onClick={e => e.stopPropagation()}
                                      onChange={e => {
                                        const ta = e.target;
                                        ta.style.height = "auto";
                                        ta.style.height = ta.scrollHeight + "px";
                                        setAnalysisResults(prev => ({
                                          ...prev,
                                          [comp.id]: { ...prev[comp.id], summary: ta.value },
                                        }));
                                      }}
                                      style={{
                                        width: "100%", fontSize: 13, color: "#374151", lineHeight: 1.55,
                                        fontFamily: "inherit", background: "#fff",
                                        border: "1px solid #d1d5db", borderRadius: 8,
                                        padding: "8px 12px", resize: "none", overflow: "hidden",
                                        boxSizing: "border-box", outline: "none",
                                        transition: "border-color .15s",
                                        minHeight: 40,
                                      }}
                                      onFocus={e => e.target.style.borderColor = "#3b82f6"}
                                      onBlur={e => e.target.style.borderColor = "#d1d5db"}
                                      placeholder="AI analysis — click to edit…"
                                    />
                                  </div>
                                );
                              })()}
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>

                  {/* ─── Comparable Rentals ─── */}
                  {includeRental && <div>
                    <label style={{ fontWeight: 600, fontSize: 14, color: "#374151", marginBottom: 2, display: "block" }}>
                      Comparable Rentals
                      {rentalComparables.length > 0 && (
                        <span style={{ fontWeight: 500, color: "#6b7280", marginLeft: 8, fontSize: 13 }}>
                          {rentalComparables.length} results
                        </span>
                      )}
                      {Object.keys(selectedRentalComparables).length > 0 && (
                        <span style={{ fontWeight: 500, color: "#16a34a", marginLeft: 6, fontSize: 13 }}>
                          · {Object.keys(selectedRentalComparables).length} selected
                        </span>
                      )}
                      {(() => {
                        const outsideCount = rentalComparables.filter(c => c._outsideSearch && selectedRentalComparables[c.id]).length;
                        return outsideCount > 0 ? (
                          <span style={{ fontWeight: 500, color: "#f59e0b", marginLeft: 6, fontSize: 13 }}>
                            · {outsideCount} outside criteria
                          </span>
                        ) : null;
                      })()}
                    </label>
                    <div style={{ fontSize: 12, color: "#6b7280", lineHeight: 1.4, marginBottom: 8 }}>
                      Recent rental listings nearby. Select the ones you'd like to include as rental comparables and add commentary on each.
                    </div>

                    {rentalComparablesLoading ? (
                      <div style={{ display: "flex", justifyContent: "center", padding: 24 }}>
                        <Spinner animation="border" />
                      </div>
                    ) : rentalComparables.length === 0 ? (
                      <div style={{ color: "#9ca3af", fontSize: 14, padding: 12 }}>
                        No rental comparables found. Enter an address in the Details tab and click Search Comparables.
                      </div>
                    ) : (
                      <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr", gap: 16 }}>
                        {[...rentalComparables].sort((a, b) => {
                          const aS = selectedRentalComparables[a.id] ? 1 : 0;
                          const bS = selectedRentalComparables[b.id] ? 1 : 0;
                          return bS - aS;
                        }).map(comp => {
                          const isSelected = !!selectedRentalComparables[comp.id];
                          return (
                            <div key={comp.id} style={{
                              borderRadius: 14, overflow: "hidden",
                              background: "#fff",
                              boxShadow: isSelected ? "0 0 0 2px #16a34a, 0 8px 25px rgba(22,163,74,.15)" : "0 1px 6px rgba(0,0,0,.08)",
                              transition: "all 0.2s ease",
                              cursor: "pointer",
                              position: "relative",
                            }} onClick={() => toggleRentalComparable(comp.id)}>
                              {/* Selection indicator */}
                              {isSelected && (
                                <div style={{ position: "absolute", top: 10, left: 10, zIndex: 2, background: "#16a34a", borderRadius: "50%", width: 26, height: 26, display: "flex", alignItems: "center", justifyContent: "center", boxShadow: "0 2px 8px rgba(22,163,74,.4)" }}>
                                  <FaCheck style={{ color: "#fff", fontSize: 12 }} />
                                </div>
                              )}
                              {/* Outside search badge */}
                              {comp._outsideSearch && (
                                <div style={{ position: "absolute", top: 10, left: isSelected ? 44 : 10, zIndex: 2, background: "rgba(245,158,11,.9)", color: "#fff", fontSize: 10, fontWeight: 700, borderRadius: 6, padding: "3px 8px", backdropFilter: "blur(4px)", letterSpacing: "0.3px" }}>
                                  Outside search criteria
                                </div>
                              )}
                              {/* Image carousel */}
                              {(() => {
                                const mediaImgs = parseMediaUrls(comp.media);
                                const allImgs = (mediaImgs.length > 0 ? mediaImgs : comp.photo_urls?.length ? comp.photo_urls : comp.photo_url_main ? [comp.photo_url_main] : []).filter(u => !failedImages.has(u));
                                if (allImgs.length === 0) return <div style={{ height: 200, background: "linear-gradient(135deg, #dcfce7 0%, #d1fae5 100%)", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: 6 }}><FaHome style={{ fontSize: 40, color: "#86efac" }} /><span style={{ fontSize: 11, color: "#86efac", fontWeight: 500 }}>No image</span></div>;
                                const loadedCount = compLoadedImages[comp.id] || 2;
                                const imgs = allImgs.slice(0, loadedCount);
                                const idx = compCarouselIndexes[comp.id] || 0;
                                const safeIdx = Math.min(idx, imgs.length - 1);
                                const hasMore = loadedCount < allImgs.length;
                                const handleNext = (e) => {
                                  e.stopPropagation();
                                  if (safeIdx === imgs.length - 1 && hasMore) {
                                    setCompLoadedImages(prev => ({ ...prev, [comp.id]: Math.min(loadedCount + 2, allImgs.length) }));
                                    setCompCarouselIndexes(prev => ({ ...prev, [comp.id]: safeIdx + 1 }));
                                  } else {
                                    setCompCarouselIndexes(prev => ({ ...prev, [comp.id]: (safeIdx + 1) % imgs.length }));
                                  }
                                };
                                const handlePrev = (e) => {
                                  e.stopPropagation();
                                  setCompCarouselIndexes(prev => ({ ...prev, [comp.id]: (safeIdx - 1 + imgs.length) % imgs.length }));
                                };
                                return (
                                  <div style={{ position: "relative", width: "100%", height: 200, overflow: "hidden" }}>
                                    <img src={imgs[safeIdx]} alt="" onError={() => setFailedImages(prev => new Set(prev).add(imgs[safeIdx]))} style={{ width: "100%", height: "100%", objectFit: "cover", display: "block", transition: "opacity .2s" }} />
                                    <div style={{ position: "absolute", bottom: 0, left: 0, right: 0, height: 60, background: "linear-gradient(transparent, rgba(0,0,0,.35))", pointerEvents: "none" }} />
                                    {(imgs.length > 1 || hasMore) && (
                                      <>
                                        <button onClick={handlePrev}
                                          style={{ position: "absolute", left: 8, top: "50%", transform: "translateY(-50%)", background: "rgba(255,255,255,.85)", color: "#1f2937", border: "none", borderRadius: "50%", width: 32, height: 32, display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer", fontSize: 12, padding: 0, boxShadow: "0 2px 6px rgba(0,0,0,.15)", backdropFilter: "blur(4px)" }}
                                        ><FaChevronLeft /></button>
                                        <button onClick={handleNext}
                                          style={{ position: "absolute", right: 8, top: "50%", transform: "translateY(-50%)", background: "rgba(255,255,255,.85)", color: "#1f2937", border: "none", borderRadius: "50%", width: 32, height: 32, display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer", fontSize: 12, padding: 0, boxShadow: "0 2px 6px rgba(0,0,0,.15)", backdropFilter: "blur(4px)" }}
                                        ><FaChevronRight /></button>
                                      </>
                                    )}
                                    <span style={{ position: "absolute", bottom: 8, right: 8, background: "rgba(0,0,0,.55)", color: "#fff", fontSize: 11, fontWeight: 600, borderRadius: 8, padding: "3px 8px", backdropFilter: "blur(4px)" }}>
                                      {safeIdx + 1} / {allImgs.length}
                                    </span>
                                    {comp.property_type_display && (
                                      <span style={{ position: "absolute", top: 10, right: 10, background: "rgba(255,255,255,.9)", color: "#374151", fontSize: 11, fontWeight: 600, borderRadius: 6, padding: "3px 8px", backdropFilter: "blur(4px)" }}>
                                        {comp.property_type_display}
                                      </span>
                                    )}
                                  </div>
                                );
                              })()}
                              {/* Card body */}
                              <div style={{ padding: "14px 16px 16px" }}>
                                <div style={{ fontSize: 20, fontWeight: 800, color: "#14532d", marginBottom: 4, letterSpacing: "-0.3px" }}>
                                  {formatCurrency(comp.price)}<span style={{ fontSize: 14, fontWeight: 600, color: "#6b7280" }}>/wk</span>
                                </div>
                                <div style={{ fontSize: 14, color: "#4b5563", marginBottom: 10, display: "flex", alignItems: "flex-start", gap: 5, lineHeight: 1.3 }}>
                                  <FaMapMarkerAlt style={{ color: "#9ca3af", fontSize: 12, marginTop: 2, flexShrink: 0 }} />
                                  <span>{comp.display_address}</span>
                                </div>
                                <div style={{ display: "flex", gap: 16, marginBottom: 10 }}>
                                  {comp.bedrooms != null && (
                                    <div style={{ display: "flex", alignItems: "center", gap: 5, fontSize: 13, color: "#6b7280" }}>
                                      <FaBed style={{ fontSize: 14, color: "#9ca3af" }} />
                                      <span style={{ fontWeight: 600, color: "#374151" }}>{comp.bedrooms}</span>
                                    </div>
                                  )}
                                  {comp.bathrooms != null && (
                                    <div style={{ display: "flex", alignItems: "center", gap: 5, fontSize: 13, color: "#6b7280" }}>
                                      <FaBath style={{ fontSize: 13, color: "#9ca3af" }} />
                                      <span style={{ fontWeight: 600, color: "#374151" }}>{comp.bathrooms}</span>
                                    </div>
                                  )}
                                  {comp.carspaces != null && (
                                    <div style={{ display: "flex", alignItems: "center", gap: 5, fontSize: 13, color: "#6b7280" }}>
                                      <FaCar style={{ fontSize: 13, color: "#9ca3af" }} />
                                      <span style={{ fontWeight: 600, color: "#374151" }}>{comp.carspaces}</span>
                                    </div>
                                  )}
                                  {comp.land_area != null && comp.land_area > 0 && (
                                    <div style={{ display: "flex", alignItems: "center", gap: 5, fontSize: 13, color: "#6b7280" }}>
                                      <FaRulerCombined style={{ fontSize: 13, color: "#9ca3af" }} />
                                      <span style={{ fontWeight: 600, color: "#374151" }}>{comp.land_area}</span>
                                      <span style={{ fontSize: 11, color: "#9ca3af" }}>m²</span>
                                    </div>
                                  )}
                                </div>
                                <div style={{ display: "flex", alignItems: "center", gap: 5, fontSize: 12, color: "#6b7280" }}>
                                  <FaCalendarAlt style={{ fontSize: 11, color: "#9ca3af" }} />
                                  <span>Leased {comp.filter_date ? new Date(comp.filter_date).toLocaleDateString() : ""}</span>
                                </div>
                                {/* Features */}
                                {(() => {
                                  let feats = [];
                                  try { feats = typeof comp.features === "string" ? JSON.parse(comp.features) : Array.isArray(comp.features) ? comp.features : []; } catch {}
                                  if (!feats.length) return null;
                                  return (
                                    <div style={{ display: "flex", flexWrap: "wrap", gap: 5, marginTop: 10 }}>
                                      {feats.map((f, i) => (
                                        <span key={i} style={{
                                          fontSize: 11, fontWeight: 500, color: "#4b5563",
                                          background: "#f3f4f6", borderRadius: 5, padding: "2px 8px",
                                          whiteSpace: "nowrap",
                                        }}>
                                          {f.replace(/([A-Z])/g, " $1").trim()}
                                        </span>
                                      ))}
                                    </div>
                                  );
                                })()}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>}
                  </>)}

                  {modalTab === "details" && sectionToggles.details && (<>
                  {/* ─── Send History Timeline ─── */}
                  {sendHistory.length > 0 && (
                    <div>
                      <label style={{ fontWeight: 600, fontSize: 14, color: "#374151", marginBottom: 2, display: "block" }}>
                        <FaClock style={{ marginRight: 6, color: "#6b7280", fontSize: 13 }} />
                        Send History
                        <span style={{ fontWeight: 500, color: "#6b7280", marginLeft: 8, fontSize: 13 }}>({sendHistory.length})</span>
                      </label>
                      <div style={{ fontSize: 12, color: "#6b7280", lineHeight: 1.4, marginBottom: 10 }}>
                        A log of when this appraisal was sent and to whom.
                      </div>
                      <div style={{ position: "relative", paddingLeft: 20 }}>
                        {/* Vertical line */}
                        <div style={{
                          position: "absolute", left: 7, top: 4, bottom: 4, width: 2,
                          background: "#e5e7eb", borderRadius: 1,
                        }} />
                        {[...sendHistory].reverse().map((entry, idx) => {
                          const d = new Date(entry.sent_at);
                          const dateStr = d.toLocaleDateString(undefined, { day: "numeric", month: "short", year: "numeric" });
                          const timeStr = d.toLocaleTimeString(undefined, { hour: "2-digit", minute: "2-digit" });
                          return (
                            <div key={idx} style={{ position: "relative", paddingBottom: idx < sendHistory.length - 1 ? 16 : 0 }}>
                              {/* Dot */}
                              <div style={{
                                position: "absolute", left: -16, top: 4,
                                width: 12, height: 12, borderRadius: "50%",
                                background: idx === 0 ? "#059669" : "#d1d5db",
                                border: "2px solid #fff", boxShadow: "0 0 0 2px " + (idx === 0 ? "#059669" : "#e5e7eb"),
                              }} />
                              <div style={{
                                background: idx === 0 ? "#f0fdf4" : "#f9fafb",
                                border: "1px solid " + (idx === 0 ? "#bbf7d0" : "#e5e7eb"),
                                borderRadius: 8, padding: "10px 14px",
                              }}>
                                <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap", fontSize: 13 }}>
                                  {entry.channel === "sms" ? (
                                    <FaSms style={{ color: "#0284c7", fontSize: 12, flexShrink: 0 }} title="SMS" />
                                  ) : (
                                    <FaPaperPlane style={{ color: "#059669", fontSize: 11, flexShrink: 0 }} title="Email" />
                                  )}
                                  <span style={{ fontWeight: 600, color: "#374151" }}>
                                    {entry.recipient_name || entry.recipient_email || entry.recipient_mobile}
                                  </span>
                                  {entry.recipient_name && (entry.recipient_email || entry.recipient_mobile) && (
                                    <span style={{ color: "#6b7280" }}>
                                      {entry.channel === "sms" ? entry.recipient_mobile : entry.recipient_email}
                                    </span>
                                  )}
                                  {entry.channel === "sms" && (
                                    <span style={{
                                      background: "#e0f2fe", color: "#0369a1",
                                      fontSize: 10, fontWeight: 700, padding: "1px 6px",
                                      borderRadius: 10, textTransform: "uppercase", letterSpacing: 0.4,
                                    }}>SMS</span>
                                  )}
                                  <span style={{ color: "#9ca3af", marginLeft: "auto", fontSize: 12, flexShrink: 0 }}>
                                    {dateStr} {timeStr}
                                  </span>
                                </div>
                                {entry.message && (
                                  <div style={{
                                    marginTop: 6, fontSize: 13, color: "#4b5563",
                                    background: "#fff", borderRadius: 6, padding: "6px 10px",
                                    border: "1px solid #e5e7eb", fontStyle: "italic",
                                  }}>
                                    "{entry.message}"
                                  </div>
                                )}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}
                  </>)}

                  {modalTab === "location" && !sectionToggles.location && (
                    <div style={{ textAlign: "center", padding: "60px 20px", color: "#9ca3af" }}>
                      <div style={{ fontSize: 32, marginBottom: 8 }}>🙈</div>
                      <div style={{ fontSize: 14 }}>This section is excluded from the appraisal. Check the box above to include it.</div>
                    </div>
                  )}
                  {modalTab === "location" && sectionToggles.location && (
                    <div>
                      <label style={{ fontWeight: 600, fontSize: 14, color: "#374151", marginBottom: 2, display: "block" }}>Property Location Map</label>
                      <div style={{ fontSize: 12, color: "#6b7280", lineHeight: 1.4, marginBottom: 12 }}>
                        Map showing the subject property and selected comparables. Select comparables on the Comparables tab to see them here.
                      </div>

                      {/* AI Location Analysis prompt */}
                      <div style={{ marginBottom: 16 }}>
                        <label style={{ fontWeight: 600, fontSize: 13, color: "#374151", marginBottom: 4, display: "block" }}>AI Analysis Prompt <span style={{ fontWeight: 400, color: "#9ca3af", fontSize: 12 }}>(optional)</span></label>
                        <div style={{ display: "flex", gap: 8, alignItems: "flex-end" }}>
                          <textarea
                            value={locationAnalysisPrompt}
                            onChange={e => {
                              setLocationAnalysisPrompt(e.target.value);
                              e.target.style.height = "auto";
                              e.target.style.height = e.target.scrollHeight + "px";
                            }}
                            placeholder="e.g. Focus on proximity to schools, public transport, and waterfront views…"
                            rows={2}
                            style={{
                              flex: 1, padding: "8px 12px", borderRadius: 8,
                              border: "1px solid #d1d5db", fontSize: 13, outline: "none",
                              resize: "none", overflow: "hidden", lineHeight: 1.5,
                              fontFamily: "inherit", transition: "border-color .15s",
                            }}
                            onFocus={e => e.target.style.borderColor = "#6366f1"}
                            onBlur={e => e.target.style.borderColor = "#d1d5db"}
                          />
                          <button
                            onClick={handleLocationAnalysis}
                            disabled={locationAnalysisLoading || !features.latitude || !features.longitude}
                            style={{
                              background: locationAnalysisLoading ? "#9ca3af" : "linear-gradient(135deg, #1f2937 0%, #374151 100%)",
                              color: "#fff", border: "none", borderRadius: 8,
                              padding: "8px 16px", fontSize: 13, fontWeight: 600,
                              cursor: (locationAnalysisLoading || !features.latitude) ? "not-allowed" : "pointer",
                              display: "flex", alignItems: "center", gap: 6,
                              whiteSpace: "nowrap", boxShadow: "0 1px 3px rgba(0,0,0,.12)",
                              transition: "all .2s", flexShrink: 0,
                            }}
                          >
                            {locationAnalysisLoading ? <Spinner animation="border" size="sm" style={{ width: 14, height: 14 }} /> : <FaMagic />}
                            {locationAnalysisLoading ? "Analysing…" : "Analyse Location"}
                          </button>
                        </div>
                        {/* Progress bar */}
                        {locationAnalysisLoading && (
                          <div style={{ marginTop: 10 }}>
                            <div style={{ width: "100%", height: 4, background: "#e5e7eb", borderRadius: 2, overflow: "hidden" }}>
                              <div style={{
                                height: "100%",
                                width: `${locationAnalysisProgress}%`,
                                background: "linear-gradient(90deg, #1f2937, #374151)",
                                borderRadius: 2,
                                transition: locationAnalysisProgress === 100 ? "width 0.3s ease" : "width 0.4s linear",
                              }} />
                            </div>
                            <div style={{ fontSize: 11, color: "#9ca3af", marginTop: 4, textAlign: "center" }}>
                              {locationAnalysisProgress < 95 ? "Analysing property location and comparables…" : "Almost there…"}
                            </div>
                          </div>
                        )}
                      </div>

                      <AppraisalMap
                        subjectAddress={selectedAddress?.address || addressQuery}
                        subjectLat={features.latitude}
                        subjectLng={features.longitude}
                        subjectPrice={priceMid}
                        subjectPhoto={propertyImages?.[0] || null}
                        subjectBedrooms={features.bedrooms}
                        subjectBathrooms={features.bathrooms}
                        subjectCarspaces={features.parking}
                        subjectLandSize={features.land_size}
                        comparables={comparables}
                        selectedComparables={selectedComparables}
                        rentalComparables={rentalComparables}
                        selectedRentalComparables={selectedRentalComparables}
                        analysisResults={analysisResults}
                        isMobile={isMobile}
                        onCameraChanged={ev => {
                          const { center, zoom } = ev.detail;
                          setMapCenter({ lat: center.lat, lng: center.lng });
                          setMapZoom(zoom);
                        }}
                      />

                      {/* Location analysis output */}
                      {(locationAnalysis || locationAnalysisLoading) && (
                        <div style={{ marginTop: 20 }}>
                          <label style={{ fontWeight: 600, fontSize: 13, color: "#374151", marginBottom: 4, display: "block" }}>Location Analysis</label>
                          <div style={{ fontSize: 12, color: "#6b7280", lineHeight: 1.4, marginBottom: 6 }}>
                            AI-generated analysis of the property location and comparables. All fields are editable.
                          </div>
                          {locationAnalysisLoading ? (
                            <div style={{ display: "flex", alignItems: "center", gap: 10, padding: 16, background: "#f9fafb", borderRadius: 10, border: "1px solid #e5e7eb" }}>
                              <Spinner animation="border" size="sm" style={{ color: "#6b7280" }} />
                              <span style={{ fontSize: 13, color: "#6b7280", fontWeight: 500 }}>Analysing property location and comparables…</span>
                            </div>
                          ) : locationAnalysis && (
                            <div style={{ background: "#f9fafb", borderRadius: 10, border: "1px solid #e5e7eb", padding: 16, display: "flex", flexDirection: "column", gap: 14 }}>
                              {/* Summary */}
                              <div>
                                <label style={{ fontWeight: 600, fontSize: 12, color: "#6b7280", marginBottom: 3, display: "block" }}>Summary</label>
                                <input
                                  value={locationAnalysis.summary || ""}
                                  onChange={e => setLocationAnalysis(prev => ({ ...prev, summary: e.target.value }))}
                                  style={{ width: "100%", padding: "7px 10px", borderRadius: 7, border: "1px solid #d1d5db", fontSize: 13, outline: "none", fontFamily: "inherit", boxSizing: "border-box" }}
                                  onFocus={e => e.target.style.borderColor = "#6366f1"}
                                  onBlur={e => e.target.style.borderColor = "#d1d5db"}
                                />
                              </div>

                              {/* Neighbourhood & Street type */}
                              <div style={{ display: "flex", gap: 10 }}>
                                <div style={{ flex: 1 }}>
                                  <label style={{ fontWeight: 600, fontSize: 12, color: "#6b7280", marginBottom: 3, display: "block" }}>Neighbourhood Type</label>
                                  <input
                                    value={locationAnalysis.neighbourhood_type || ""}
                                    onChange={e => setLocationAnalysis(prev => ({ ...prev, neighbourhood_type: e.target.value }))}
                                    style={{ width: "100%", padding: "7px 10px", borderRadius: 7, border: "1px solid #d1d5db", fontSize: 13, outline: "none", fontFamily: "inherit", boxSizing: "border-box" }}
                                    onFocus={e => e.target.style.borderColor = "#6366f1"}
                                    onBlur={e => e.target.style.borderColor = "#d1d5db"}
                                  />
                                </div>
                                <div style={{ flex: 1 }}>
                                  <label style={{ fontWeight: 600, fontSize: 12, color: "#6b7280", marginBottom: 3, display: "block" }}>Street Type</label>
                                  <input
                                    value={locationAnalysis.street_type || ""}
                                    onChange={e => setLocationAnalysis(prev => ({ ...prev, street_type: e.target.value }))}
                                    style={{ width: "100%", padding: "7px 10px", borderRadius: 7, border: "1px solid #d1d5db", fontSize: 13, outline: "none", fontFamily: "inherit", boxSizing: "border-box" }}
                                    onFocus={e => e.target.style.borderColor = "#6366f1"}
                                    onBlur={e => e.target.style.borderColor = "#d1d5db"}
                                  />
                                </div>
                              </div>

                              {/* Positives */}
                              <div>
                                <label style={{ fontWeight: 600, fontSize: 12, color: "#6b7280", marginBottom: 3, display: "block" }}>Positives</label>
                                {(locationAnalysis.positives || []).map((item, idx) => (
                                  <div key={idx} style={{ display: "flex", gap: 6, alignItems: "center", marginBottom: 4 }}>
                                    <span style={{ color: "#22c55e", fontSize: 14, flexShrink: 0 }}>+</span>
                                    <input
                                      value={item}
                                      onChange={e => {
                                        const arr = [...(locationAnalysis.positives || [])];
                                        arr[idx] = e.target.value;
                                        setLocationAnalysis(prev => ({ ...prev, positives: arr }));
                                      }}
                                      style={{ flex: 1, padding: "5px 8px", borderRadius: 6, border: "1px solid #d1d5db", fontSize: 13, outline: "none", fontFamily: "inherit", boxSizing: "border-box" }}
                                      onFocus={e => e.target.style.borderColor = "#6366f1"}
                                      onBlur={e => e.target.style.borderColor = "#d1d5db"}
                                    />
                                    <button onClick={() => setLocationAnalysis(prev => ({ ...prev, positives: prev.positives.filter((_, i) => i !== idx) }))} style={{ background: "none", border: "none", color: "#9ca3af", cursor: "pointer", padding: 2, fontSize: 14 }} title="Remove">×</button>
                                  </div>
                                ))}
                                <button
                                  onClick={() => setLocationAnalysis(prev => ({ ...prev, positives: [...(prev.positives || []), ""] }))}
                                  style={{ background: "none", border: "1px dashed #d1d5db", borderRadius: 6, padding: "4px 10px", fontSize: 12, color: "#6b7280", cursor: "pointer", marginTop: 2 }}
                                >+ Add positive</button>
                              </div>

                              {/* Negatives */}
                              <div>
                                <label style={{ fontWeight: 600, fontSize: 12, color: "#6b7280", marginBottom: 3, display: "block" }}>Negatives</label>
                                {(locationAnalysis.negatives || []).map((item, idx) => (
                                  <div key={idx} style={{ display: "flex", gap: 6, alignItems: "center", marginBottom: 4 }}>
                                    <span style={{ color: "#ef4444", fontSize: 14, flexShrink: 0 }}>−</span>
                                    <input
                                      value={item}
                                      onChange={e => {
                                        const arr = [...(locationAnalysis.negatives || [])];
                                        arr[idx] = e.target.value;
                                        setLocationAnalysis(prev => ({ ...prev, negatives: arr }));
                                      }}
                                      style={{ flex: 1, padding: "5px 8px", borderRadius: 6, border: "1px solid #d1d5db", fontSize: 13, outline: "none", fontFamily: "inherit", boxSizing: "border-box" }}
                                      onFocus={e => e.target.style.borderColor = "#6366f1"}
                                      onBlur={e => e.target.style.borderColor = "#d1d5db"}
                                    />
                                    <button onClick={() => setLocationAnalysis(prev => ({ ...prev, negatives: prev.negatives.filter((_, i) => i !== idx) }))} style={{ background: "none", border: "none", color: "#9ca3af", cursor: "pointer", padding: 2, fontSize: 14 }} title="Remove">×</button>
                                  </div>
                                ))}
                                <button
                                  onClick={() => setLocationAnalysis(prev => ({ ...prev, negatives: [...(prev.negatives || []), ""] }))}
                                  style={{ background: "none", border: "1px dashed #d1d5db", borderRadius: 6, padding: "4px 10px", fontSize: 12, color: "#6b7280", cursor: "pointer", marginTop: 2 }}
                                >+ Add negative</button>
                              </div>

                              {/* Nearby Amenities */}
                              <div>
                                <label style={{ fontWeight: 600, fontSize: 12, color: "#6b7280", marginBottom: 3, display: "block" }}>Nearby Amenities</label>
                                {(locationAnalysis.nearby_amenities || []).map((am, idx) => (
                                  <div key={idx} style={{ display: "flex", gap: 6, alignItems: "center", marginBottom: 4 }}>
                                    <input
                                      value={am.type || ""}
                                      onChange={e => {
                                        const arr = [...(locationAnalysis.nearby_amenities || [])];
                                        arr[idx] = { ...arr[idx], type: e.target.value };
                                        setLocationAnalysis(prev => ({ ...prev, nearby_amenities: arr }));
                                      }}
                                      placeholder="Type"
                                      style={{ width: 90, padding: "5px 8px", borderRadius: 6, border: "1px solid #d1d5db", fontSize: 12, outline: "none", fontFamily: "inherit", boxSizing: "border-box" }}
                                      onFocus={e => e.target.style.borderColor = "#6366f1"}
                                      onBlur={e => e.target.style.borderColor = "#d1d5db"}
                                    />
                                    <input
                                      value={am.name || ""}
                                      onChange={e => {
                                        const arr = [...(locationAnalysis.nearby_amenities || [])];
                                        arr[idx] = { ...arr[idx], name: e.target.value };
                                        setLocationAnalysis(prev => ({ ...prev, nearby_amenities: arr }));
                                      }}
                                      placeholder="Name"
                                      style={{ flex: 1, padding: "5px 8px", borderRadius: 6, border: "1px solid #d1d5db", fontSize: 12, outline: "none", fontFamily: "inherit", boxSizing: "border-box" }}
                                      onFocus={e => e.target.style.borderColor = "#6366f1"}
                                      onBlur={e => e.target.style.borderColor = "#d1d5db"}
                                    />
                                    <input
                                      value={am.distance_label || ""}
                                      onChange={e => {
                                        const arr = [...(locationAnalysis.nearby_amenities || [])];
                                        arr[idx] = { ...arr[idx], distance_label: e.target.value };
                                        setLocationAnalysis(prev => ({ ...prev, nearby_amenities: arr }));
                                      }}
                                      placeholder="Distance"
                                      style={{ width: 90, padding: "5px 8px", borderRadius: 6, border: "1px solid #d1d5db", fontSize: 12, outline: "none", fontFamily: "inherit", boxSizing: "border-box" }}
                                      onFocus={e => e.target.style.borderColor = "#6366f1"}
                                      onBlur={e => e.target.style.borderColor = "#d1d5db"}
                                    />
                                    <button onClick={() => setLocationAnalysis(prev => ({ ...prev, nearby_amenities: prev.nearby_amenities.filter((_, i) => i !== idx) }))} style={{ background: "none", border: "none", color: "#9ca3af", cursor: "pointer", padding: 2, fontSize: 14 }} title="Remove">×</button>
                                  </div>
                                ))}
                                <button
                                  onClick={() => setLocationAnalysis(prev => ({ ...prev, nearby_amenities: [...(prev.nearby_amenities || []), { type: "", name: "", distance_label: "" }] }))}
                                  style={{ background: "none", border: "1px dashed #d1d5db", borderRadius: 6, padding: "4px 10px", fontSize: 12, color: "#6b7280", cursor: "pointer", marginTop: 2 }}
                                >+ Add amenity</button>
                              </div>

                              {/* Comparable Positions */}
                              <div>
                                <label style={{ fontWeight: 600, fontSize: 12, color: "#6b7280", marginBottom: 3, display: "block" }}>Comparable Positions</label>
                                {(locationAnalysis.comparable_positions || []).map((cp, idx) => (
                                  <div key={idx} style={{ display: "flex", gap: 6, alignItems: "center", marginBottom: 4 }}>
                                    <input
                                      value={cp.address || ""}
                                      onChange={e => {
                                        const arr = [...(locationAnalysis.comparable_positions || [])];
                                        arr[idx] = { ...arr[idx], address: e.target.value };
                                        setLocationAnalysis(prev => ({ ...prev, comparable_positions: arr }));
                                      }}
                                      placeholder="Address"
                                      style={{ flex: 1, padding: "5px 8px", borderRadius: 6, border: "1px solid #d1d5db", fontSize: 12, outline: "none", fontFamily: "inherit", boxSizing: "border-box" }}
                                      onFocus={e => e.target.style.borderColor = "#6366f1"}
                                      onBlur={e => e.target.style.borderColor = "#d1d5db"}
                                    />
                                    <input
                                      value={cp.relative_position || ""}
                                      onChange={e => {
                                        const arr = [...(locationAnalysis.comparable_positions || [])];
                                        arr[idx] = { ...arr[idx], relative_position: e.target.value };
                                        setLocationAnalysis(prev => ({ ...prev, comparable_positions: arr }));
                                      }}
                                      placeholder="Relative position"
                                      style={{ flex: 1, padding: "5px 8px", borderRadius: 6, border: "1px solid #d1d5db", fontSize: 12, outline: "none", fontFamily: "inherit", boxSizing: "border-box" }}
                                      onFocus={e => e.target.style.borderColor = "#6366f1"}
                                      onBlur={e => e.target.style.borderColor = "#d1d5db"}
                                    />
                                    <input
                                      value={cp.location_advantage || ""}
                                      onChange={e => {
                                        const arr = [...(locationAnalysis.comparable_positions || [])];
                                        arr[idx] = { ...arr[idx], location_advantage: e.target.value };
                                        setLocationAnalysis(prev => ({ ...prev, comparable_positions: arr }));
                                      }}
                                      placeholder="Advantage"
                                      style={{ width: 90, padding: "5px 8px", borderRadius: 6, border: "1px solid #d1d5db", fontSize: 12, outline: "none", fontFamily: "inherit", boxSizing: "border-box" }}
                                      onFocus={e => e.target.style.borderColor = "#6366f1"}
                                      onBlur={e => e.target.style.borderColor = "#d1d5db"}
                                    />
                                    <button onClick={() => setLocationAnalysis(prev => ({ ...prev, comparable_positions: prev.comparable_positions.filter((_, i) => i !== idx) }))} style={{ background: "none", border: "none", color: "#9ca3af", cursor: "pointer", padding: 2, fontSize: 14 }} title="Remove">×</button>
                                  </div>
                                ))}
                                <button
                                  onClick={() => setLocationAnalysis(prev => ({ ...prev, comparable_positions: [...(prev.comparable_positions || []), { address: "", relative_position: "", location_advantage: "" }] }))}
                                  style={{ background: "none", border: "1px dashed #d1d5db", borderRadius: 6, padding: "4px 10px", fontSize: 12, color: "#6b7280", cursor: "pointer", marginTop: 2 }}
                                >+ Add comparable position</button>
                              </div>

                              {/* Value Factors */}
                              <div>
                                <label style={{ fontWeight: 600, fontSize: 12, color: "#6b7280", marginBottom: 3, display: "block" }}>Value Factors</label>
                                {(locationAnalysis.value_factors || []).map((vf, idx) => (
                                  <div key={idx} style={{ display: "flex", gap: 6, alignItems: "center", marginBottom: 4 }}>
                                    <input
                                      value={vf.factor || ""}
                                      onChange={e => {
                                        const arr = [...(locationAnalysis.value_factors || [])];
                                        arr[idx] = { ...arr[idx], factor: e.target.value };
                                        setLocationAnalysis(prev => ({ ...prev, value_factors: arr }));
                                      }}
                                      placeholder="Factor"
                                      style={{ flex: 1, padding: "5px 8px", borderRadius: 6, border: "1px solid #d1d5db", fontSize: 12, outline: "none", fontFamily: "inherit", boxSizing: "border-box" }}
                                      onFocus={e => e.target.style.borderColor = "#6366f1"}
                                      onBlur={e => e.target.style.borderColor = "#d1d5db"}
                                    />
                                    <select
                                      value={vf.impact || "positive"}
                                      onChange={e => {
                                        const arr = [...(locationAnalysis.value_factors || [])];
                                        arr[idx] = { ...arr[idx], impact: e.target.value };
                                        setLocationAnalysis(prev => ({ ...prev, value_factors: arr }));
                                      }}
                                      style={{ width: 100, padding: "5px 8px", borderRadius: 6, border: "1px solid #d1d5db", fontSize: 12, outline: "none", fontFamily: "inherit", boxSizing: "border-box", background: "#fff" }}
                                    >
                                      <option value="positive">Positive</option>
                                      <option value="negative">Negative</option>
                                      <option value="neutral">Neutral</option>
                                    </select>
                                    <input
                                      value={vf.detail || ""}
                                      onChange={e => {
                                        const arr = [...(locationAnalysis.value_factors || [])];
                                        arr[idx] = { ...arr[idx], detail: e.target.value };
                                        setLocationAnalysis(prev => ({ ...prev, value_factors: arr }));
                                      }}
                                      placeholder="Detail"
                                      style={{ flex: 1, padding: "5px 8px", borderRadius: 6, border: "1px solid #d1d5db", fontSize: 12, outline: "none", fontFamily: "inherit", boxSizing: "border-box" }}
                                      onFocus={e => e.target.style.borderColor = "#6366f1"}
                                      onBlur={e => e.target.style.borderColor = "#d1d5db"}
                                    />
                                    <button onClick={() => setLocationAnalysis(prev => ({ ...prev, value_factors: prev.value_factors.filter((_, i) => i !== idx) }))} style={{ background: "none", border: "none", color: "#9ca3af", cursor: "pointer", padding: 2, fontSize: 14 }} title="Remove">×</button>
                                  </div>
                                ))}
                                <button
                                  onClick={() => setLocationAnalysis(prev => ({ ...prev, value_factors: [...(prev.value_factors || []), { factor: "", impact: "positive", detail: "" }] }))}
                                  style={{ background: "none", border: "1px dashed #d1d5db", borderRadius: 6, padding: "4px 10px", fontSize: 12, color: "#6b7280", cursor: "pointer", marginTop: 2 }}
                                >+ Add value factor</button>
                              </div>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  )}
                </>
              )}

              {modalTab === "team" && !sectionToggles.team && (
                <div style={{ textAlign: "center", padding: "60px 20px", color: "#9ca3af" }}>
                  <div style={{ fontSize: 32, marginBottom: 8 }}>🙈</div>
                  <div style={{ fontSize: 14 }}>This section is excluded from the appraisal. Check the box above to include it.</div>
                </div>
              )}
              {modalTab === "team" && sectionToggles.team && (
                <div>
                  {/* Load / Refresh button */}
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
                    <div>
                      <label style={{ fontWeight: 600, fontSize: 14, color: "#374151", marginBottom: 2, display: "block" }}>Team Performance</label>
                      <div style={{ fontSize: 12, color: "#6b7280", lineHeight: 1.4 }}>
                        {includeRental && !includeSale ? "Leased" : "Sold"} listings from your agency in the last 12 months.
                      </div>
                    </div>
                    <button
                      onClick={handleFetchTeamStats}
                      disabled={agencyStatsLoading}
                      style={{
                        padding: "8px 18px", borderRadius: 8, border: "none",
                        background: agencyStatsLoading ? "#d1d5db" : "#2563eb", color: "#fff",
                        fontWeight: 600, fontSize: 13, cursor: agencyStatsLoading ? "not-allowed" : "pointer",
                        display: "flex", alignItems: "center", gap: 6,
                      }}
                    >
                      {agencyStatsLoading && <Spinner animation="border" size="sm" style={{ width: 14, height: 14 }} />}
                      {agencyStatsLoading ? "Loading…" : agencyStats ? "Refresh" : "Load Stats"}
                    </button>
                  </div>

                  {agencyStats && (
                    <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
                      {/* Agency overview */}
                      <div style={{ background: "#f9fafb", borderRadius: 10, border: "1px solid #e5e7eb", padding: 16 }}>
                        <div style={{ fontWeight: 700, fontSize: 16, color: "#111827", marginBottom: 8 }}>
                          <input value={agencyStats.agency_name || ""} onChange={e => setAgencyStats(prev => ({ ...prev, agency_name: e.target.value }))} style={{ border: "1px solid #d1d5db", borderRadius: 6, padding: "4px 8px", fontSize: 16, fontWeight: 700, width: "100%" }} />
                        </div>
                        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8, fontSize: 13 }}>
                          <div><span style={{ color: "#6b7280" }}>Total {includeRental && !includeSale ? "Leased" : "Sold"}:</span>{" "}
                            <input value={agencyStats.total_listings ?? ""} onChange={e => setAgencyStats(prev => ({ ...prev, total_listings: e.target.value }))} style={{ border: "1px solid #d1d5db", borderRadius: 6, padding: "2px 6px", fontSize: 13, width: 60 }} />
                          </div>
                          <div><span style={{ color: "#6b7280" }}>Avg Price:</span>{" "}
                            <input value={agencyStats.avg_sale_price ?? ""} onChange={e => setAgencyStats(prev => ({ ...prev, avg_sale_price: e.target.value }))} style={{ border: "1px solid #d1d5db", borderRadius: 6, padding: "2px 6px", fontSize: 13, width: 100 }} />
                          </div>
                          <div><span style={{ color: "#6b7280" }}>Total Value:</span>{" "}
                            <input value={agencyStats.total_sales_value ?? ""} onChange={e => setAgencyStats(prev => ({ ...prev, total_sales_value: e.target.value }))} style={{ border: "1px solid #d1d5db", borderRadius: 6, padding: "2px 6px", fontSize: 13, width: 100 }} />
                          </div>
                          <div><span style={{ color: "#6b7280" }}>Avg Days on Market:</span>{" "}
                            <input value={agencyStats.avg_days_on_market ?? ""} onChange={e => setAgencyStats(prev => ({ ...prev, avg_days_on_market: e.target.value }))} style={{ border: "1px solid #d1d5db", borderRadius: 6, padding: "2px 6px", fontSize: 13, width: 60 }} />
                          </div>
                        </div>
                        {agencyStats.sales_by_type && agencyStats.sales_by_type.length > 0 && (
                          <div style={{ marginTop: 10 }}>
                            <span style={{ color: "#6b7280", fontSize: 12, fontWeight: 600 }}>By Type:</span>
                            <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginTop: 4 }}>
                              {agencyStats.sales_by_type.map((s, i) => (
                                <div key={i} style={{ background: "#fff", border: "1px solid #e5e7eb", borderRadius: 6, padding: "4px 8px", fontSize: 12, display: "flex", alignItems: "center", gap: 4 }}>
                                  <input value={s.type || ""} onChange={e => { const arr = [...agencyStats.sales_by_type]; arr[i] = { ...arr[i], type: e.target.value }; setAgencyStats(prev => ({ ...prev, sales_by_type: arr })); }} style={{ border: "1px solid #d1d5db", borderRadius: 4, padding: "1px 4px", fontSize: 12, fontWeight: 600, width: 80 }} />
                                  <input value={s.count ?? ""} onChange={e => { const arr = [...agencyStats.sales_by_type]; arr[i] = { ...arr[i], count: e.target.value }; setAgencyStats(prev => ({ ...prev, sales_by_type: arr })); }} style={{ border: "1px solid #d1d5db", borderRadius: 4, padding: "1px 4px", fontSize: 12, width: 30 }} />
                                  <span style={{ color: "#9ca3af" }}>avg</span>
                                  <input value={s.avg_price || ""} onChange={e => { const arr = [...agencyStats.sales_by_type]; arr[i] = { ...arr[i], avg_price: e.target.value }; setAgencyStats(prev => ({ ...prev, sales_by_type: arr })); }} style={{ border: "1px solid #d1d5db", borderRadius: 4, padding: "1px 4px", fontSize: 12, width: 70 }} />
                                  <button onClick={() => { const arr = agencyStats.sales_by_type.filter((_, j) => j !== i); setAgencyStats(prev => ({ ...prev, sales_by_type: arr })); }} style={{ background: "none", border: "none", color: "#d1d5db", cursor: "pointer", fontSize: 14, padding: 0, lineHeight: 1 }} title="Remove">×</button>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>

                      {/* Team members with selection */}
                      <div>
                        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
                          <div style={{ fontWeight: 700, fontSize: 14, color: "#374151" }}>Team Members</div>
                          <div style={{ fontSize: 12, color: "#6b7280" }}>
                            {Object.values(selectedTeamMembers).filter(Boolean).length} of {(agencyStats.team || []).length} selected
                          </div>
                        </div>
                        <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                          {(agencyStats.team || []).map((member, idx) => {
                            const isSelected = !!selectedTeamMembers[member.agent_email];
                            return (
                              <div key={member.agent_email || idx} style={{
                                background: isSelected ? "#fff" : "#fafafa", borderRadius: 10,
                                border: isSelected ? "2px solid #2563eb" : "1px solid #e5e7eb",
                                padding: 14, display: "flex", gap: 14, opacity: isSelected ? 1 : 0.6,
                                cursor: "pointer", transition: "all 0.15s",
                              }} onClick={() => setSelectedTeamMembers(prev => ({ ...prev, [member.agent_email]: !prev[member.agent_email] }))}>
                                <div style={{ display: "flex", alignItems: "flex-start", paddingTop: 2 }}>
                                  <input type="checkbox" checked={isSelected} readOnly style={{ accentColor: "#2563eb", width: 16, height: 16 }} />
                                </div>
                                {member.photo_url && (
                                  <img src={member.photo_url} alt={member.name} style={{ width: 48, height: 48, borderRadius: "50%", objectFit: "cover", flexShrink: 0 }} />
                                )}
                                <div style={{ flex: 1, minWidth: 0 }} onClick={e => e.stopPropagation()}>
                                  <div style={{ fontWeight: 700, fontSize: 14, color: "#111827", marginBottom: 4 }}>
                                    <input value={member.name || ""} onChange={e => {
                                      const newTeam = [...(agencyStats.team || [])];
                                      newTeam[idx] = { ...newTeam[idx], name: e.target.value };
                                      setAgencyStats(prev => ({ ...prev, team: newTeam }));
                                    }} style={{ border: "1px solid #d1d5db", borderRadius: 6, padding: "2px 6px", fontSize: 14, fontWeight: 700, width: "60%" }} />
                                  </div>
                                  <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 4, fontSize: 12, color: "#374151" }}>
                                    <div><span style={{ color: "#6b7280" }}>{includeRental && !includeSale ? "Leased" : "Sold"}:</span>{" "}
                                      <input value={member.sold ?? ""} onChange={e => {
                                        const newTeam = [...(agencyStats.team || [])];
                                        newTeam[idx] = { ...newTeam[idx], sold: e.target.value };
                                        setAgencyStats(prev => ({ ...prev, team: newTeam }));
                                      }} style={{ border: "1px solid #d1d5db", borderRadius: 6, padding: "1px 4px", fontSize: 12, width: 40 }} />
                                    </div>
                                    <div><span style={{ color: "#6b7280" }}>Avg Price:</span>{" "}
                                      <input value={member.avg_sale_price ?? ""} onChange={e => {
                                        const newTeam = [...(agencyStats.team || [])];
                                        newTeam[idx] = { ...newTeam[idx], avg_sale_price: e.target.value };
                                        setAgencyStats(prev => ({ ...prev, team: newTeam }));
                                      }} style={{ border: "1px solid #d1d5db", borderRadius: 6, padding: "1px 4px", fontSize: 12, width: 70 }} />
                                    </div>
                                    <div><span style={{ color: "#6b7280" }}>Total Value:</span>{" "}
                                      <input value={member.total_sales_value ?? ""} onChange={e => {
                                        const newTeam = [...(agencyStats.team || [])];
                                        newTeam[idx] = { ...newTeam[idx], total_sales_value: e.target.value };
                                        setAgencyStats(prev => ({ ...prev, team: newTeam }));
                                      }} style={{ border: "1px solid #d1d5db", borderRadius: 6, padding: "1px 4px", fontSize: 12, width: 70 }} />
                                    </div>
                                    {member.avg_days_on_market != null && (
                                      <div><span style={{ color: "#6b7280" }}>Avg DOM:</span>{" "}
                                        <input value={member.avg_days_on_market ?? ""} onChange={e => {
                                          const newTeam = [...(agencyStats.team || [])];
                                          newTeam[idx] = { ...newTeam[idx], avg_days_on_market: e.target.value };
                                          setAgencyStats(prev => ({ ...prev, team: newTeam }));
                                        }} style={{ border: "1px solid #d1d5db", borderRadius: 6, padding: "1px 4px", fontSize: 12, width: 40 }} />
                                      </div>
                                    )}
                                  </div>
                                  {member.sales_by_type && member.sales_by_type.length > 0 && (
                                    <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginTop: 6 }}>
                                      {member.sales_by_type.map((s, si) => (
                                        <div key={si} style={{ background: "#f3f4f6", borderRadius: 4, padding: "2px 6px", fontSize: 11, display: "flex", alignItems: "center", gap: 3 }}>
                                          <input value={s.type || ""} onChange={e => { const newTeam = [...(agencyStats.team || [])]; const arr = [...(member.sales_by_type || [])]; arr[si] = { ...arr[si], type: e.target.value }; newTeam[idx] = { ...newTeam[idx], sales_by_type: arr }; setAgencyStats(prev => ({ ...prev, team: newTeam })); }} style={{ border: "1px solid #d1d5db", borderRadius: 3, padding: "1px 3px", fontSize: 11, fontWeight: 600, width: 70 }} />
                                          <input value={s.count ?? ""} onChange={e => { const newTeam = [...(agencyStats.team || [])]; const arr = [...(member.sales_by_type || [])]; arr[si] = { ...arr[si], count: e.target.value }; newTeam[idx] = { ...newTeam[idx], sales_by_type: arr }; setAgencyStats(prev => ({ ...prev, team: newTeam })); }} style={{ border: "1px solid #d1d5db", borderRadius: 3, padding: "1px 3px", fontSize: 11, width: 26 }} />
                                          <span style={{ color: "#9ca3af" }}>avg</span>
                                          <input value={s.avg_price || ""} onChange={e => { const newTeam = [...(agencyStats.team || [])]; const arr = [...(member.sales_by_type || [])]; arr[si] = { ...arr[si], avg_price: e.target.value }; newTeam[idx] = { ...newTeam[idx], sales_by_type: arr }; setAgencyStats(prev => ({ ...prev, team: newTeam })); }} style={{ border: "1px solid #d1d5db", borderRadius: 3, padding: "1px 3px", fontSize: 11, width: 60 }} />
                                          <button onClick={() => { const newTeam = [...(agencyStats.team || [])]; const arr = member.sales_by_type.filter((_, j) => j !== si); newTeam[idx] = { ...newTeam[idx], sales_by_type: arr }; setAgencyStats(prev => ({ ...prev, team: newTeam })); }} style={{ background: "none", border: "none", color: "#d1d5db", cursor: "pointer", fontSize: 13, padding: 0, lineHeight: 1 }} title="Remove">×</button>
                                        </div>
                                      ))}
                                    </div>
                                  )}
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      </div>

                      {/* Recent listings with selection */}
                      {(agencyStats.recent_listings || []).length > 0 && (
                        <div>
                          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
                            <div style={{ fontWeight: 700, fontSize: 14, color: "#374151" }}>Recent {includeRental && !includeSale ? "Leased" : "Sold"} Listings</div>
                            <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                              <span style={{ fontSize: 12, color: "#6b7280" }}>
                                {Object.values(selectedRecentListings).filter(Boolean).length} selected
                              </span>
                              <button onClick={() => {
                                const all = {};
                                (agencyStats.recent_listings || []).forEach(l => { all[l.id] = true; });
                                setSelectedRecentListings(prev => Object.keys(prev).filter(k => prev[k]).length === (agencyStats.recent_listings || []).length ? {} : all);
                              }} style={{ background: "none", border: "1px solid #d1d5db", borderRadius: 6, padding: "2px 10px", fontSize: 11, color: "#6b7280", cursor: "pointer" }}>
                                {Object.values(selectedRecentListings).filter(Boolean).length === (agencyStats.recent_listings || []).length ? "Deselect All" : "Select All"}
                              </button>
                            </div>
                          </div>
                          <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                            {(agencyStats.recent_listings || []).map((listing) => {
                              const isSelected = !!selectedRecentListings[listing.id];
                              return (
                                <div key={listing.id} style={{
                                  display: "flex", gap: 12, alignItems: "center", padding: 10,
                                  borderRadius: 10, border: isSelected ? "2px solid #2563eb" : "1px solid #e5e7eb",
                                  background: isSelected ? "#fff" : "#fafafa", opacity: isSelected ? 1 : 0.7,
                                  cursor: "pointer", transition: "all 0.15s",
                                }} onClick={() => setSelectedRecentListings(prev => ({ ...prev, [listing.id]: !prev[listing.id] }))}>
                                  <input type="checkbox" checked={isSelected} readOnly style={{ accentColor: "#2563eb", width: 16, height: 16, flexShrink: 0 }} />
                                  {listing.photo && (
                                    <img src={listing.photo} alt="" style={{ width: 56, height: 42, borderRadius: 6, objectFit: "cover", flexShrink: 0 }} />
                                  )}
                                  <div style={{ flex: 1, minWidth: 0 }} onClick={e => e.stopPropagation()}>
                                    <div style={{ fontSize: 13, fontWeight: 600, color: "#111827", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                                      <input value={listing.address || ""} onChange={e => {
                                        const newListings = [...(agencyStats.recent_listings || [])];
                                        const i = newListings.findIndex(l => l.id === listing.id);
                                        if (i >= 0) { newListings[i] = { ...newListings[i], address: e.target.value }; setAgencyStats(prev => ({ ...prev, recent_listings: newListings })); }
                                      }} style={{ border: "1px solid #d1d5db", borderRadius: 4, padding: "1px 4px", fontSize: 13, fontWeight: 600, width: "100%" }} />
                                    </div>
                                    <div style={{ fontSize: 11, color: "#6b7280", marginTop: 2, display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center" }}>
                                      <input value={listing.display_price || ""} onChange={e => {
                                        const newListings = [...(agencyStats.recent_listings || [])];
                                        const i = newListings.findIndex(l => l.id === listing.id);
                                        if (i >= 0) { newListings[i] = { ...newListings[i], display_price: e.target.value }; setAgencyStats(prev => ({ ...prev, recent_listings: newListings })); }
                                      }} style={{ border: "1px solid #d1d5db", borderRadius: 4, padding: "1px 4px", fontSize: 11, width: 90, fontWeight: 600 }} />
                                      {listing.bedrooms != null && <span>{listing.bedrooms} bed</span>}
                                      {listing.bathrooms != null && <span>{listing.bathrooms} bath</span>}
                                      {listing.property_type && <span>{listing.property_type}</span>}
                                      {listing.sold_date && <span>{new Date(listing.sold_date).toLocaleDateString("en-AU", { month: "short", year: "numeric" })}</span>}
                                      {listing.agent_names && listing.agent_names.length > 0 && <span style={{ color: "#9ca3af" }}>— {listing.agent_names.join(", ")}</span>}
                                    </div>
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Published URL */}
            {publishedUrl && (
              <div style={{
                padding: "10px 28px", background: "#f0fdf4", borderTop: "1px solid #bbf7d0",
                display: "flex", alignItems: "center", gap: 10, flexShrink: 0,
              }}>
                <FaLink style={{ color: "#059669", flexShrink: 0 }} />
                <span style={{ fontSize: 14, color: "#374151", fontWeight: 600 }}>Published:</span>
                <a
                  href={publishedUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{ color: "#2563eb", fontSize: 14, wordBreak: "break-all" }}
                >
                  {publishedUrl}
                </a>
              </div>
            )}

            {/* Footer */}
            <div style={{
              display: "flex", gap: 12, justifyContent: isMobile ? "stretch" : "flex-end", flexWrap: "wrap",
              padding: "12px 28px 20px 28px", borderTop: "1px solid #e5e7eb", flexShrink: 0,
              flexDirection: isMobile ? "column" : "row",
            }}>
              {!isMobile && (
                <button
                  onClick={closeModal}
                  disabled={saving}
                  style={{
                    background: "#f1f5f9", color: "#374151", border: "none",
                    borderRadius: 6, padding: "8px 18px", fontWeight: 600,
                    cursor: "pointer", fontSize: 15,
                  }}
                >
                  Close
                </button>
              )}
              <button
                onClick={handlePublishFromModal}
                disabled={publishing || saving || !selectedAddress}
                style={{
                  background: publishing ? "#c4b5fd" : "#7c3aed", color: "#fff",
                  border: "none", borderRadius: 6, padding: "10px 18px",
                  fontWeight: 600,
                  cursor: publishing || saving || !selectedAddress ? "not-allowed" : "pointer",
                  fontSize: 15, display: "flex", alignItems: "center", justifyContent: "center",
                  opacity: !selectedAddress ? 0.5 : 1,
                  width: isMobile ? "100%" : "auto",
                }}
              >
                {publishing && <Spinner animation="border" size="sm" style={{ marginRight: 8 }} />}
                <FaGlobe style={{ marginRight: 6 }} />
                {publishing ? "Publishing…" : "Publish Appraisal"}
              </button>
              <button
                onClick={handleSave}
                disabled={saving || !selectedAddress}
                style={{
                  background: saving ? "#93c5fd" : "#2563eb", color: "#fff",
                  border: "none", borderRadius: 6, padding: "10px 18px",
                  fontWeight: 600,
                  cursor: saving || !selectedAddress ? "not-allowed" : "pointer",
                  fontSize: 15, display: "flex", alignItems: "center", justifyContent: "center",
                  opacity: !selectedAddress ? 0.5 : 1,
                  width: isMobile ? "100%" : "auto",
                }}
              >
                {saving && <Spinner animation="border" size="sm" style={{ marginRight: 8 }} />}
                {saving ? "Saving…" : (editingId ? "Save Changes" : "Save Appraisal")}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ─── Delete modal ─── */}
      {deleteModal.open && (
        <div style={{
          position: "fixed", top: 0, right: 0, bottom: 0,
          left: isMobile ? 0 : 260,
          background: "rgba(0,0,0,0.25)", zIndex: 1000,
          display: "flex", alignItems: "center", justifyContent: "center",
        }}>
          <div style={{
            background: "#fff", borderRadius: 10, padding: 32,
            minWidth: 320, maxWidth: 400, width: "90vw",
            boxShadow: "0 4px 24px #0002",
            display: "flex", flexDirection: "column", alignItems: "center",
          }}>
            <div style={{ fontSize: 20, fontWeight: 700, marginBottom: 16 }}>Delete Appraisal?</div>
            <div style={{ color: "#444", marginBottom: 24, textAlign: "center" }}>
              Are you sure you want to delete this appraisal? This action cannot be undone.
            </div>
            <div style={{ display: "flex", gap: 16 }}>
              <button
                onClick={() => setDeleteModal({ open: false, id: null })}
                disabled={deleting}
                style={{
                  background: "#f1f5f9", color: "#374151", border: "none",
                  borderRadius: 6, padding: "8px 18px", fontWeight: 600,
                  cursor: "pointer", fontSize: 15,
                }}
              >Cancel</button>
              <button
                onClick={() => handleDelete(deleteModal.id)}
                disabled={deleting}
                style={{
                  background: "#e11d48", color: "#fff", border: "none",
                  borderRadius: 6, padding: "8px 18px", fontWeight: 600,
                  cursor: "pointer", fontSize: 15, display: "flex", alignItems: "center",
                }}
              >
                {!deleting && <FaTrash style={{ marginRight: 8 }} />}
                {deleting && <Spinner animation="border" size="sm" style={{ marginRight: 8 }} />}
                {deleting ? "Deleting…" : "Yes, Delete"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ═══════════════════════════════════════════════
          SEND APPRAISAL MODAL
         ═══════════════════════════════════════════════ */}
      {sendModal.open && (
        <div
          onClick={() => { if (!sending) setSendModal({ open: false, appraisalId: null, address: "" }); }}
          style={{
            position: "fixed", top: 0, right: 0, bottom: 0,
            left: isMobile ? 0 : 260,
            background: "rgba(0,0,0,0.25)", zIndex: 1000,
            display: "flex", alignItems: "center", justifyContent: "center",
          }}
        >
          <div
            onClick={e => e.stopPropagation()}
            style={{
              background: "#fff", borderRadius: 12, padding: 0,
              minWidth: 360, maxWidth: 520, width: "90vw",
              boxShadow: "0 4px 24px #0002",
              display: "flex", flexDirection: "column",
              maxHeight: "80vh",
            }}
          >
            {/* Header */}
            <div style={{
              fontSize: 18, fontWeight: 700, padding: "20px 24px 12px",
              borderBottom: "1px solid #e5e7eb",
              display: "flex", alignItems: "center", justifyContent: "space-between",
            }}>
              <span><FaPaperPlane style={{ marginRight: 8, color: "#059669" }} />Send Appraisal</span>
              <button
                onClick={() => setSendModal({ open: false, appraisalId: null, address: "" })}
                disabled={sending}
                style={{ background: "none", border: "none", cursor: "pointer", fontSize: 22, color: "#9ca3af", padding: "0 4px", lineHeight: 1 }}
              >×</button>
            </div>

            {/* Body */}
            <div style={{ padding: "16px 24px", overflowY: "auto", display: "flex", flexDirection: "column", gap: 16 }}>
              <div style={{ fontSize: 14, color: "#374151" }}>
                <span style={{ fontWeight: 600 }}>Property: </span>{sendModal.address}
              </div>

              {/* Recipients */}
              <div>
                <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 10 }}>
                  <label style={{ fontWeight: 600, fontSize: 14, color: "#374151" }}>Recipients</label>
                  <button
                    onClick={() => setSendRecipients(prev => [...prev, { name: "", email: "", mobile: "", message: "" }])}
                    style={{
                      background: "none", border: "1px solid #059669", borderRadius: 6,
                      color: "#059669", fontSize: 12, fontWeight: 600, padding: "4px 10px",
                      cursor: "pointer", display: "flex", alignItems: "center", gap: 4,
                    }}
                  >
                    <FaPlus style={{ fontSize: 10 }} /> Add Recipient
                  </button>
                </div>

                <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
                  {sendRecipients.map((r, idx) => (
                    <div key={idx} style={{
                      background: "#f9fafb", border: "1px solid #e5e7eb", borderRadius: 8,
                      padding: 12, display: "flex", flexDirection: "column", gap: 8,
                      position: "relative",
                    }}>
                      {sendRecipients.length > 1 && (
                        <button
                          onClick={() => setSendRecipients(prev => prev.filter((_, i) => i !== idx))}
                          style={{
                            position: "absolute", top: 8, right: 8, background: "none",
                            border: "none", cursor: "pointer", color: "#9ca3af", fontSize: 14,
                          }}
                          title="Remove recipient"
                        ><FaTimes /></button>
                      )}
                      <div style={{ fontSize: 12, fontWeight: 600, color: "#6b7280" }}>Recipient {idx + 1}</div>
                      <div style={{ fontSize: 11, color: "#9ca3af", marginTop: -4 }}>
                        Provide an email, a mobile, or both. The appraisal will be sent via each channel you fill in.
                      </div>
                      <div>
                        <label style={{ fontSize: 12, color: "#6b7280", marginBottom: 2, display: "block" }}>
                          <FaEnvelope style={{ marginRight: 4, fontSize: 11 }} />Email
                        </label>
                        <input
                          value={r.email}
                          onChange={e => setSendRecipients(prev => prev.map((x, i) => i === idx ? { ...x, email: e.target.value } : x))}
                          placeholder="recipient@example.com"
                          type="email"
                          style={{ width: "100%", padding: "8px 10px", borderRadius: 6, border: "1px solid #e5e7eb", fontSize: 14, boxSizing: "border-box" }}
                        />
                      </div>
                      <div>
                        <label style={{ fontSize: 12, color: "#6b7280", marginBottom: 2, display: "block" }}>
                          <FaMobileAlt style={{ marginRight: 4, fontSize: 11 }} />Mobile (SMS)
                        </label>
                        <input
                          value={r.mobile}
                          onChange={e => setSendRecipients(prev => prev.map((x, i) => i === idx ? { ...x, mobile: e.target.value } : x))}
                          placeholder="04xx xxx xxx"
                          type="tel"
                          style={{ width: "100%", padding: "8px 10px", borderRadius: 6, border: "1px solid #e5e7eb", fontSize: 14, boxSizing: "border-box" }}
                        />
                      </div>
                      <div>
                        <label style={{ fontSize: 12, color: "#6b7280", marginBottom: 2, display: "block" }}>Name</label>
                        <input
                          value={r.name}
                          onChange={e => setSendRecipients(prev => prev.map((x, i) => i === idx ? { ...x, name: e.target.value } : x))}
                          placeholder="John Smith (optional)"
                          style={{ width: "100%", padding: "8px 10px", borderRadius: 6, border: "1px solid #e5e7eb", fontSize: 14, boxSizing: "border-box" }}
                        />
                      </div>
                      <div>
                        <label style={{ fontSize: 12, color: "#6b7280", marginBottom: 2, display: "block" }}>Message</label>
                        <textarea
                          value={r.message}
                          onChange={e => setSendRecipients(prev => prev.map((x, i) => i === idx ? { ...x, message: e.target.value } : x))}
                          placeholder="Optional message to include…"
                          rows={6}
                          style={{
                            width: "100%", padding: "8px 10px", borderRadius: 6,
                            border: "1px solid #e5e7eb", fontSize: 14, boxSizing: "border-box",
                            resize: "vertical", fontFamily: "inherit",
                          }}
                        />
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Footer */}
            <div style={{
              display: "flex", gap: 12, justifyContent: "flex-end",
              padding: "12px 24px 20px", borderTop: "1px solid #e5e7eb",
            }}>
              <button
                onClick={() => setSendModal({ open: false, appraisalId: null, address: "" })}
                disabled={sending}
                style={{
                  background: "#f1f5f9", color: "#374151", border: "none",
                  borderRadius: 6, padding: "8px 18px", fontWeight: 600,
                  cursor: "pointer", fontSize: 15,
                }}
              >Cancel</button>
              <button
                onClick={handleSendAppraisal}
                disabled={sending || sendRecipients.every(r => !r.email.trim() && !r.mobile.trim())}
                style={{
                  background: sending ? "#86efac" : "#059669", color: "#fff",
                  border: "none", borderRadius: 6, padding: "8px 18px",
                  fontWeight: 600,
                  cursor: sending || sendRecipients.every(r => !r.email.trim() && !r.mobile.trim()) ? "not-allowed" : "pointer",
                  fontSize: 15, display: "flex", alignItems: "center",
                }}
              >
                {sending && <Spinner animation="border" size="sm" style={{ marginRight: 8 }} />}
                <FaPaperPlane style={{ marginRight: 6 }} />
                {sending ? "Sending…" : "Send Appraisal"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ─── Toast ─── */}
      {toast.show && (
        <div style={{
          position: "fixed", bottom: 24,
          left: isMobile ? "50%" : "calc(50% + 130px)",
          transform: "translateX(-50%)",
          background: "#065f46", color: "#fff",
          padding: "12px 24px", borderRadius: 8,
          fontSize: 15, fontWeight: 600,
          boxShadow: "0 4px 16px rgba(0,0,0,0.18)",
          zIndex: 2000, display: "flex", alignItems: "center", gap: 8,
          animation: "toast-fade-in 0.25s ease-out",
        }}>
          <span style={{ fontSize: 18 }}>✓</span> {toast.message}
        </div>
      )}
      <style>{`
        @keyframes toast-fade-in {
          from { opacity: 0; transform: translateX(-50%) translateY(12px); }
          to { opacity: 1; transform: translateX(-50%) translateY(0); }
        }
      `}</style>
    </div>
  );
}
